import { TradingMethodSelector, TradingMethodEvidencePanel } from "./TradingMethodsPanel.tsx";
import { selectTradingMethod, methodSignalMarkers, isTradingMethodID, METHOD_UNAVAILABLE, METHOD_INVALID, METHOD_RESULT_MISMATCH } from "./trading-methods.ts";
import { methodT, methodReadOnlyT, methodName, methodRisk, storedMethodName } from "./trading-methods-i18n.ts";
import { loadAccountBootstrap } from "./account-bootstrap.ts";
import { futuresMarketBacktestForm, type FuturesMarketSelection, type FuturesMarket } from "./futures-market";
import { futuresMarketT } from "./futures-market-i18n";
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { communityPreviewCache } from "./community-preview-cache.ts";
import {
  Activity,
  ArrowRight,
  BarChart3,
  BellRing,
  Bot,
  BookOpen,
  ChevronDown,
  Clock,
  CheckCircle2,
  Code2,
  CreditCard,
  Database,
  Download,
  Home,
  LineChart,
  Loader2,
  Lock,
  LogOut,
  Minus,
  Play,
  RefreshCw,
  Settings,
  Share2,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  TrendingUp,
  UserCircle,
  Users,
  Zap,
} from "lucide-react";
import type { User } from "firebase/auth";
import { createChart, type IChartApi, type ISeriesApi, type UTCTimestamp } from "lightweight-charts";
import {
  DEFAULT_MAX_BACKTEST_CANDLES,
  createRealtimeBot,
  createPendingSharedStrategyAttribution,
  deleteRealtimeBot,
  effectiveMaxDaysForTimeframe,
  estimatedCandlesForRange,
  fetchAnonymousUsage,
  fetchCapabilities,
  fetchHistory,
  fetchPublicPlans,
  fetchRealtimeBots,
  fetchUsage,
  maxDaysForTimeframe,
  runBacktest,
  sharedAttributionForBacktest,
  syncHistory,
  tickRealtimeBot,
  type AuthTokenSource,
  type PendingSharedStrategyAttribution,
} from "./api";
import {
  prepareBacktestSnapshot,
  applyHistorySetup,
  canPrepareHistoryRerun,
} from "./backtest-history";
import { completeBacktestAndSync, mergeBacktestHistory as mergeHistory } from "./backtest-completion";
import { currentToken, firebaseEnabled, listenToAuth, loginWithEmail, logout, signUpWithEmail } from "./auth";
import { setAnalyticsExperience, trackFunnelEvent, trackStarterSampleEvent, trackHistoryEvent } from "./analytics";
import { AnalyticsConsent } from "./AnalyticsConsent";
import { backtestRunOriginAnalyticsParameters } from "./analytics-run-origin";
import {
  backtestDurationAnalyticsParameters,
  backtestFailureAnalyticsParameters,
} from "./analytics-backtest";
import { backtestStrategyAnalyticsParameters } from "./analytics-strategy";
import {
  DEFAULT_EXECUTION_SETTINGS,
  resolveDerivativesCapability,
  shouldFallbackToSpot,
  shouldOfferFutures,
} from "./execution-defaults";
import { createTranslator, isRTL, locales, resolveLocale, seoLocaleContent, type Locale, type TranslationKey } from "./i18n";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { languageNavigationPath, localeFromPath, stripLocaleFromPath } from "./language-navigation";
import { APP_NAVIGATION_EVENT } from "./navigation-events.ts";
import { searchLanguageCode } from "./seo-hreflang";
import type {
  AccountUsage,
  BacktestFormState,
  BacktestResult,
  BillingPlan,
  Candle,
  HistoryItem,
  PlanLimits,
  RealtimeBot,
  StrategyMode,
  Trade,
} from "./types";
import { API_BASE_URL } from "./api-base";
import { localDraftStorage, readLocalDraft, removeLocalDraft, saveLocalDraft } from "./local-draft";
import { localSupportT } from "./local-support-i18n";
import { referenceT } from "./public-reference-i18n";
import { registerOfflineShell } from "./offline-shell";
import { localizedAPIErrorMessage } from "./api-error-i18n";
import {
  buildAppStoreCampaignURL,
  buildWebToAppURL,
  isIOSOrIPadOSDevice,
  isMacOSDesktopDevice,
  localizedSectionPath,
  webToAppAnalyticsParameters,
  type WebToAppPlacement,
  type WebToAppStrategyContext,
} from "./web-to-app";
import {
  formatLocalizedCurrency,
  formatLocalizedDateTime,
  formatLocalizedDuration,
  formatLocalizedNumber,
  formatLocalizedPercent,
} from "./locale-format";
import {
  annualBillingSavings,
  APP_STORE_REFERENCE_PLANS,
  formatFreeBillingPrice,
  formatReferenceBillingAmount,
  formatReferenceBillingPrice,
} from "./pricing-catalog";
import { PaidAppPricingPanel } from "./PaidAppPricingPanel";
import { paidAppCopy, resolveAppPricingMode, withoutSubscriptionOffer, type AppPricingMode } from "./paid-app-pricing";
import "./paid-app-pricing.css";
import { normalizeStrategyEntry } from "./strategy-entry";
import { simpleStrategyT } from "./simple-strategy-i18n";
import {
  copyGuestHistoryToNewAccount,
  loadLocalHistoryForAccount,
  localHistoryStorageKey,
  localHistoryStorageScope,
  saveLocalHistoryForAccount,
} from "./local-history";
import {
  hasStoredExperienceMode,
  loadExperienceMode,
  saveExperienceMode,
  type ExperienceMode,
} from "./experience-mode";
import { createExperienceModeTranslator } from "./experience-mode-i18n";
import { ExperienceModeSelector, GuidedLearningCard } from "./ExperienceModeUI";
import { MarketDiscoverySelect } from "./MarketDiscoverySelect";
import { marketSymbolLabels as symbolLabels } from "./market-discovery";
import { strategyRuleDSLIdentity, type StrategyRuleDSL } from "./strategy-builder";
import { StrategyLibraryTransport, recipeText, selectLibraryRecipe } from "./strategy-library";
import type { PaperExecutionSource } from "./PaperExecutionPanel";
import { paperExecutionT } from "./paper-execution-i18n";
import { paperHistorySourceItems, paperSourceAfterExplicitSelection, paperSourceAfterHistory, readPaperHistoryBlock,
  supportsPaperHistorySetup, writePaperHistoryBlock, type PaperAppliedSourceState } from "./paper-execution-sources";
import { profitLossDirection } from "./result-presentation";
import {
  DEFAULT_STRATEGY_PRESET,
  isStrategyPresetName,
  selectStrategyPreset,
  strategyPresets,
} from "./strategy-presets";
import { strategyBuilderT } from "./strategy-builder-i18n";
import type { StrategyBuilderValidationHandle, StrategyBuilderValidationState } from "./StrategyBuilderPanel";
import {
  resolveBotCreationAction,
  resolveCustomDateRecovery,
} from "./flow-recovery";
import { flowRecoveryT } from "./flow-recovery-i18n";
import { StrategyRuleSummary } from "./StrategyRuleSummary";
import { SharedResultPage } from "./SharedResultPage";
import { sharedResultTokenFromPath } from "../shared-result-contract";
import {
  applySharedStrategy,
  fetchSharedStrategy,
  takeInitialSharedStrategyDescriptor,
  hadInitialSharedStrategyInput,
} from "./shared-research-network";
import {
  canShareReproducibleStrategy,
  canPublishProfitableCommunityResult,
  prepareCommunityShareSource,
  createCommunityShareSource,
  createShareableResult,
  legacyShareSetupURL,
  type CommunityShareSourceAttempt,
  type ShareMode,
} from "./shareable-results-client";
import {
  applyCommunityReproduction,
  resolveStrategyCommunityCapability,
} from "./strategy-community-contract";
import { communityT } from "./strategy-community-i18n";
import {
  prepareGeneratedCommunityPublication,
  publishGeneratedCommunityStrategy,
  type GeneratedCommunityPublicationAttempt,
  type CommunityDuplicateReceipt,
  type CommunityPublication,
  type CommunityReproduction,
  type StrategyCommunityCapability,
} from "./strategy-community";
import {
  accountScopedValue,
  accountScopedTokenSource,
  advanceAccountGeneration,
  createAccountGeneration,
  readAccountScopedValue,
  sameAccountGeneration,
  updateAccountScopedValue,
  type AccountGeneration,
} from "./account-generation";
import { PRIMARY_SECTIONS, primarySectionFor, productFocusT, recentSavedRuns, historyItemForResult } from "./product-focus";
import { SavedRunComparison } from "./SavedRunComparison";
import { workspaceT } from "./trader-workspace-i18n";
import { ftmoT } from "./ftmo-i18n";
import { FTMOSimulationSetup, FTMOSimulationResult, FTMOSimulatorLanding } from "./FTMOSimulator";
import { simulationT } from "./ftmo-simulation";
import { WorkspaceAttemptRegistry, workspaceBacktestForm, type WorkspaceStream } from "./trader-workspace";
import "./styles.css";

const FTMOWorkspace = React.lazy(async () => {
  const module = await import("./FTMOWorkspace");
  return { default: module.FTMOWorkspace };
});

const FuturesMarketPanel = React.lazy(async () => {
  const module = await import("./FuturesMarketPanel");
  return { default: module.FuturesMarketPanel };
});

const ResearchWorkspacePanel = React.lazy(async () => {
  const module = await import("./ResearchWorkspacePanel");
  return { default: module.ResearchWorkspacePanel };
});

const PaperMonitoringPanel = React.lazy(async () => {
  const module = await import("./PaperMonitoringPanel");
  return { default: module.PaperMonitoringPanel };
});

const PaperExecutionPanel = React.lazy(async () => {
  const module = await import("./PaperExecutionPanel");
  return { default: module.PaperExecutionPanel };
});

const FuturesDemoPanel = React.lazy(async () => {
  const module = await import("./FuturesDemoPanel");
  return { default: module.FuturesDemoPanel };
});

const StrategyBuilderPanel = React.lazy(async () => {
  const module = await import("./StrategyBuilderPanel");
  return { default: module.StrategyBuilderPanel };
});

const StrategyCommunityPanel = React.lazy(async () => {
  const module = await import("./StrategyCommunityPanel");
  return { default: module.StrategyCommunityPanel };
});

import { browserStarterStorage, claimStarterSample, hasPreviousStarterState, hasStarterURLInput, starterSampleForm } from "./starter-sample";
import { StarterSampleCard } from "./StarterSampleCard";

const defaultForm: BacktestFormState = {
  symbol: "BTCUSDT",
  provider: "binance_futures",
  market: "crypto_futures",
  timeframe: "1h",
  period: "6M",
  strategyMode: "auto",
  strategyName: DEFAULT_STRATEGY_PRESET,
  prompt: "Buy MACD bullish signal crossover on BTCUSDT and sell on bearish signal crossover",
  initialCapital: 10000,
  commissionRate: 0.1,
  slippageBps: 5,
  positionSizePct: 2,
  executionModel: DEFAULT_EXECUTION_SETTINGS.executionModel,
  marginMode: DEFAULT_EXECUTION_SETTINGS.marginMode,
  leverage: 1,
  maintenanceMarginPercent: 0.5,
  fundingRatePercent: 0,
  fundingIntervalHours: 8,
  liquidationFeePercent: 0.5,
  stopLossPct: 2,
  takeProfitPct: 4,
  tradeDirection: "long",
  exitMode: "tp_sl_only",
  useAI: false,
  startDate: "",
  endDate: "",
};

const timeframes = ["5m", "15m", "30m", "1h", "4h", "1d"] as const;
const timeframeLabels = {
  "5m": "5M",
  "15m": "15M",
  "30m": "30M",
  "1h": "1H",
  "4h": "4H",
  "1d": "1D",
} satisfies Record<(typeof timeframes)[number], string>;
const periods = ["1M", "3M", "6M", "1Y", "Custom"] as const;
const quickPeriodApproxDays: Record<string, number> = {
  "1M": 30,
  "3M": 90,
  "6M": 183,
  "1Y": 365,
};
const tradeDirections = ["long", "short", "both"] as const;
const exitModes = ["tp_sl_only", "signal_and_risk", "signal_only"] as const;
const executionModels = ["spot", "futures"] as const;
const marginModes = ["isolated", "cross"] as const;
const tradeDirectionLabelKeys = {
  long: "long",
  short: "short",
  both: "both",
} satisfies Record<(typeof tradeDirections)[number], TranslationKey>;
const exitModeLabelKeys = {
  tp_sl_only: "tpSlOnly",
  signal_and_risk: "signalAndRisk",
  signal_only: "signalOnly",
} satisfies Record<(typeof exitModes)[number], TranslationKey>;
const executionModelLabelKeys = {
  spot: "spotCash",
  futures: "futuresPerpetual",
} satisfies Record<(typeof executionModels)[number], TranslationKey>;
const marginModeLabelKeys = {
  isolated: "isolatedMargin",
  cross: "crossMargin",
} satisfies Record<(typeof marginModes)[number], TranslationKey>;
const RISK_NOTICE_ACCEPTED_KEY = "backtest.web.riskNoticeAccepted";
const GUEST_LOCAL_HISTORY_LIMIT = 50;
const SIGNED_IN_LOCAL_HISTORY_FALLBACK_LIMIT = 100;
const LOCAL_HISTORY_MAX_LIMIT = 300;
const navItems = [
  { id: "home", icon: Home, labelKey: "home" },
  { id: "watchlist", icon: Activity, labelKey: "home" },
  { id: "community", icon: Users, labelKey: "backtest" },
  { id: "research", icon: BookOpen, labelKey: "backtest" },
  { id: "backtest", icon: Play, labelKey: "backtest" },
  { id: "results", icon: LineChart, labelKey: "results" },
  { id: "history", icon: Clock, labelKey: "history" },
  { id: "bots", icon: Bot, labelKey: "bots" },
  { id: "pricing", icon: CreditCard, labelKey: "pricing" },
  { id: "account", icon: UserCircle, labelKey: "account" },
  { id: "ftmo", icon: ShieldCheck, labelKey: "account" },
  { id: "settings", icon: Settings, labelKey: "settings" },
] as const;
type NavSection = (typeof navItems)[number]["id"] | "community";
type QuickPeriod = (typeof periods)[number];
type Translator = ReturnType<typeof createTranslator>;

const SITE_ORIGIN = "https://backtest.click";

const PLAN_COMPARISON = [
  { labelKey: "quotaBacktests", field: "normal_backtests", windowField: "normal_window" },
  { labelKey: "quotaMaxRange", field: "max_period_days", format: "days" },
  { labelKey: "quotaMarketCandles", field: "max_backtest_candles", format: "count" },
  { labelKey: "quotaSavedStrategies", field: "saved_strategies", format: "count" },
  { labelKey: "quotaCloudHistory", field: "history_items", format: "count", guestLocal: true },
  { labelKey: "quotaPaperBots", field: "max_realtime_bots", format: "count" },
] as const;

type ComparisonTier = "guest" | "free" | "pro" | "power";
type PlanLimitsByTier = Partial<Record<ComparisonTier, PlanLimits>>;
const planLimitNumberFields = [
  "normal_backtests",
  "ai_credits",
  "max_period_days",
  "max_backtest_candles",
  "saved_strategies",
  "history_items",
  "max_realtime_bots",
] as const satisfies ReadonlyArray<keyof PlanLimits>;

function normalizedPlanLimits(value: unknown): PlanLimits | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  const hasValidNumbers = planLimitNumberFields.every((field) => {
    const limit = candidate[field];
    return typeof limit === "number" && Number.isFinite(limit) && limit >= 0;
  });
  if (
    !hasValidNumbers
    || typeof candidate.normal_window !== "string"
    || !candidate.normal_window
    || typeof candidate.ai_window !== "string"
    || !candidate.ai_window
  ) {
    return null;
  }
  return candidate as unknown as PlanLimits;
}

function formatQuotaValue(
  row: (typeof PLAN_COMPARISON)[number],
  tier: ComparisonTier,
  planLimits: PlanLimitsByTier,
  t: Translator,
  locale: Locale,
): string {
  const limits = planLimits[tier];
  if (!limits) return "—";
  const value = limits[row.field];
  const explicitFormat = "windowField" in row ? limits[row.windowField] : row.format;
  const formatted = formatLocalizedNumber(value, locale);
  if (explicitFormat === "day") return `${formatted} ${t("perDayQuota")}`;
  if (explicitFormat === "month") return `${formatted} ${t("perMonthQuota")}`;
  if (explicitFormat === "days") {
    if (value >= 365 && value % 365 === 0) {
      const years = value / 365;
      return `${years} ${t(years === 1 ? "quotaYear" : "quotaYears")}`;
    }
    return `${formatted} ${t("quotaDays")}`;
  }
  if (tier === "guest" && "guestLocal" in row && row.guestLocal) return `${formatted} (${t("localOnly")})`;
  return formatted;
}

function strategyContextForHandoff(form: BacktestFormState | undefined): WebToAppStrategyContext | undefined {
  if (!form) return undefined;
  return {
    symbol: form.symbol,
    timeframe: form.timeframe,
    period: form.period,
    strategy: form.strategyMode === "auto"
      ? form.strategyName
      : form.strategyMode === "builder"
        ? "Visual Strategy Builder"
        : undefined,
    direction: form.tradeDirection,
    mode: form.strategyMode,
  };
}

function DesktopHandoffDialog({
  appStoreURL,
  handoffURL,
  onAppStoreClick,
  onClose,
  t,
}: {
  appStoreURL: string;
  handoffURL: string;
  onAppStoreClick: () => void;
  onClose: () => void;
  t: Translator;
}) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const [qrDataURL, setQrDataURL] = useState<string | null>(null);
  const [qrFailed, setQrFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setQrDataURL(null);
    setQrFailed(false);
    void import("qrcode-generator")
      .then((module) => {
        const code = module.default(0, "M");
        code.addData(handoffURL, "Byte");
        code.make();
        if (!cancelled) setQrDataURL(code.createDataURL(6, 24));
      })
      .catch(() => {
        if (!cancelled) setQrFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [handoffURL]);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) || [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop desktop-handoff-backdrop" role="presentation" onClick={onClose}>
      <section
        ref={dialogRef}
        className="modal desktop-handoff-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="desktop-handoff-title"
        aria-describedby="desktop-handoff-description"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="desktop-handoff-heading">
          <div>
            <p className="eyebrow">Backtest</p>
            <h2 id="desktop-handoff-title">{t("continueOnIOSDevice")}</h2>
          </div>
          <button ref={closeButtonRef} className="secondary" type="button" onClick={onClose}>
            {t("privacyClose")}
          </button>
        </div>
        <p id="desktop-handoff-description" className="desktop-handoff-description">
          {t("desktopHandoffBody")}
        </p>
        <div className="desktop-handoff-qr" aria-live="polite">
          {qrDataURL ? (
            <img src={qrDataURL} alt={t("desktopHandoffQrAlt")} width="264" height="264" />
          ) : qrFailed ? (
            <p role="status">{t("desktopHandoffUnavailable")}</p>
          ) : (
            <p role="status">{t("desktopHandoffLoading")}</p>
          )}
        </div>
        <a
          className="app-store-badge desktop-handoff-store"
          href={appStoreURL}
          target="_blank"
          rel="noreferrer"
          aria-label={t("downloadOnAppStore")}
          onClick={onAppStoreClick}
        >
          <img src="/download-on-the-app-store.svg" alt={t("downloadOnAppStore")} width="120" height="40" />
        </a>
      </section>
    </div>
  );
}

function AppHandoffActions({
  t,
  placement,
  locale,
  form,
  pathname,
}: {
  t: Translator;
  placement: WebToAppPlacement;
  locale: Locale;
  form?: BacktestFormState;
  pathname?: string;
}) {
  const [isDesktopHandoffOpen, setIsDesktopHandoffOpen] = useState(false);
  const desktopTriggerRef = useRef<HTMLButtonElement>(null);
  const context = {
    pathname: pathname
      || (form?.strategyMode === "builder"
        ? "/open/visual-builder"
        : localizedSectionPath(locale, placement === "pricing" ? "pricing" : "backtest")),
    source: "website",
    medium: placement,
    campaign: "web_to_app",
    locale,
    strategy: strategyContextForHandoff(form),
  };
  const handoffURL = buildWebToAppURL(context);
  const appStoreURL = buildAppStoreCampaignURL(context.campaign, placement);
  const isAppleMobile = typeof navigator !== "undefined" && isIOSOrIPadOSDevice(navigator);
  const isMacDesktop = typeof navigator !== "undefined" && isMacOSDesktopDevice(navigator);
  const analyticsParameters = webToAppAnalyticsParameters(context, placement);

  const closeDesktopHandoff = useCallback(() => {
    setIsDesktopHandoffOpen(false);
    window.setTimeout(() => desktopTriggerRef.current?.focus(), 0);
  }, []);

  return (
    <>
      <div className="app-handoff-actions">
        {isAppleMobile ? (
          <a
            className="open-in-app-link"
            href={handoffURL}
            onClick={() => trackFunnelEvent("web_to_app_open", analyticsParameters)}
          >
            {t("continueInIOSApp")}
          </a>
        ) : (
          <button
            ref={desktopTriggerRef}
            className="open-in-app-link desktop-handoff-button"
            type="button"
            onClick={() => {
              trackFunnelEvent("web_to_app_desktop_qr_open", analyticsParameters);
              setIsDesktopHandoffOpen(true);
            }}
          >
            {t("continueOnIOSDevice")}
          </button>
        )}
        {isMacDesktop ? (
          <a
            className="download-mac-link"
            href={appStoreURL}
            target="_blank"
            rel="noreferrer"
            aria-label={t("downloadForMac")}
            onClick={() => trackFunnelEvent("app_store_click", analyticsParameters)}
          >
            <Download size={18} aria-hidden="true" />
            <span>{t("downloadForMac")}</span>
          </a>
        ) : (
          <a
            className="app-store-badge"
            href={appStoreURL}
            target="_blank"
            rel="noreferrer"
            aria-label={t("downloadOnAppStore")}
            onClick={() => trackFunnelEvent("app_store_click", analyticsParameters)}
          >
            <img src="/download-on-the-app-store.svg" alt={t("downloadOnAppStore")} width="120" height="40" />
          </a>
        )}
      </div>
      {isDesktopHandoffOpen && (
        <DesktopHandoffDialog
          appStoreURL={appStoreURL}
          handoffURL={handoffURL}
          onAppStoreClick={() => trackFunnelEvent("app_store_click", analyticsParameters)}
          onClose={closeDesktopHandoff}
          t={t}
        />
      )}
    </>
  );
}
const localizedLearnIndexLocales = new Set<Locale>(locales.filter((locale) => locale !== "en"));

function formFromUrlPreset(): BacktestFormState {
  const params = new URLSearchParams(window.location.search);
  const next: BacktestFormState = { ...defaultForm };
  const symbol = params.get("symbol")?.trim().toUpperCase();
  const timeframe = params.get("timeframe")?.trim().toLowerCase();
  const period = params.get("period")?.trim().toUpperCase();
  const strategy = params.get("strategy")?.trim();
  const direction = params.get("direction")?.trim().toLowerCase();
  const mode = params.get("mode")?.trim().toLowerCase();

  if (symbol && Object.prototype.hasOwnProperty.call(symbolLabels, symbol)) { next.symbol = symbol; next.provider = undefined; next.market = undefined; }
  if (timeframe && timeframes.includes(timeframe as (typeof timeframes)[number])) next.timeframe = timeframe as BacktestFormState["timeframe"];
  if (period && periods.includes(period as (typeof periods)[number])) next.period = period as BacktestFormState["period"];
  if (strategy && strategyPresets.some((preset) => preset.name.toLowerCase() === strategy.toLowerCase())) {
    next.strategyName = strategyPresets.find((preset) => preset.name.toLowerCase() === strategy.toLowerCase())?.name || next.strategyName;
    next.strategyMode = "auto";
  }
  if (direction && tradeDirections.includes(direction as (typeof tradeDirections)[number])) {
    next.tradeDirection = direction as BacktestFormState["tradeDirection"];
  }
  if (mode === "manual") next.strategyMode = "manual";
  return next;
}

function hasUrlPreset(): boolean {
  const params = new URLSearchParams(window.location.search);
  return ["symbol", "timeframe", "period", "strategy", "direction", "mode"].some((key) => params.has(key));
}

function localeHomePath(locale: Locale) {
  return `/${locale}/`;
}

function sectionFromPath(pathname: string): NavSection {
  const section = stripLocaleFromPath(pathname).replace(/^\/+/, "").split("/")[0];
  if (section === "app") return "watchlist";
  if (section === "portfolio") return "watchlist";
  if (["paper", "paper-execution", "futures", "futures-demo"].includes(section)) return "bots";
  if (section === "privacy") return "settings";
  if (section === "community") return "community";
  return navItems.some((item) => item.id === section) ? (section as NavSection) : "home";
}

function pathForSection(section: NavSection, locale?: Locale) {
  const prefix = locale ? `/${locale}` : "";
  return section === "home" ? `${prefix}/` : `${prefix}/${section}`;
}

function learnPathForLocale(locale?: Locale) {
  return locale && localizedLearnIndexLocales.has(locale) ? `/${locale}/learn/` : "/learn/";
}

function methodologyPath(locale: Locale) {
  return locale === "en" ? "/methodology/" : `/${locale}/methodology/`;
}

function resultStrategyLabel(result: BacktestResult, t: Translator, locale: Locale): string {
  const evidence = result.method_evidence;
  const method = evidence ? { schema_version: evidence.schema_version, method_id: evidence.method_id,
    method_version: evidence.method_version, parameters: evidence.parameters } : undefined;
  return storedMethodName(locale, method, strategyLabel(result.strategy_name, t, locale));
}

function shareEvidenceTextForResult(result: BacktestResult, locale: Locale, t: Translator) {
  return [
    `${t("backtest")}: ${symbolLabel(result.symbol)} ${timeframeLabel(result.timeframe)} ${resultStrategyLabel(result, t, locale)}`,
    `${t("netProfit")}: ${currency(result.performance.net_profit_amount, locale)} · ${t("winRate")}: ${formatLocalizedPercent(result.performance.win_rate, locale, 1)} · ${t("drawdown")}: ${formatLocalizedPercent(result.performance.max_drawdown, locale, 1)}`,
    `${t("trades")}: ${formatLocalizedNumber(result.trades?.length || 0, locale)}`,
  ].join("\n");
}

function privacyPath(locale?: Locale) {
  return locale ? `/${locale}/privacy` : "/privacy";
}

function canonicalUrlForLocale(locale: Locale) {
  return `${SITE_ORIGIN}${localeHomePath(locale)}`;
}

function canonicalUrlForRoute(locale: Locale, pathname: string) {
  const parts = pathname.replace(/^\/+/, "").split("/").filter(Boolean);
  const pathLocale = localeFromPath(pathname);
  const effectiveLocale = pathLocale || locale;
  const routeParts = pathLocale ? parts.slice(1) : parts;
  const route = routeParts[0] || "";
  const prefix = pathLocale ? `/${effectiveLocale}` : "";

  if (!route) {
    return `${SITE_ORIGIN}${pathLocale ? `${prefix}/` : "/"}`;
  }

  if (route === "privacy") {
    return `${SITE_ORIGIN}${prefix}/privacy`;
  }

  if (route === "community") {
    const publicationID = routeParts[1] && /^[A-Za-z0-9._:-]{1,128}$/.test(routeParts[1])
      ? routeParts[1]
      : "";
    return `${SITE_ORIGIN}${prefix}/community${publicationID ? `/${publicationID}` : ""}`;
  }

  if (navItems.some((item) => item.id === route)) {
    return `${SITE_ORIGIN}${prefix}/${route}`;
  }

  return pathLocale ? canonicalUrlForLocale(effectiveLocale) : `${SITE_ORIGIN}/`;
}

function setMeta(name: string, content: string, property = false) {
  const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    if (property) {
      element.setAttribute("property", name);
    } else {
      element.setAttribute("name", name);
    }
    document.head.appendChild(element);
  }
  element.content = content;
}

function setLink(rel: string, href: string, attrs: Record<string, string> = {}) {
  const extraSelector = Object.entries(attrs)
    .map(([key, value]) => `[${key}="${value}"]`)
    .join("");
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]${extraSelector}`);
  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    Object.entries(attrs).forEach(([key, value]) => element?.setAttribute(key, value));
    document.head.appendChild(element);
  }
  element.href = href;
}

function updateRouteHreflang(pathname: string) {
  const stripped = stripLocaleFromPath(pathname);
  const route = stripped === "/" ? "/" : `/${stripped.replace(/^\/+|\/+$/g, "")}`;
  const hrefFor = (routeLocale?: Locale) => {
    const prefix = routeLocale ? `/${routeLocale}` : "";
    return `${SITE_ORIGIN}${prefix}${route === "/" ? "/" : route}`;
  };
  const routeLocales = locales;
  const values: Array<[string, string]> = [
    ["x-default", hrefFor()],
    ...routeLocales.map((routeLocale) => [searchLanguageCode(routeLocale), hrefFor(routeLocale)] as [string, string]),
  ];
  document.head.querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]').forEach((element) => {
    if (!values.some(([hreflang]) => hreflang === element.hreflang)) element.remove();
  });
  for (const [hreflang, href] of values) {
    setLink("alternate", href, { hreflang });
  }
}

function updateLocalizedSeo(locale: Locale, pricingMode: AppPricingMode = "legacy") {
  const seo = seoLocaleContent[locale] || seoLocaleContent.en;
  const translate = createTranslator(locale);
  const canonicalUrl = canonicalUrlForRoute(locale, window.location.pathname);
  const isPricing = stripLocaleFromPath(window.location.pathname) === "/pricing";
  const pricingCopy = paidAppCopy[locale];
  const title = isPricing
    ? pricingMode === "legacy" ? translate("pricingSeoTitle")
      : `${pricingMode === "paid_app" ? pricingCopy.title : translate("pricing")} | Backtest`
    : seo.title;
  const description = isPricing
    ? pricingMode === "legacy" ? translate("pricingSeoDescription")
      : pricingMode === "paid_app" ? pricingCopy.body : pricingCopy.pendingBody
    : seo.description;
  if (pricingMode !== "legacy") {
    document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]').forEach((script) => {
      try {
        const value = withoutSubscriptionOffer(JSON.parse(script.textContent || "null")) as Record<string, unknown> | null;
        if (value?.["@type"] === "WebPage") {
          value.name = title;
          value.description = description;
        }
        script.textContent = JSON.stringify(value);
      } catch { /* Leave unrelated malformed metadata to the normal SEO audit. */ }
    });
  }
  document.title = title;
  setMeta("description", description);
  setMeta("og:title", title, true);
  setMeta("og:description", description, true);
  setMeta("og:url", canonicalUrl, true);
  setMeta("og:locale", locale.replace("-", "_"), true);
  setMeta("twitter:title", title);
  setMeta("twitter:description", description);
  setLink("canonical", canonicalUrl);
  updateRouteHreflang(window.location.pathname);
}

function accountAuthErrorMessage(error: unknown, t: Translator) {
  const code = typeof error === "object" && error && "code" in error ? String((error as { code?: unknown }).code) : "";
  if (code.includes("email-already-in-use")) return t("emailAlreadyInUse");
  if (code.includes("invalid-credential") || code.includes("wrong-password") || code.includes("user-not-found")) return t("invalidCredentials");
  if (code.includes("weak-password")) return t("passwordTooWeak");
  if (
    code.includes("operation-not-allowed") ||
    code.includes("unauthorized-domain") ||
    code.includes("popup-blocked") ||
    code.includes("popup-closed-by-user") ||
    code.includes("network-request-failed")
  ) {
    return t("signInUnavailable");
  }
  return t("emailAuthFailed");
}

function symbolLabel(symbol?: string | null) {
  if (!symbol) return "-";
  return symbolLabels[symbol] || symbol;
}

function timeframeLabel(timeframe?: string | null) {
  if (!timeframe) return "-";
  return timeframeLabels[timeframe as keyof typeof timeframeLabels] || timeframe.toUpperCase();
}

function tradeSide(trade: Trade): "long" | "short" {
  return (trade.side || "long").toLowerCase() === "short" ? "short" : "long";
}

function tradeSideLabel(trade: Trade, t: Translator) {
  return tradeSide(trade) === "short" ? t("sell") : t("buy");
}

function tradePositionLabel(trade: Trade, t: Translator) {
  return tradeSide(trade) === "short" ? t("short") : t("long");
}

function exitReasonLabel(reason: string | null | undefined, t: Translator): string {
  switch ((reason || "").toLowerCase()) {
    case "stop_loss": return t("stopLoss");
    case "take_profit": return t("takeProfit");
    case "liquidation": return t("liquidation");
    case "signal": return t("signalOnly");
    case "end_of_data": return t("endDate");
    default: return t("exit");
  }
}

function translatedLabels<T extends string>(keys: Record<T, TranslationKey>, t: Translator): Record<T, string> {
  return Object.fromEntries(Object.entries(keys).map(([value, key]) => [value, t(key as TranslationKey)])) as Record<T, string>;
}

function localHistoryLimit(usage?: AccountUsage | null): number {
  if (!usage) return GUEST_LOCAL_HISTORY_LIMIT;
  if (usage.plan === "anonymous") return GUEST_LOCAL_HISTORY_LIMIT;
  return Math.min(
    LOCAL_HISTORY_MAX_LIMIT,
    Math.max(SIGNED_IN_LOCAL_HISTORY_FALLBACK_LIMIT, usage.limits.history_items || 0),
  );
}

function cloudHistoryItem(item: HistoryItem): HistoryItem {
  // The backend intentionally stores a compact result placeholder. Avoid
  // uploading candle arrays while preserving the server item ID and exact
  // setup used for quota-exempt reruns.
  return {
    id: item.id,
    createdAt: item.createdAt,
    prompt: item.prompt,
    setup: item.setup,
  };
}

function hasCompleteHistoryResult(
  item: HistoryItem,
): item is HistoryItem & { result: BacktestResult } {
  return Boolean(
    item.result
    && (
      item.result.run_id
      || item.result.candles?.length
      || item.result.trades?.length
      || item.result.equity_curve?.length
    )
  );
}

function backtestRangeSummary(form: BacktestFormState, maxCandles: number, maxPeriodDays?: number): { days: number; candles: number } {
  const endDate = form.endDate && form.period === "Custom" ? new Date(`${form.endDate}T23:59:59`) : new Date();
  const startDate = new Date(endDate);
  if (form.period === "Custom" && form.startDate) {
    startDate.setTime(new Date(`${form.startDate}T00:00:00`).getTime());
  } else if (form.period === "1M") {
    startDate.setMonth(startDate.getMonth() - 1);
  } else if (form.period === "3M") {
    startDate.setMonth(startDate.getMonth() - 3);
  } else if (form.period === "6M") {
    startDate.setMonth(startDate.getMonth() - 6);
  } else if (form.period === "1Y") {
    startDate.setFullYear(startDate.getFullYear() - 1);
  } else {
    startDate.setDate(startDate.getDate() - effectiveMaxDaysForTimeframe(form.timeframe, maxCandles, maxPeriodDays));
  }

  const earliest = new Date(endDate);
  earliest.setDate(earliest.getDate() - effectiveMaxDaysForTimeframe(form.timeframe, maxCandles, maxPeriodDays));
  const effectiveStart = startDate < earliest ? earliest : startDate;
  const days = Math.max(1, Math.ceil((endDate.getTime() - effectiveStart.getTime()) / 86400000));
  return {
    days,
    candles: estimatedCandlesForRange(form.timeframe, effectiveStart, endDate),
  };
}

function resultStatusMessage(result: BacktestResult, t: Translator, locale: Locale): string {
  const base = `${t("backtestComplete")}: ${resultStrategyLabel(result, t, locale)}`;
  const adjustment = result.range_adjustment;
  if (!adjustment) return base;
  const maxDays = adjustment.max_period_days
    ? `${formatLocalizedNumber(adjustment.max_period_days, locale)} ${t("quotaDays")}`
    : "";
  const maxCandles = adjustment.max_candles
    ? `~${formatLocalizedNumber(adjustment.max_candles, locale)} ${t("bars")}`
    : "";
  const limit = [maxDays, maxCandles].filter(Boolean).join(" / ");
  return limit ? `${base}. ${t("periodClampedByPlan")}: ${limit}.` : `${base}. ${t("periodClampedByPlan")}.`;
}

function estimatedBacktestWait(candles: number, locale: Locale): string {
  const range = (start: number, end: number, unit: "second" | "minute") => [start, end]
    .map((value) => formatLocalizedNumber(value, locale, { style: "unit", unit, unitDisplay: "short" }))
    .join("–");
  if (candles <= 5000) return range(10, 20, "second");
  if (candles <= 12000) return range(20, 45, "second");
  if (candles <= 25000) return range(45, 90, "second");
  return range(1, 3, "minute");
}

function RunProgressCard({
  title,
  steps,
  eta,
}: {
  title: string;
  steps: string[];
  eta: string;
}) {
  return (
    <div className="run-progress" role="status" aria-live="polite">
      <div className="run-progress-head">
        <span className="run-loader">
          <Loader2 size={18} />
        </span>
        <div>
          <strong>{title}</strong>
          <small>{eta}</small>
        </div>
      </div>
      <div className="run-progress-bar" />
      <div className="run-steps">
        {steps.map((step, index) => {
          const Icon = index === 0 ? Database : index === 1 ? Zap : LineChart;
          return (
            <span key={step} className={index === 0 ? "active" : ""}>
              <Icon size={15} />
              {step}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function useAccountScopedState<T>(
  accountContext: AccountGeneration,
  activeAccountContextRef: React.MutableRefObject<AccountGeneration>,
  emptyValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [scoped, setScoped] = useState(() => accountScopedValue(accountContext, emptyValue));
  const value = readAccountScopedValue(scoped, accountContext, emptyValue);
  const setValue = useCallback<React.Dispatch<React.SetStateAction<T>>>((update) => {
    setScoped((current) => updateAccountScopedValue(
      current,
      accountContext,
      activeAccountContextRef.current,
      update,
      emptyValue,
    ));
  }, [accountContext, activeAccountContextRef, emptyValue]);
  return [value, setValue];
}

function App() {
  const libraryTransport = useMemo(() => new StrategyLibraryTransport(), []);
  const [strategyLibrary, setStrategyLibrary] = useState(() => libraryTransport.cached());
  const initialFormRef = useRef(formFromUrlPreset());
  const [initialStarterState] = useState(() => hasPreviousStarterState(browserStarterStorage()));
  const initialStarterPathRef = useRef(stripLocaleFromPath(window.location.pathname));
  const initialStarterURLInputRef = useRef(hadInitialSharedStrategyInput || hasStarterURLInput(window.location.search, window.location.hash));
  const initialPresetRef = useRef(hasUrlPreset());
  const [locale, setLocale] = useState<Locale>(
    () => localeFromPath(window.location.pathname) || resolveLocale(localStorage.getItem("stoptrading.locale") || navigator.language || "en"),
  );
  const [experienceMode, setExperienceMode] = useState<ExperienceMode>(
    () => loadExperienceMode(window.localStorage),
  );
  const [hasChosenExperienceMode, setHasChosenExperienceMode] = useState(
    () => hasStoredExperienceMode(window.localStorage),
  );
  const [isExecutionSettingsOpen, setIsExecutionSettingsOpen] = useState(false);
  const [areMonitorLabsOpen, setAreMonitorLabsOpen] = useState(false);
  const initialAccountContextRef = useRef<AccountGeneration>(createAccountGeneration());
  const activeAccountContextRef = useRef<AccountGeneration>(initialAccountContextRef.current);
  const [authSnapshot, setAuthSnapshot] = useState<{
    user: User | null;
    context: AccountGeneration;
  }>(() => ({ user: null, context: initialAccountContextRef.current }));
  const user = authSnapshot.user;
  const accountContext = authSnapshot.context;
  const [authResolved, setAuthResolved] = useState(false);
  const [starterSampleVisible, setStarterSampleVisible] = useAccountScopedState(
    accountContext, activeAccountContextRef, false,
  );
  const [form, setForm] = useAccountScopedState<BacktestFormState>(
    accountContext, activeAccountContextRef, initialFormRef.current,
  );
  const [preparedPaperSource, setPreparedPaperSource] = useAccountScopedState<PaperExecutionSource | undefined>(
    accountContext, activeAccountContextRef, undefined,
  );
  const [paperAppliedSourceState, setPaperAppliedSourceState] = useAccountScopedState<PaperAppliedSourceState>(
    accountContext, activeAccountContextRef, "pending",
  );
  const [methodsAvailable, setMethodsAvailable] = useState(false);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine !== false);
  const [draftHydrated, setDraftHydrated] = useState<AccountGeneration | null>(null);
  const [draftStatus, setDraftStatus] = useAccountScopedState<{
    state: "empty" | "saving" | "saved" | "restored" | "unavailable" | "cleared";
    savedAt?: number;
  }>(accountContext, activeAccountContextRef, { state: "empty" });
  const [builderLocalDraft, setBuilderLocalDraft] = useAccountScopedState<StrategyRuleDSL | undefined>(
    accountContext, activeAccountContextRef, undefined,
  );
  const draftForm = useMemo(() => form.strategyMode === "builder" && builderLocalDraft
    ? { ...form, strategyDSL: builderLocalDraft } : form, [form, builderLocalDraft]);
  const lastSavedDraftRef = useRef("");
  const firstDraftHydrationRef = useRef(true);
  const [authenticatedSession, setAuthenticatedSession] = useState<{
    context: AccountGeneration;
    token: string;
  } | null>(null);
  const token = authenticatedSession
    && sameAccountGeneration(authenticatedSession.context, accountContext)
    ? authenticatedSession.token
    : null;
  const authTokenSource = useMemo<AuthTokenSource | null>(() => {
    const requestUser = user;
    return requestUser
      ? accountScopedTokenSource(accountContext, () => activeAccountContextRef.current, (forceRefresh) => currentToken(requestUser, forceRefresh))
      : null;
  }, [user, accountContext]);
  const [result, setResult] = useAccountScopedState<BacktestResult | null>(
    accountContext,
    activeAccountContextRef,
    null,
  );
  const [historyCache, setHistoryCache] = useState<{
    scope: string;
    items: HistoryItem[];
  }>(() => ({
    scope: localHistoryStorageScope(null),
    items: loadLocalHistoryForAccount(
      window.localStorage,
      null,
      GUEST_LOCAL_HISTORY_LIMIT,
    ),
  }));
  const currentHistoryScope = localHistoryStorageScope(user?.uid);
  const history = historyCache.scope === currentHistoryScope
    ? historyCache.items
    : [];
  const paperSources = useMemo<PaperExecutionSource[]>(() => {
    const choices: PaperExecutionSource[] = [];
    if (preparedPaperSource && JSON.stringify(preparedPaperSource.form) === JSON.stringify(form)) {
      choices.push(preparedPaperSource);
    }
    if (paperAppliedSourceState === "ready" && form.strategyMode === "builder" && form.strategyDSL) {
      choices.push({ id: "applied-setup", label: `${paperExecutionT(locale, "currentSetup")} · ${form.strategyName}`, form });
    }
    for (const item of paperHistorySourceItems(history)) {
      const savedForm = applyHistorySetup(defaultForm, item);
      choices.push({ id: `history:${item.id}`, label: `${paperExecutionT(locale, "history")} · ${savedForm.strategyName}`, form: savedForm });
    }
    for (const recipe of strategyLibrary?.items ?? []) {
      const recipeForm = selectLibraryRecipe(defaultForm, recipe, locale);
      choices.push({ id: `library:${recipe.id}`, label: `${paperExecutionT(locale, "library")} · ${recipeText(recipe.title, locale)}`, form: recipeForm });
    }
    return choices;
  }, [form, history, strategyLibrary, locale, preparedPaperSource, paperAppliedSourceState]);
  const [variantBaseline, setVariantBaseline] = useAccountScopedState<HistoryItem | null>(accountContext, activeAccountContextRef, null);
  const [completedVariantComparison, setCompletedVariantComparison] = useAccountScopedState<{ baseline: HistoryItem; candidate: HistoryItem } | null>(accountContext, activeAccountContextRef, null);
  const recentRuns = useMemo(() => recentSavedRuns(history), [history]);
  const resultHistoryItem = historyItemForResult(history, result?.run_id);
  const [bots, setBots] = useAccountScopedState<RealtimeBot[]>(
    accountContext,
    activeAccountContextRef,
    [],
  );
  const [usage, setUsage] = useAccountScopedState<AccountUsage | null>(
    accountContext,
    activeAccountContextRef,
    null,
  );
  const [anonymousUsage, setAnonymousUsage] = useState<AccountUsage | null>(null);
  const [planLimits, setPlanLimits] = useState<PlanLimitsByTier>({});
  const [pricingMode, setPricingMode] = useState<AppPricingMode>(() =>
    resolveAppPricingMode(import.meta.env.VITE_PAID_APP_MARKETING, null));
  const [selectedTrade, setSelectedTrade] = useAccountScopedState<Trade | null>(
    accountContext,
    activeAccountContextRef,
    null,
  );
  const [isRunning, setIsRunning] = useAccountScopedState(
    accountContext,
    activeAccountContextRef,
    false,
  );
  const [isBotActionRunning, setIsBotActionRunning] = useAccountScopedState(
    accountContext,
    activeAccountContextRef,
    false,
  );
  const [isBotsRefreshing, setIsBotsRefreshing] = useAccountScopedState(
    accountContext,
    activeAccountContextRef,
    false,
  );
  const [isUsageRefreshing, setIsUsageRefreshing] = useState(false);
  const [isBuilderReady, setIsBuilderReady] = useAccountScopedState(accountContext, activeAccountContextRef, false);
  const [builderValidationState, setBuilderValidationState] = useState<StrategyBuilderValidationState>("unavailable");
  const [supportsDerivativesBacktesting, setSupportsDerivativesBacktesting] = useState<boolean | null>(null);
  const [supportsReproducibleSharing, setSupportsReproducibleSharing] = useState(false);
  const [supportsShareableResults, setSupportsShareableResults] = useState(false);
  const [strategyCommunityCapability, setStrategyCommunityCapability] = useState<StrategyCommunityCapability>(
    () => resolveStrategyCommunityCapability(null),
  );
  const [communityCapabilityState, setCommunityCapabilityState] = useState<"pending" | "ready" | "failed">("pending");
  const [communityCapabilityRetry, setCommunityCapabilityRetry] = useState(0);
  const [isShareDialogOpen, setIsShareDialogOpen] = useAccountScopedState(accountContext, activeAccountContextRef, false);
  const [isShareLinkCreating, setIsShareLinkCreating] = useAccountScopedState(accountContext, activeAccountContextRef, false);
  const [shareFeedback, setShareFeedback] = useAccountScopedState<{ message: string; failed: boolean } | null>(accountContext, activeAccountContextRef, null);
  const [isCommunityPublishing, setIsCommunityPublishing] = useAccountScopedState(
    accountContext, activeAccountContextRef, false,
  );
  const [isLoadingAccount, setIsLoadingAccount] = useAccountScopedState(
    accountContext,
    activeAccountContextRef,
    false,
  );
  const [isAuthSubmitting, setIsAuthSubmitting] = useState(false);
  const [authMode, setAuthMode] = useState<"signin" | "signup">("signin");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authName, setAuthName] = useState("");
  const [pendingSignUpImport, setPendingSignUpImport] = useState<{
    uid: string;
    items: HistoryItem[];
    result: BacktestResult | null;
  } | null>(null);
  const [message, setMessage] = useAccountScopedState(
    accountContext,
    activeAccountContextRef,
    "",
  );
  const [error, setError] = useAccountScopedState<string | null>(
    accountContext,
    activeAccountContextRef,
    null,
  );
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(() => stripLocaleFromPath(window.location.pathname) === "/privacy");
  const [isRiskNoticeOpen, setIsRiskNoticeOpen] = useState(() => localStorage.getItem(RISK_NOTICE_ACCEPTED_KEY) !== "true");
  const [riskNoticeRequiresConfirmation, setRiskNoticeRequiresConfirmation] = useState(
    () => localStorage.getItem(RISK_NOTICE_ACCEPTED_KEY) !== "true",
  );
  const [activeSection, setActiveSection] = useState<NavSection>(() => sectionFromPath(window.location.pathname));
  useEffect(() => {
    let current = true;
    if (isOnline && ["backtest", "bots"].includes(activeSection)) void libraryTransport.refresh(locale).then((library) => {
      if (current) setStrategyLibrary(library);
    }).catch(() => { /* Keep the last valid public library available offline. */ });
    return () => { current = false; };
  }, [libraryTransport, locale, isOnline, activeSection]);
  const [communityMarketSelection, setCommunityMarketSelection] = useState<FuturesMarketSelection | null>(null);
  const marketSelectionRef = useRef<FuturesMarketSelection | null>(null);
  useEffect(() => { marketSelectionRef.current = null; setCommunityMarketSelection(null); }, [accountContext.accountID, accountContext.generation]);
  const workspaceAttempts = useMemo(() => new WorkspaceAttemptRegistry(), [accountContext.accountID, accountContext.generation]);
  const [guidePresetLoaded, setGuidePresetLoaded] = useState(() => hasUrlPreset());
  const [resultRevealRequest, setResultRevealRequest] = useState(0);
  const routeContentRef = React.useRef<HTMLElement | null>(null);
  const resultHeadingRef = React.useRef<HTMLHeadingElement | null>(null);
  const marginModeHelpID = useId();
  const accountPanelRef = React.useRef<HTMLElement | null>(null);
  const authEmailRef = React.useRef<HTMLInputElement | null>(null);
  const builderRecoveryRef = React.useRef<HTMLDivElement | null>(null);
  const builderValidationRef = useRef<StrategyBuilderValidationHandle | null>(null);
  const dateRecoveryRef = React.useRef<HTMLDivElement | null>(null);
  const deferredSectionAfterSignInRef = React.useRef<NavSection | null>(null);
  const signUpImportOperationRef = useRef<string | null>(null);
  const backtestOperationRef = useRef(0);
  const backtestFlightRef = useRef<{ context: typeof accountContext; operation: number } | null>(null);
  const starterSampleOriginRef = useRef<{ context: typeof accountContext; fingerprint: string } | null>(null);
  const historyRerunCandidateRef = useRef<{
    id: string;
    fingerprint: string;
  } | null>(null);
  const sharedStrategyDescriptorRef = useRef(takeInitialSharedStrategyDescriptor());
  const initialSharedDraftRef = useRef(Boolean(sharedStrategyDescriptorRef.current));
  const sharedStrategyAttributionRef = useRef<PendingSharedStrategyAttribution | null>(null);
  const [communityPublishFeedback, setCommunityPublishFeedback] = useAccountScopedState<{ message: string; failed: boolean } | null>(accountContext, activeAccountContextRef, null);
  const [communityHistoryRequested, setCommunityHistoryRequested] = useAccountScopedState(accountContext, activeAccountContextRef, false);
  const communityPublishOperationRef = useRef<AbortController | null>(null);
  const shareOperationRef = useRef<AbortController | null>(null);
  const communityShareSourceRef = useRef<CommunityShareSourceAttempt | null>(null);
  const communitySourceReceiptRef = useRef<{ identity: string; shareID: string } | null>(null);
  const generatedPublishAttemptRef = useRef<GeneratedCommunityPublicationAttempt | null>(null);
  useEffect(() => {
    setShareFeedback(null); setIsShareDialogOpen(false); setIsShareLinkCreating(false);
    return () => { shareOperationRef.current?.abort(); shareOperationRef.current = null; };
  }, [accountContext, result]);
  useEffect(() => {
    setIsCommunityPublishing(false);
    setCommunityPublishFeedback(null);
    return () => {
      communityPublishOperationRef.current?.abort();
      communityPublishOperationRef.current = null;
      communityShareSourceRef.current = null;
      communitySourceReceiptRef.current = null;
      generatedPublishAttemptRef.current = null;
    };
  }, [accountContext, result?.run_id, result?.generated_at, result?.symbol, result?.timeframe]);

  const t = useMemo(() => createTranslator(locale), [locale]);
  const experienceT = useMemo(() => createExperienceModeTranslator(locale), [locale]);
  const flowT = (key: Parameters<typeof flowRecoveryT>[1], parameters?: Record<string, string | number>) =>
    flowRecoveryT(locale, key, parameters);
  const learnPath = learnPathForLocale(locale);
  const isAccountContextCurrent = useCallback(
    (candidate: AccountGeneration) => sameAccountGeneration(
      candidate,
      activeAccountContextRef.current,
    ),
    [],
  );
  const isCommunityAccountCurrent = useCallback(
    () => isAccountContextCurrent(accountContext),
    [accountContext, isAccountContextCurrent],
  );

  function recordPaperHistoryImport(setup: HistoryItem["setup"]): void {
    if (!isAccountContextCurrent(accountContext)) return;
    const previous = paperAppliedSourceState === "blocked"
      || readPaperHistoryBlock(localDraftStorage(), API_BASE_URL, accountContext.accountID) ? "blocked" : paperAppliedSourceState;
    const next = paperSourceAfterHistory(previous, setup);
    writePaperHistoryBlock(localDraftStorage(), API_BASE_URL, accountContext.accountID, next === "blocked");
    setPaperAppliedSourceState(next);
    setPreparedPaperSource(undefined);
  }

  function recordExplicitPaperSource(nextForm: BacktestFormState): void {
    if (!isAccountContextCurrent(accountContext)) return;
    const next = paperSourceAfterExplicitSelection(paperAppliedSourceState, nextForm);
    writePaperHistoryBlock(localDraftStorage(), API_BASE_URL, accountContext.accountID, next !== "ready");
    setPaperAppliedSourceState(next);
    setPreparedPaperSource(undefined);
  }

  useEffect(() => listenToAuth((nextUser) => {
    communityPreviewCache.resolveViewer(nextUser?.uid ?? null);
    const nextContext = advanceAccountGeneration(
      activeAccountContextRef.current,
      nextUser?.uid ?? null,
    );
    // Move the live fence before React renders. A promise resolving in this
    // same turn must not commit data for the account that just signed out.
    activeAccountContextRef.current = nextContext;
    setAuthResolved(true);
    setAuthSnapshot((current) => {
      if (current.user === nextUser && sameAccountGeneration(current.context, nextContext)) {
        return current;
      }
      return { user: nextUser, context: nextContext };
    });
    if (!nextUser) {
      setAuthenticatedSession(null);
      return;
    }
    void currentToken(nextUser)
      .then((nextToken) => {
        if (
          nextToken
          && nextUser.uid === nextContext.accountID
          && sameAccountGeneration(nextContext, activeAccountContextRef.current)
        ) {
          setAuthenticatedSession({ context: nextContext, token: nextToken });
        }
      })
      .catch(() => {
        if (sameAccountGeneration(nextContext, activeAccountContextRef.current)) {
          setAuthenticatedSession(null);
        }
      });
  }), []);

  useEffect(() => {
    const online = () => setIsOnline(navigator.onLine !== false);
    window.addEventListener("online", online);
    window.addEventListener("offline", online);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", online);
    };
  }, []);

  useEffect(() => {
    if (!authResolved) return;
    setPaperAppliedSourceState((current) => current === "blocked"
      || readPaperHistoryBlock(localDraftStorage(), API_BASE_URL, accountContext.accountID) ? "blocked" : "ready");
    const explicitInput = firstDraftHydrationRef.current
      && (initialPresetRef.current || initialSharedDraftRef.current);
    // A slow auth bootstrap must not discard edits already made by the same
    // guest. UID changes still restore only the newly resolved account's data.
    const preserveEarlyEdits = firstDraftHydrationRef.current
      && sameAccountGeneration(accountContext, initialAccountContextRef.current)
      && JSON.stringify(draftForm) !== JSON.stringify(initialFormRef.current);
    const firstHydration = firstDraftHydrationRef.current;
    firstDraftHydrationRef.current = false;
    const saved = explicitInput || preserveEarlyEdits ? null
      : readLocalDraft(localDraftStorage(), API_BASE_URL, accountContext.accountID);
    const showStarter = claimStarterSample(browserStarterStorage(), {
      authResolved, accountID: accountContext.accountID,
      hasPreviousState: initialStarterState || Boolean(saved) || history.length > 0,
      explicitInput: explicitInput || initialStarterURLInputRef.current,
      hasEarlyEdits: preserveEarlyEdits, firstHydration,
      entryPath: initialStarterPathRef.current,
    });
    // The optional sample is an explicit import; a fresh Backtest keeps its futures source.
    starterSampleOriginRef.current = null;
    setStarterSampleVisible(showStarter);
    if (!preserveEarlyEdits) setBuilderLocalDraft(undefined);
    setIsBuilderReady(false);
    if (preserveEarlyEdits) {
      lastSavedDraftRef.current = JSON.stringify(initialFormRef.current);
      setDraftStatus({ state: "saving" });
    } else if (saved) {
      setForm(normalizeStrategyEntry(saved.form));
      lastSavedDraftRef.current = JSON.stringify(saved.form);
      setDraftStatus({ state: "restored", savedAt: saved.savedAt });
    } else {
      // Do not carry a previous account's editable setup into the new scope.
      const initial = explicitInput ? initialFormRef.current : { ...defaultForm };
      if (!explicitInput) setForm(initial);
      // An import/link that has not produced a new setup must not overwrite
      // an existing private draft merely because its network request failed.
      lastSavedDraftRef.current = JSON.stringify(initial);
      setDraftStatus({ state: localDraftStorage() ? "empty" : "unavailable" });
    }
    setDraftHydrated(accountContext);
  }, [accountContext, authResolved]);

  useEffect(() => {
    setBuilderLocalDraft(undefined);
    // Preserve Custom edits across preset switches; other modes ignore this draft.
  }, [form.strategyDSL]);

  useEffect(() => {
    if (!authResolved || !draftHydrated || !sameAccountGeneration(draftHydrated, accountContext)) return;
    const serialized = JSON.stringify(draftForm);
    if (serialized === lastSavedDraftRef.current) return;
    const captured = accountContext;
    setDraftStatus({ state: "saving" });
    const timer = window.setTimeout(() => {
      if (!sameAccountGeneration(captured, activeAccountContextRef.current)
        || serialized === lastSavedDraftRef.current) return;
      const savedAt = Date.now();
      const saved = saveLocalDraft(localDraftStorage(), API_BASE_URL, captured.accountID, draftForm, savedAt);
      if (saved) lastSavedDraftRef.current = serialized;
      setDraftStatus(saved ? { state: "saved", savedAt } : { state: "unavailable" });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [draftForm, accountContext, draftHydrated, authResolved]);

  // Preserve a final keystroke on reload/close; the same generation fence
  // prevents a departing account's delayed save from writing into another one.
  useEffect(() => {
    const flush = () => {
      if (!authResolved || !draftHydrated || !sameAccountGeneration(draftHydrated, accountContext)
        || !sameAccountGeneration(accountContext, activeAccountContextRef.current)) return;
      if (JSON.stringify(draftForm) === lastSavedDraftRef.current) return;
      saveLocalDraft(localDraftStorage(), API_BASE_URL, accountContext.accountID, draftForm);
    };
    window.addEventListener("pagehide", flush);
    return () => window.removeEventListener("pagehide", flush);
  }, [draftForm, accountContext, draftHydrated, authResolved]);

  function clearSavedDraft(): void {
    if (!sameAccountGeneration(accountContext, activeAccountContextRef.current)) return;
    const removed = removeLocalDraft(localDraftStorage(), API_BASE_URL, accountContext.accountID);
    if (removed) lastSavedDraftRef.current = JSON.stringify(draftForm);
    setDraftStatus({ state: removed ? "cleared" : "unavailable" });
  }

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setCommunityCapabilityState("pending");
    if (!isOnline) { setCommunityCapabilityState("failed"); return undefined; }
    fetchCapabilities(locale, controller.signal, communityCapabilityRetry > 0)
      .then((response) => {
        if (!active) return;
        setSupportsDerivativesBacktesting(resolveDerivativesCapability(
          response.capabilities?.derivatives_backtesting,
        ));
        setSupportsReproducibleSharing(
          response.capabilities?.shareable_results_v1?.enabled === true
          && response.capabilities.shareable_results_v1.reproducible_strategies === true,
        );
        setSupportsShareableResults(
          response.capabilities?.shareable_results_v1?.enabled === true,
        );
        const communityCapability = resolveStrategyCommunityCapability(response);
        if (!communityCapability.enabled) communityPreviewCache.invalidate();
        setStrategyCommunityCapability(communityCapability);
        setCommunityCapabilityState("ready");
      })
      .catch(() => {
        if (active) {
          setSupportsDerivativesBacktesting(null);
          setSupportsReproducibleSharing(false);
          setSupportsShareableResults(false);
          setStrategyCommunityCapability(resolveStrategyCommunityCapability(null));
          setCommunityCapabilityState("failed");
        }
      });
    return () => { active = false; controller.abort(); };
  }, [locale, isOnline, communityCapabilityRetry]);

  useEffect(() => {
    if (!authResolved) return undefined;
    const descriptor = sharedStrategyDescriptorRef.current;
    if (!descriptor) return undefined;
    sharedStrategyDescriptorRef.current = null;
    const controller = new AbortController();
    const captured = accountContext;
    void fetchSharedStrategy(descriptor, controller.signal).then((shared) => {
      if (controller.signal.aborted || !isAccountContextCurrent(captured)) return;
      sharedStrategyAttributionRef.current = descriptor.attributionEventID
        ? createPendingSharedStrategyAttribution(
            {
              shareID: descriptor.shareID,
              attributionEventID: descriptor.attributionEventID,
            },
            shared.reproduction?.strategyDSL as StrategyRuleDSL | undefined,
          )
        : null;
      if (descriptor.attributionEventID && !sharedStrategyAttributionRef.current) {
        throw new Error("shared_strategy_invalid");
      }
      setVariantBaseline(null);
      setForm((current) => applySharedStrategy(current, shared));
      setActiveSection("backtest");
      setGuidePresetLoaded(true);
      setMessage(t("ready"));
      trackFunnelEvent("shared_strategy_loaded", {
        source: "shared_result",
        strategy_mode: "builder",
      });
    }).catch(() => {
      if (!controller.signal.aborted && isAccountContextCurrent(captured)) setError(t("shareFailed"));
    });
    return () => controller.abort();
  }, [t, authResolved, accountContext, isAccountContextCurrent]);

  useEffect(() => {
    const pending = sharedStrategyAttributionRef.current;
    if (pending && !sharedAttributionForBacktest(form, pending)) {
      sharedStrategyAttributionRef.current = null;
    }
  }, [form.strategyDSL, form.strategyMode]);

  useEffect(() => {
    if (form.provider === "binance_futures" || !shouldFallbackToSpot(supportsDerivativesBacktesting, form.executionModel)) return;
    setForm((current) => ({ ...current, executionModel: "spot" }));
  }, [form.executionModel, form.provider, supportsDerivativesBacktesting]);

  useEffect(() => {
    let active = true;
    Promise.allSettled([fetchPublicPlans(locale), fetchAnonymousUsage(locale)]).then(([catalogResult, anonymousResult]) => {
      if (!active) return;
      setPricingMode(resolveAppPricingMode(
        import.meta.env.VITE_PAID_APP_MARKETING,
        catalogResult.status === "fulfilled" ? catalogResult.value : null,
      ));
      const next: PlanLimitsByTier = {};
      if (catalogResult.status === "fulfilled" && Array.isArray(catalogResult.value.plans)) {
        catalogResult.value.plans.forEach((plan) => {
          const limits = normalizedPlanLimits(plan.limits);
          if (["free", "pro", "power"].includes(plan.id) && limits) {
            next[plan.id] = limits;
          }
        });
      }
      if (anonymousResult.status === "fulfilled") {
        const limits = normalizedPlanLimits(anonymousResult.value.limits);
        if (limits) {
          next.guest = limits;
          setAnonymousUsage({ ...anonymousResult.value, limits });
        }
      }
      setPlanLimits(next);
      const loadedTiers = Object.keys(next);
      trackFunnelEvent(
        loadedTiers.length ? "pricing_catalog_loaded" : "pricing_catalog_failed",
        loadedTiers.length
          ? { tier_count: loadedTiers.length, status: loadedTiers.length === 4 ? "complete" : "partial" }
          : {},
      );
    });
    return () => {
      active = false;
    };
  }, [locale]);

  useEffect(() => {
    try {
      localStorage.setItem("stoptrading.locale", locale);
    } catch {
      // Language changes still apply when browser storage is unavailable.
    }
    document.documentElement.lang = locale;
    document.documentElement.dir = isRTL(locale) ? "rtl" : "ltr";
    updateLocalizedSeo(locale, pricingMode);
  }, [locale, pricingMode]);

  useEffect(() => {
    setAnalyticsExperience(experienceMode);
  }, [experienceMode]);

  useEffect(() => {
    const syncPath = () => {
      const pathLocale = localeFromPath(window.location.pathname);
      if (pathLocale) setLocale(pathLocale);
      setIsPrivacyOpen(stripLocaleFromPath(window.location.pathname) === "/privacy");
      setActiveSection(sectionFromPath(window.location.pathname));
      updateLocalizedSeo(pathLocale || resolveLocale(localStorage.getItem("stoptrading.locale") || navigator.language || "en"), pricingMode);
    };
    window.addEventListener("popstate", syncPath);
    return () => window.removeEventListener("popstate", syncPath);
  }, [pricingMode]);

  useEffect(() => {
    const accountID = user?.uid || null;
    const limit = accountID
      ? SIGNED_IN_LOCAL_HISTORY_FALLBACK_LIMIT
      : GUEST_LOCAL_HISTORY_LIMIT;
    setHistoryCache({
      scope: localHistoryStorageScope(accountID),
      items: loadLocalHistoryForAccount(
        window.localStorage,
        accountID,
        limit,
      ),
    });
    setResult(null);
    setSelectedTrade(null);
    setBots([]);
    setUsage(null);
    historyRerunCandidateRef.current = null;
    sharedStrategyAttributionRef.current = null;
    setIsLoadingAccount(false);
    setError(null);
  }, [accountContext, user?.uid]);

  useEffect(() => {
    const accountID = user?.uid || null;
    const storageKey = localHistoryStorageKey(accountID);
    const scope = localHistoryStorageScope(accountID);
    const limit = accountID ? localHistoryLimit(usage) : GUEST_LOCAL_HISTORY_LIMIT;
    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage || event.key !== storageKey) return;
      setHistoryCache({
        scope,
        items: loadLocalHistoryForAccount(window.localStorage, accountID, limit),
      });
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [user?.uid, usage]);

  useEffect(() => {
    if (
      !pendingSignUpImport
      || !user?.uid
      || user.uid !== pendingSignUpImport.uid
      || !token
      || !authTokenSource
    ) return;

    const requestContext = accountContext;
    const accountID = user.uid;
    const operationID = `${accountID}:${pendingSignUpImport.items.map((item) => item.id).join(",")}`;
    if (signUpImportOperationRef.current === operationID) return;
    signUpImportOperationRef.current = operationID;
    const limit = localHistoryLimit(usage);
    const merged = mergeHistory(
      pendingSignUpImport.items,
      loadLocalHistoryForAccount(window.localStorage, accountID, limit),
      limit,
    );
    saveLocalHistoryForAccount(window.localStorage, accountID, merged, limit);
    setHistoryCache({ scope: localHistoryStorageScope(accountID), items: merged });
    if (pendingSignUpImport.result) {
      const importedResult = { ...pendingSignUpImport.result, usage: null };
      setResult(importedResult);
      setSelectedTrade(latestTrade(importedResult.trades || []) || null);
    }
    setMessage(t("accountCreated"));

    if (merged.length) {
      void syncHistory(
        authTokenSource,
        merged.map(cloudHistoryItem),
        locale,
      ).then((cloudResponse) => {
        if (!isAccountContextCurrent(requestContext)) return;
        const authoritative = mergeHistory(merged, cloudResponse.items || [], limit);
        saveLocalHistoryForAccount(window.localStorage, accountID, authoritative, limit);
        setHistoryCache({
          scope: localHistoryStorageScope(accountID),
          items: authoritative,
        });
        if (cloudResponse.usage) setUsage(cloudResponse.usage);
      }).catch(() => {
        // Local account copy remains durable and account bootstrap retries it.
      }).finally(() => {
        if (signUpImportOperationRef.current === operationID) {
          signUpImportOperationRef.current = null;
        }
        setPendingSignUpImport((current) => {
          if (!current) return null;
          const currentOperationID = `${current.uid}:${current.items.map((item) => item.id).join(",")}`;
          return currentOperationID === operationID ? null : current;
        });
      });
    } else {
      signUpImportOperationRef.current = null;
      setPendingSignUpImport(null);
    }
  }, [
    accountContext,
    authTokenSource,
    isAccountContextCurrent,
    locale,
    pendingSignUpImport,
    t,
    token,
    usage,
    user?.uid,
  ]);

  useEffect(() => {
    const destination = deferredSectionAfterSignInRef.current;
    if (!token || !destination) return;
    deferredSectionAfterSignInRef.current = null;
    navigateToSection(destination, true);
  }, [token]);


  useEffect(() => {
    if (!token || !user?.uid || !authTokenSource) return;
    const controller = new AbortController();
    const accountID = user.uid;
    const requestContext = accountContext;
    const scope = localHistoryStorageScope(accountID);
    const current = () => !controller.signal.aborted && isAccountContextCurrent(requestContext);
    setIsLoadingAccount(true);
    void loadAccountBootstrap({
      usage: () => fetchUsage(authTokenSource, locale, controller.signal),
      history: () => fetchHistory(authTokenSource, locale, controller.signal),
      isCurrent: current,
      onUsage: (value) => { setUsage(value); setIsLoadingAccount(false); },
      onHistory: (response, nextUsage) => {
        // An early history response can render immediately without trimming the
        // durable account cache before its authoritative quota has arrived.
        const limit = nextUsage ? localHistoryLimit(nextUsage) : LOCAL_HISTORY_MAX_LIMIT;
        const local = loadLocalHistoryForAccount(window.localStorage, accountID, limit);
        setHistoryCache({ scope, items: mergeHistory(local, response.items || [], limit) });
        setIsLoadingAccount(false);
      },
      onComplete: ([usageResult, historyResult]) => {
        const usageResponse = usageResult.status === "fulfilled" ? usageResult.value : null;
        const historyResponse = historyResult.status === "fulfilled" ? historyResult.value : null;
        const nextUsage = historyResponse?.usage || usageResponse;
        const nextLimit = nextUsage ? localHistoryLimit(nextUsage) : LOCAL_HISTORY_MAX_LIMIT;
        if (nextUsage) setUsage(nextUsage);
        const localItems = loadLocalHistoryForAccount(
          window.localStorage,
          accountID,
          nextLimit,
        );
        const nextHistory = mergeHistory(
          localItems,
          historyResponse?.items || [],
          nextLimit,
        );
        saveLocalHistoryForAccount(window.localStorage, accountID, nextHistory, nextLimit);
        setHistoryCache({ scope, items: nextHistory });
        const cloudIDs = new Set((historyResponse?.items || []).map((item) => item.id));
        const unsyncedLocalItems = localItems.filter((item) => !cloudIDs.has(item.id));
        if (historyResponse && unsyncedLocalItems.length) {
          void syncHistory(
            authTokenSource,
            unsyncedLocalItems.map(cloudHistoryItem),
            locale,
          ).then((cloudResponse) => {
            if (!current()) return;
            const merged = mergeHistory(nextHistory, cloudResponse.items || [], nextLimit);
            saveLocalHistoryForAccount(window.localStorage, accountID, merged, nextLimit);
            setHistoryCache({ scope, items: merged });
            if (cloudResponse.usage) setUsage(cloudResponse.usage);
          }).catch(() => {
            // Retain local items; the next account bootstrap retries them.
          });
        }

        // A failure is surfaced only when neither required account read succeeded.
        if (!nextUsage && !historyResponse) {
          const failure = usageResult.status === "rejected"
            ? usageResult.reason
            : historyResult.status === "rejected"
              ? historyResult.reason
              : new Error("Account data is unavailable");
          setError(localizedAPIErrorMessage(failure, t, "accountSignInUnavailableHint"));
        }
        setIsLoadingAccount(false);
      },
    });
    return () => controller.abort();
  }, [accountContext, authTokenSource, isAccountContextCurrent, locale, t, token, user?.uid]);

  useEffect(() => {
    if (!token || !authTokenSource || !["account", "bots"].includes(activeSection)) return;
    const controller = new AbortController(), captured = accountContext;
    const current = () => !controller.signal.aborted && isAccountContextCurrent(captured);
    setIsBotsRefreshing(true);
    void fetchRealtimeBots(authTokenSource, locale, controller.signal)
      .then((response) => { if (current()) setBots(response.bots || []); })
      .catch(() => { /* Optional bot chrome never blocks usage or history. */ })
      .finally(() => { if (current()) setIsBotsRefreshing(false); });
    return () => { controller.abort(); setIsBotsRefreshing(false); };
  }, [accountContext, activeSection, authTokenSource, isAccountContextCurrent, locale, token]);

  const tradeDirectionLabels = useMemo(() => translatedLabels(tradeDirectionLabelKeys, t), [t]);
  const exitModeLabels = useMemo(() => translatedLabels(exitModeLabelKeys, t), [t]);
  const executionModelLabels = useMemo(() => translatedLabels(executionModelLabelKeys, t), [t]);
  const marginModeLabels = useMemo(() => translatedLabels(marginModeLabelKeys, t), [t]);
  const availableExecutionModels: readonly BacktestFormState["executionModel"][] = form.provider === "binance_futures" ? ["futures"] : shouldOfferFutures(supportsDerivativesBacktesting)
    ? executionModels
    : ["spot"];
  const allocatedCapital = form.initialCapital * form.positionSizePct / 100;
  const estimatedNotional = allocatedCapital * (form.executionModel === "futures" ? form.leverage : 1);
  const estimatedStopLoss = estimatedNotional * form.stopLossPct / 100;
  const estimatedAccountLossPercent = form.initialCapital > 0
    ? estimatedStopLoss / form.initialCapital * 100
    : 0;
  const maximumMaintenanceMarginPercent = Math.min(
    20,
    Math.max(0.01, 100 / Math.max(form.leverage, 1) - 0.01),
  );
  const activeUsage = user ? usage : anonymousUsage;
  const maxBacktestCandles = activeUsage?.limits.max_backtest_candles || DEFAULT_MAX_BACKTEST_CANDLES;
  const maxPeriodDays = activeUsage?.limits.max_period_days;
  const maxRangeDays = useMemo(
    () => effectiveMaxDaysForTimeframe(form.timeframe, maxBacktestCandles, maxPeriodDays),
    [form.timeframe, maxBacktestCandles, maxPeriodDays],
  );
  const availablePeriods = useMemo(
    () => periods.filter((period) => period === "Custom" || quickPeriodApproxDays[period] <= maxRangeDays),
    [maxRangeDays],
  );
  const rangeSummary = useMemo(() => backtestRangeSummary(form, maxBacktestCandles, maxPeriodDays), [form, maxBacktestCandles, maxPeriodDays]);
  const customDateRecovery = useMemo(
    () => resolveCustomDateRecovery({
      period: form.period,
      startDate: form.startDate,
      endDate: form.endDate,
      maximumDays: maxRangeDays,
      planMaximumDays: maxPeriodDays,
      candleMaximumDays: maxDaysForTimeframe(form.timeframe, maxBacktestCandles),
    }),
    [
      form.endDate,
      form.period,
      form.startDate,
      form.timeframe,
      maxBacktestCandles,
      maxPeriodDays,
      maxRangeDays,
    ],
  );
  const botCreationAction = resolveBotCreationAction({
    signedIn: Boolean(user && token),
    busy: isBotActionRunning || isBotsRefreshing || isUsageRefreshing,
    usageLoaded: Boolean(usage),
    currentBots: bots.length,
    maximumBots: usage?.limits.max_realtime_bots ?? 0,
  });
  const botStrategyNeedsReview = form.strategyMode === "builder" && (
    !form.strategyDSL || Boolean(builderLocalDraft
      && strategyRuleDSLIdentity(builderLocalDraft) !== strategyRuleDSLIdentity(form.strategyDSL))
  );
  const botCreationActionLabel =
    botCreationAction === "sign_in"
      ? t("signIn")
      : botCreationAction === "wait"
        ? t("running")
        : botCreationAction === "refresh_usage"
          ? t("refresh")
          : botCreationAction === "view_plans"
            ? t("pricing")
            : botStrategyNeedsReview
              ? strategyBuilderT(locale, "openBuilder")
              : t("createBot");
  const backtestPrimaryLabel =
    form.strategyMode === "builder" && !isBuilderReady
      ? strategyBuilderT(locale, builderValidationState === "validating" ? "validating" : "apply")
      : customDateRecovery?.kind === "invalid_order"
          ? flowT("invalidDateOrder")
          : customDateRecovery?.kind === "use_allowed_start"
            ? flowT("useAllowedDates")
            : t("runBacktest");
  const backtestEta = `${t("estimatedWait")}: ${estimatedBacktestWait(rangeSummary.candles, locale)}`;
  const trades = result?.trades || [];
  const newestTrades = useMemo(() => sortTradesNewest(trades), [trades]);

  useEffect(() => {
    if (!isRunning) return;
    window.requestAnimationFrame(() => {
      document.querySelector(".run-progress")?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [isRunning]);

  useEffect(() => {
    if (activeSection !== "results") return;
    const frame = window.requestAnimationFrame(() => {
      const heading = resultHeadingRef.current;
      if (!heading) return;
      heading.scrollIntoView({ behavior: "auto", block: "start" });
      heading.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeSection, result, resultRevealRequest]);

  useEffect(() => {
    if (availablePeriods.includes(form.period)) {
      return;
    }
    const fallback = [...availablePeriods].reverse().find((period) => period !== "Custom") || "Custom";
    setForm((current) => ({ ...current, period: fallback }));
    setMessage(t("periodAdjusted"));
  }, [availablePeriods, form.period, t]);

  useEffect(() => {
    setForm((current) => current.useAI ? normalizeStrategyEntry(current) : current);
  }, [form.strategyMode, form.strategyDSL, form.useAI]);

  async function refreshAccountUsage(): Promise<boolean> {
    if (!token || !authTokenSource || isUsageRefreshing) return false;
    const requestContext = accountContext;
    setIsUsageRefreshing(true);
    setError(null);
    try {
      const nextUsage = await fetchUsage(authTokenSource, locale);
      if (!isAccountContextCurrent(requestContext)) return false;
      setUsage(nextUsage);
      return true;
    } catch (err) {
      if (isAccountContextCurrent(requestContext)) {
        setError(localizedAPIErrorMessage(err, t, "accountSignInUnavailableHint"));
      }
      return false;
    } finally {
      if (isAccountContextCurrent(requestContext)) setIsUsageRefreshing(false);
    }
  }

  function focusRecoveryTarget(target: React.RefObject<HTMLElement | null>): void {
    window.requestAnimationFrame(() => {
      target.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      target.current?.focus({ preventScroll: true });
    });
  }

  async function performBacktestPrimaryAction(): Promise<void> {
    if (form.strategyMode === "builder" && !isBuilderReady) {
      setError(null);
      await builderValidationRef.current?.validate();
      return;
    }
    if (form.strategyMode === "manual") {
      setError(simpleStrategyT(locale, "legacy"));
      return;
    }
    if (customDateRecovery?.kind === "invalid_order") {
      setError(flowT("invalidDateOrder"));
      focusRecoveryTarget(dateRecoveryRef);
      return;
    }
    if (customDateRecovery?.kind === "use_allowed_start") {
      updateForm("startDate", customDateRecovery.effectiveStartDate);
      setError(null);
      setMessage(flowT("dateAdjusted", {
        previous: customDateRecovery.requestedStartDate,
        current: customDateRecovery.effectiveStartDate,
      }));
      focusRecoveryTarget(dateRecoveryRef);
      return;
    }
    await handleRunBacktest();
  }

  async function handleRunBacktest() {
    if (form.ftmoSimulation && form.strategyMode !== "method") { setError(simulationT(locale, "methodRequired")); return; }
    if (backtestFlightRef.current && isAccountContextCurrent(backtestFlightRef.current.context)) return;
    if (form.strategyMode === "manual") {
      setError(simpleStrategyT(locale, "legacy"));
      return;
    }
    if (navigator.onLine === false) {
      setError(localSupportT(locale, "onlineRequired"));
      return;
    }
    if (form.strategyMode === "builder" && !form.strategyDSL) {
      setError(flowT("builderApplyFirst"));
      focusRecoveryTarget(builderRecoveryRef);
      return;
    }
    const requestContext = accountContext;
    const startedAccountID = requestContext.accountID;
    const startedAuthTokenSource = startedAccountID ? authTokenSource : null;
    if (startedAccountID && (!token || !startedAuthTokenSource)) {
      setError(t("accountSignInUnavailableHint"));
      return;
    }
    const formSnapshot = form;
    // Pair the completed result with the baseline chosen for this request,
    // even if another draft is selected while the request is in flight.
    const startedVariantBaseline = variantBaseline;
    // Freeze the range, setup and rerun identity in one synchronous snapshot.
    // A quick-range request may otherwise cross local midnight while awaiting
    // the backend and be saved as a different run than the one actually sent.
    const preparedBacktest = prepareBacktestSnapshot(
      formSnapshot,
      maxBacktestCandles,
      maxPeriodDays,
    );
    const operation = ++backtestOperationRef.current;
    backtestFlightRef.current = { context: requestContext, operation };
    const isCurrentBacktest = () => isAccountContextCurrent(requestContext) && backtestOperationRef.current === operation;
    const rerunCandidate = historyRerunCandidateRef.current;
    const sampleOrigin = starterSampleOriginRef.current;
    const runOrigin = backtestRunOriginAnalyticsParameters(formSnapshot,
      rerunCandidate?.fingerprint === preparedBacktest.fingerprint ? "history"
        : sampleOrigin && isAccountContextCurrent(sampleOrigin.context) && sampleOrigin.fingerprint === preparedBacktest.fingerprint
          ? "starter_sample" : "strategy");
    const sourceHistoryItemID = startedAccountID
      && rerunCandidate?.fingerprint === preparedBacktest.fingerprint
      ? rerunCandidate.id
      : null;
    if (rerunCandidate && !sourceHistoryItemID) {
      historyRerunCandidateRef.current = null;
    }
    const sharedAttribution = sharedAttributionForBacktest(
      formSnapshot,
      sharedStrategyAttributionRef.current,
    );
    // Imported attribution is single-use. Consume it before the request so a
    // validation/network failure cannot poison every later ordinary backtest.
    sharedStrategyAttributionRef.current = null;
    const backtestStartedAt = performance.now();
    trackFunnelEvent("backtest_started", {
      symbol: form.symbol,
      timeframe: form.timeframe,
      strategy_mode: form.strategyMode,
      ...backtestStrategyAnalyticsParameters(form.strategyMode, form.strategyName),
      ...runOrigin,
      ui_experience: experienceMode,
    });
    setStarterSampleVisible(false);
    setIsRunning(true);
    setError(null);
    setMessage(t("running"));
    try {
      const response = await runBacktest(
        formSnapshot,
        startedAuthTokenSource,
        locale,
        maxBacktestCandles,
        maxPeriodDays,
        sourceHistoryItemID,
        preparedBacktest.dateRange,
        sharedAttribution,
      );
      const localItemID = response.quota_exempt && sourceHistoryItemID
        ? sourceHistoryItemID
        : `local-${response.run_id || Date.now()}`;
      const localItem: HistoryItem = {
        id: localItemID,
        createdAt: response.generated_at || new Date().toISOString(),
        prompt: preparedBacktest.prompt,
        setup: preparedBacktest.setup,
        result: response,
      };
      const scope = localHistoryStorageScope(startedAccountID);
      const nextLimit = localHistoryLimit(
        response.usage || (startedAccountID ? usage : anonymousUsage),
      );
      completeBacktestAndSync({
        item: localItem, accountID: startedAccountID, storage: window.localStorage, limit: nextLimit,
        isCurrent: isCurrentBacktest,
        publishLocal: (nextHistory) => {
          setHistoryCache({ scope, items: nextHistory });
          historyRerunCandidateRef.current = startedAccountID
            ? { id: localItem.id, fingerprint: preparedBacktest.fingerprint }
            : null;
          setResult(response);
          setCompletedVariantComparison(startedVariantBaseline && startedVariantBaseline.id !== localItem.id
            ? { baseline: startedVariantBaseline, candidate: localItem }
            : null);
          setSelectedTrade(latestTrade(response.trades || []) || null);
          if (startedAccountID) {
            setUsage(response.usage || usage);
          } else if (response.usage) {
            setAnonymousUsage(response.usage);
          }
          setIsRunning(false);
          setMessage(resultStatusMessage(response, t, locale));
          trackFunnelEvent("backtest_completed", {
            symbol: response.symbol,
            timeframe: response.timeframe,
            ...backtestStrategyAnalyticsParameters(formSnapshot.strategyMode, formSnapshot.strategyName),
            ...runOrigin,
            ...backtestDurationAnalyticsParameters(backtestStartedAt, performance.now()),
            trade_count: response.trades?.length || 0,
            net_profit_percentage: response.performance.total_profit_percentage,
            ui_experience: experienceMode,
          });
          navigateToSection("results", true);
        },
        synchronize: startedAccountID && startedAuthTokenSource
          ? () => syncHistory(startedAuthTokenSource, [cloudHistoryItem(localItem)], locale)
          : undefined,
        // Cloud history does not consume quota, so its late usage snapshot must
        // not replace a newer backtest ledger already on screen.
        publishSynchronizedHistory: (items) => setHistoryCache({ scope, items }),
      });
    } catch (err) {
      if (!isCurrentBacktest()) return;
      trackFunnelEvent("backtest_failed", {
        symbol: formSnapshot.symbol,
        timeframe: formSnapshot.timeframe,
        ...backtestStrategyAnalyticsParameters(formSnapshot.strategyMode, formSnapshot.strategyName),
        ...runOrigin,
        ...backtestFailureAnalyticsParameters(err, backtestStartedAt, performance.now()),
        ui_experience: experienceMode,
      });
      setError(err instanceof Error && [METHOD_UNAVAILABLE, METHOD_INVALID, METHOD_RESULT_MISMATCH].includes(err.message)
        ? methodT(locale, err.message === METHOD_RESULT_MISMATCH ? "invalid" : "unavailable")
        : localizedAPIErrorMessage(err, t, "backtestFailed"));
      setMessage(t("backtestFailed"));
    } finally {
      if (backtestFlightRef.current?.operation === operation) backtestFlightRef.current = null;
      if (isCurrentBacktest()) setIsRunning(false);
    }
  }

  function handleExperienceModeChange(nextMode: ExperienceMode, source: "backtest" | "settings") {
    const previousMode = experienceMode;
    if (hasChosenExperienceMode && previousMode === nextMode) return;

    saveExperienceMode(window.localStorage, nextMode);
    setExperienceMode(nextMode);
    setHasChosenExperienceMode(true);

    if (hasChosenExperienceMode) {
      trackFunnelEvent("ui_experience_changed", {
        mode: nextMode,
        previous_mode: previousMode,
        source,
      });
    } else {
      trackFunnelEvent("ui_experience_selected", {
        mode: nextMode,
        source,
      });
    }
  }

  function updateForm<K extends keyof BacktestFormState>(key: K, value: BacktestFormState[K]) {
    if (key === "strategyMode" && value !== form.strategyMode) setVariantBaseline(null);
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === "strategyMode" && value !== "method") { next.strategyMethod = undefined; next.unsupportedMethodExecution = undefined; }
      if (key === "symbol") { next.provider = undefined; next.market = undefined; }
      if (key === "strategyMode" && value !== "manual") {
        next.useAI = false;
      }
      if (key === "strategyMode" && value === "auto" && !isStrategyPresetName(next.strategyName)) {
        next.strategyName = DEFAULT_STRATEGY_PRESET;
      }
      return next;
    });
  }

  function applyResult(nextResult: BacktestResult, status: string) {
    setCompletedVariantComparison(null);
    setResult(nextResult);
    setSelectedTrade(latestTrade(nextResult.trades || []) || null);
    setMessage(status);
    navigateToSection("results", true);
  }

  async function shareLegacyResult() {
    if (!result || shareOperationRef.current || !isAccountContextCurrent(accountContext)) return;
    const controller = new AbortController(); shareOperationRef.current = controller;
    const requestContext = accountContext;
    const isCurrent = () => shareOperationRef.current === controller && !controller.signal.aborted && isAccountContextCurrent(requestContext);
    setIsShareLinkCreating(true); setShareFeedback(null); setError(null);
    const url = legacyShareSetupURL(result, history, locale);
    const text = `${shareEvidenceTextForResult(result, locale, t)}${url ? `\n${url}` : ""}`;
    try {
      if ("share" in navigator && typeof navigator.share === "function") {
        await navigator.share({ title: `${t("backtest")} · ${t("results")}`, text, ...(url ? { url } : {}) });
        if (isCurrent()) { setMessage(t("shareReady")); setShareFeedback({ message: t("shareReady"), failed: false }); }
      } else {
        await navigator.clipboard.writeText(text);
        if (isCurrent()) { setMessage(t("shareCopied")); setShareFeedback({ message: t("shareCopied"), failed: false }); }
      }
    } catch (err) {
      if (!isCurrent()) return;
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(t("shareFailed")); setShareFeedback({ message: t("shareFailed"), failed: true });
    } finally {
      if (isCurrent()) setIsShareLinkCreating(false);
      if (shareOperationRef.current === controller) shareOperationRef.current = null;
    }
  }

  function handleShareResult() {
    if (!result || shareOperationRef.current || !isAccountContextCurrent(accountContext)) return;
    setShareFeedback(null);
    trackFunnelEvent("share_preview_opened", {
      source: "result",
      reproducible_available: supportsReproducibleSharing
        && canShareReproducibleStrategy(result, history),
    });
    if (!supportsShareableResults || !user || !token || !authTokenSource) {
      void shareLegacyResult();
      return;
    }
    setIsShareDialogOpen(true);
  }

  async function shareProfitableMethodToCommunity() {
    if (communityPublishOperationRef.current || !result || !strategyCommunityCapability.generatedPublishing) return;
    if (!user || !authTokenSource || !accountContext.accountID) {
      handleSignInFocus("results");
      return;
    }
    const requestContext = accountContext;
    if (!isAccountContextCurrent(requestContext) || !canPublishProfitableCommunityResult(result, history)) return;
    const controller = new AbortController();
    communityPublishOperationRef.current = controller;
    const isCurrent = () => communityPublishOperationRef.current === controller
      && !controller.signal.aborted && isAccountContextCurrent(requestContext);
    setIsCommunityPublishing(true);
    setCommunityPublishFeedback(null);
    try {
      const sourceAttempt = prepareCommunityShareSource(
        requestContext.accountID!, result, history, locale, communityShareSourceRef.current,
      );
      communityShareSourceRef.current = sourceAttempt;
      const cached = communitySourceReceiptRef.current;
      const shareID = cached?.identity === sourceAttempt.identity ? cached.shareID
        : await createCommunityShareSource(sourceAttempt, authTokenSource, controller.signal, isCurrent);
      if (!isCurrent()) return;
      communitySourceReceiptRef.current = { identity: sourceAttempt.identity, shareID };
      const attempt = prepareGeneratedCommunityPublication(
        requestContext.accountID!, shareID, locale, generatedPublishAttemptRef.current,
      );
      generatedPublishAttemptRef.current = attempt;
      const publication = await publishGeneratedCommunityStrategy(attempt, authTokenSource, controller.signal, isCurrent);
      if (isCurrent()) setCommunityPublishFeedback({ message: communityT(locale, publication.status === "listed" ? "generatedListed" : "generatedPending"), failed: false });
    } catch (error) {
      if (isCurrent()) setCommunityPublishFeedback({ message: communityT(locale,
        error instanceof Error && error.name === "TimeoutError" ? "requestTimeout" : "generatedError"), failed: true });
    } finally {
      if (communityPublishOperationRef.current === controller) {
        communityPublishOperationRef.current = null;
        if (isAccountContextCurrent(requestContext)) setIsCommunityPublishing(false);
      }
    }
  }

  function openCommunitySharingHistory() {
    setCommunityHistoryRequested(true);
    setCommunityMarketSelection(null);
    navigateToSection("community");
  }

  async function publishShare(mode: ShareMode) {
    if (!result || !authTokenSource || shareOperationRef.current || !isAccountContextCurrent(accountContext)) return;
    const controller = new AbortController(); shareOperationRef.current = controller;
    const requestContext = accountContext;
    const isCurrent = () => shareOperationRef.current === controller && !controller.signal.aborted && isAccountContextCurrent(requestContext);
    setIsShareLinkCreating(true);
    setShareFeedback(null);
    setError(null);
    trackFunnelEvent("share_content_selected", { content_type: mode });
    try {
      const publicURL = await createShareableResult(
        mode,
        result,
        history,
        locale,
        authTokenSource,
        controller.signal,
        isCurrent,
      );
      if (!isCurrent()) return;
      setIsShareDialogOpen(false);
      trackFunnelEvent("share_link_create_succeeded", { content_type: mode });
      const text = shareEvidenceTextForResult(result, locale, t);
      if ("share" in navigator && typeof navigator.share === "function") {
        await navigator.share({ title: `${t("backtest")} · ${t("results")}`, text, url: publicURL });
        if (isCurrent()) { setMessage(t("shareReady")); setShareFeedback({ message: t("shareReady"), failed: false }); }
      } else {
        await navigator.clipboard.writeText(`${text}\n${publicURL}`);
        if (isCurrent()) { setMessage(t("shareCopied")); setShareFeedback({ message: t("shareCopied"), failed: false }); }
      }
      if (!isCurrent()) return;
      trackFunnelEvent("result_shared", { content_type: mode });
    } catch (err) {
      if (!isCurrent()) return;
      if (err instanceof DOMException && err.name === "AbortError") {
        trackFunnelEvent("share_cancelled", { content_type: mode });
      } else {
        trackFunnelEvent("share_link_create_failed", { content_type: mode });
        setError(t("shareFailed")); setShareFeedback({ message: t("shareFailed"), failed: true });
      }
    } finally {
      if (isCurrent()) setIsShareLinkCreating(false);
      if (shareOperationRef.current === controller) shareOperationRef.current = null;
    }
  }

  function applyCommunityStrategy(
    publication: CommunityPublication,
    receipt: CommunityDuplicateReceipt,
  ) {
    if (!receipt.reproduction) {
      setError(t("shareReusableUnavailable"));
      return;
    }
    starterSampleOriginRef.current = null;
    setVariantBaseline(null);
    recordExplicitPaperSource(applyCommunityReproduction(form, receipt.reproduction));
    setForm((current) => applyCommunityReproduction(current, receipt.reproduction!));
    sharedStrategyAttributionRef.current = receipt.shareID && receipt.attributionEventID
      ? createPendingSharedStrategyAttribution(
          {
            shareID: receipt.shareID,
            attributionEventID: receipt.attributionEventID,
          },
          receipt.reproduction.strategyDSL,
        )
      : null;
    setGuidePresetLoaded(true);
    setError(null);
    setMessage(t("ready"));
    trackFunnelEvent("strategy_community_recipe_applied", {
      evidence_state: publication.evidence.state,
      configuration_attested: publication.evidence.configurationAttested,
      strategy_mode: "builder",
    });
    navigateToSection("backtest", true);
  }

  function openHistoryItem(item: HistoryItem) {
    if (isRunning || !isAccountContextCurrent(accountContext)) return;
    setVariantBaseline(null);
    sharedStrategyAttributionRef.current = null;
    starterSampleOriginRef.current = null;
    trackHistoryEvent("result_opened");
    trackFunnelEvent("research_resume_selected", { source: "saved_history", strategy_mode: item.setup.strategyMode || "unknown" });
    recordPaperHistoryImport(item.setup);
    setForm((current) => {
      const next = applyHistorySetup(current, item);
      const preparedBacktest = prepareBacktestSnapshot(
        next,
        maxBacktestCandles,
        maxPeriodDays,
      );
      historyRerunCandidateRef.current = { id: item.id, fingerprint: preparedBacktest.fingerprint };
      return next;
    });
    if (hasCompleteHistoryResult(item)) {
      applyResult(item.result, t("loadedResultFromHistory"));
    } else {
      setMessage(t("setupLoadedFromHistory"));
      navigateToSection("backtest", true);
    }
  }

  function duplicateHistoryItem(item: HistoryItem) {
    if (isRunning || !isAccountContextCurrent(accountContext)) return;
    sharedStrategyAttributionRef.current = null;
    starterSampleOriginRef.current = null;
    setVariantBaseline(applyHistorySetup(defaultForm, item).strategyMode === "manual" ? null : item);
    trackFunnelEvent("research_variant_prepared", { source: "saved_history", strategy_mode: item.setup.strategyMode || "unknown" });
    recordPaperHistoryImport(item.setup);
    setForm((current) => {
      const next = applyHistorySetup(current, item);
      const preparedBacktest = prepareBacktestSnapshot(
        next,
        maxBacktestCandles,
        maxPeriodDays,
      );
      historyRerunCandidateRef.current = { id: item.id, fingerprint: preparedBacktest.fingerprint };
      return next;
    });
    setMessage(t("setupLoadedFromHistory"));
    navigateToSection("backtest", true);
  }

  function prepareHistoryRerun(item: HistoryItem) {
    if (isRunning || !isAccountContextCurrent(accountContext)) return;
    if (!canPrepareHistoryRerun(form, item)) return;
    const next = applyHistorySetup(form, item);
    if (next.strategyMode === "manual") return;
    const prepared = prepareBacktestSnapshot(next, maxBacktestCandles, maxPeriodDays);
    sharedStrategyAttributionRef.current = null;
    starterSampleOriginRef.current = null;
    historyRerunCandidateRef.current = { id: item.id, fingerprint: prepared.fingerprint };
    recordPaperHistoryImport(item.setup);
    setVariantBaseline(null);
    setForm(next);
    setError(null);
    setMessage(t("setupLoadedFromHistory"));
    trackHistoryEvent("rerun_prepared");
    navigateToSection("backtest", true);
  }

  function applyStarterSampleSetup() {
    if (isRunning || !isAccountContextCurrent(accountContext)) return;
    sharedStrategyAttributionRef.current = null;
    historyRerunCandidateRef.current = null;
    setVariantBaseline(null);
    const next = starterSampleForm();
    starterSampleOriginRef.current = { context: accountContext, fingerprint: prepareBacktestSnapshot(next, maxBacktestCandles, maxPeriodDays).fingerprint };
    setForm(next);
    setStarterSampleVisible(false);
    setError(null);
    setMessage(t("ready"));
    trackStarterSampleEvent("setup_applied");
    navigateToSection("backtest", true);
  }

  function openMarketCommunity(selection: FuturesMarketSelection) {
    if (!isAccountContextCurrent(accountContext)) return;
    marketSelectionRef.current = selection;
    setCommunityMarketSelection(selection);
    setError(null);
    setMessage("");
    navigateToSection("community");
  }

  function prepareMarketBacktest(selection: FuturesMarketSelection, empty = false) {
    if (isRunning || !isAccountContextCurrent(accountContext) || marketSelectionRef.current?.requestID !== selection.requestID) return;
    sharedStrategyAttributionRef.current = null;
    historyRerunCandidateRef.current = null;
    starterSampleOriginRef.current = null;
    setVariantBaseline(null);
    const next = futuresMarketBacktestForm(defaultForm, selection);
    marketSelectionRef.current = null;
    setCommunityMarketSelection(null);
    setForm(next);
    recordExplicitPaperSource(next);
    setError(null);
    setMessage(futuresMarketT(locale, empty ? "empty" : "prepared"));
    navigateToSection("backtest", empty);
  }

  function chooseBacktestMarket(market: FuturesMarket) {
    if (!isAccountContextCurrent(accountContext)) return;
    setForm((current) => ({ ...current, symbol: market.backtest_defaults.symbol, provider: market.backtest_defaults.provider, market: market.backtest_defaults.market,
      executionModel: "futures", timeframe: market.supported_timeframes.includes(current.timeframe) ? current.timeframe : "1h" }));
  }

  function prepareWorkspaceBacktest(stream: WorkspaceStream) {
    if (isRunning || !isAccountContextCurrent(accountContext)) return;
    const next = workspaceBacktestForm(defaultForm, stream);
    if (!next) { setError(workspaceT(locale, "backtestUnavailable")); return; }
    sharedStrategyAttributionRef.current = null;
    historyRerunCandidateRef.current = null;
    starterSampleOriginRef.current = null;
    setVariantBaseline(null);
    recordExplicitPaperSource(next);
    setForm(next);
    setError(null);
    setMessage(workspaceT(locale, "copyRules"));
    navigateToSection("backtest", true);
  }

  function prepareHistoryItemForPaper(item: HistoryItem) {
    if (item.setup.strategyDSL && !supportsPaperHistorySetup(item.setup)) {
      setError(paperExecutionT(locale, "historyUnsupported"));
      return;
    }
    const next = applyHistorySetup(defaultForm, item);
    recordPaperHistoryImport(item.setup);
    setVariantBaseline(null);
    setForm(next);
    setAreMonitorLabsOpen(true);
    setPreparedPaperSource({ id: `prepared-history:${item.id}`, label: `${paperExecutionT(locale, "history")} · ${next.strategyName}`, form: next });
    setMessage(t("setupLoadedFromHistory"));
    navigateToSection("bots", true);
  }

  function prepareCommunityForPaper(publication: CommunityPublication, reproduction: CommunityReproduction) {
    if (!isAccountContextCurrent(accountContext)) return;
    const next = applyCommunityReproduction(form, reproduction);
    recordExplicitPaperSource(next);
    setVariantBaseline(null);
    setForm(next);
    setAreMonitorLabsOpen(true);
    setPreparedPaperSource({ id: `publication:${publication.id}`, label: `${paperExecutionT(locale, "shared")} · ${publication.title}`,
      form: next, publicationID: publication.id });
    navigateToSection("bots", true);
  }

  async function refreshBots() {
    if (!token || !authTokenSource || isBotsRefreshing || isBotActionRunning) return;
    const requestContext = accountContext;
    setIsBotsRefreshing(true);
    setError(null);
    try {
      const response = await fetchRealtimeBots(authTokenSource, locale);
      if (!isAccountContextCurrent(requestContext)) return;
      setBots(response.bots || []);
    } catch {
      if (isAccountContextCurrent(requestContext)) setError(referenceT(locale, "refreshFailed"));
    } finally {
      if (isAccountContextCurrent(requestContext)) setIsBotsRefreshing(false);
    }
  }

  async function handleBotPrimaryAction(): Promise<void> {
    if (botCreationAction === "sign_in") {
      handleSignInFocus("bots");
      return;
    }
    if (botCreationAction === "refresh_usage") {
      await refreshAccountUsage();
      return;
    }
    if (botCreationAction === "view_plans") {
      navigateToSection("pricing", true);
      return;
    }
    if (botCreationAction === "wait") return;
    await handleCreateBot();
  }

  async function handleCreateBot() {
    if (form.strategyMode === "method") { setError(methodT(locale, "unavailable")); return; }
    if (form.strategyMode === "manual") {
      setError(simpleStrategyT(locale, "legacy"));
      navigateToSection("backtest");
      return;
    }
    if (!token || !authTokenSource) {
      setError(t("signInCreateBots"));
      handleSignInFocus("bots");
      return;
    }
    if (botStrategyNeedsReview) {
      setError(flowT("builderApplyFirst"));
      navigateToSection("backtest");
      return;
    }
    const requestContext = accountContext;
    setIsBotActionRunning(true);
    setError(null);
    try {
      const response = await createRealtimeBot(form, authTokenSource, locale, maxBacktestCandles, maxPeriodDays);
      if (!isAccountContextCurrent(requestContext)) return;
      setBots((current) => [response.bot, ...current.filter((bot) => bot.id !== response.bot.id)]);
      setMessage(t("realtimeBotCreated"));
    } catch (err) {
      if (!isAccountContextCurrent(requestContext)) return;
      setError(localizedAPIErrorMessage(err, t, "createBotFailed"));
    } finally {
      if (isAccountContextCurrent(requestContext)) setIsBotActionRunning(false);
    }
  }

  async function handleTickBot(bot: RealtimeBot) {
    if (!token || !authTokenSource) return;
    const requestContext = accountContext;
    setIsBotActionRunning(true);
    setError(null);
    try {
      const response = await tickRealtimeBot(bot.id, authTokenSource, locale);
      if (!isAccountContextCurrent(requestContext)) return;
      setBots((current) => current.map((item) => (item.id === bot.id ? response.bot : item)));
      const latestResult = response.bot.events?.[0]?.result;
      if (latestResult) {
        applyResult(latestResult, t("botTickComplete"));
      } else {
        setMessage(t("botTickComplete"));
      }
    } catch (err) {
      if (!isAccountContextCurrent(requestContext)) return;
      setError(localizedAPIErrorMessage(err, t, "tickBotFailed"));
    } finally {
      if (isAccountContextCurrent(requestContext)) setIsBotActionRunning(false);
    }
  }

  async function handleDeleteBot(bot: RealtimeBot) {
    if (!token || !authTokenSource) return;
    const requestContext = accountContext;
    setIsBotActionRunning(true);
    setError(null);
    try {
      await deleteRealtimeBot(bot.id, authTokenSource, locale);
      if (!isAccountContextCurrent(requestContext)) return;
      setBots((current) => current.filter((item) => item.id !== bot.id));
      setMessage(t("realtimeBotDeleted"));
    } catch (err) {
      if (!isAccountContextCurrent(requestContext)) return;
      setError(localizedAPIErrorMessage(err, t, "deleteBotFailed"));
    } finally {
      if (isAccountContextCurrent(requestContext)) setIsBotActionRunning(false);
    }
  }

  function openPrivacy() {
    window.history.pushState({}, "", privacyPath(locale));
    setIsPrivacyOpen(true);
    setActiveSection("settings");
  }

  function closePrivacy() {
    window.history.pushState({}, "", pathForSection(activeSection, locale));
    setIsPrivacyOpen(false);
  }

  function navigateToSection(section: NavSection, replace = false) {
    if (section !== "community") setCommunityHistoryRequested(false);
    setIsPrivacyOpen(false);
    setActiveSection(section);
    if (section === "results") {
      setResultRevealRequest((current) => current + 1);
    }
    const nextPath = pathForSection(section, locale);
    if (window.location.pathname !== nextPath) {
      if (replace) {
        window.history.replaceState({}, "", nextPath);
      } else {
        window.history.pushState({}, "", nextPath);
      }
    }
    window.dispatchEvent(new Event(APP_NAVIGATION_EVENT));
    updateLocalizedSeo(locale, pricingMode);
    const focusPageContent = window.matchMedia("(max-width: 680px)").matches;
    window.setTimeout(() => {
      if (section === "results") return;
      if (focusPageContent) {
        routeContentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 0);
  }

  function clearGuidePresetBanner() {
    setGuidePresetLoaded(false);
  }

  function updateLocale(nextLocale: Locale) {
    if (nextLocale === locale) return;
    setLocale(nextLocale);
    window.history.replaceState(window.history.state, "", languageNavigationPath(window.location, nextLocale));
  }

  function handleSignInFocus(returnTo?: NavSection) {
    deferredSectionAfterSignInRef.current = returnTo ?? null;
    setError(null);
    setMessage("");
    setAuthMode("signin");
    navigateToSection("account");
    window.setTimeout(() => {
      authEmailRef.current?.focus();
    }, 50);
  }

  function handleNavClick(section: NavSection) {
    if (section !== "backtest" && [futuresMarketT(locale, "empty"), futuresMarketT(locale, "prepared")].includes(message)) setMessage("");
    if (section === "community" && activeSection !== "community") {
      trackFunnelEvent("strategy_community_tab_opened", {
        entry_point: "primary_navigation",
        signed_in: Boolean(user),
      });
    }
    navigateToSection(section);
  }

  const communityNavigationAvailable = strategyCommunityCapability.enabled || communityCapabilityState !== "ready";

  function acceptRiskNotice() {
    localStorage.setItem(RISK_NOTICE_ACCEPTED_KEY, "true");
    setRiskNoticeRequiresConfirmation(false);
    setIsRiskNoticeOpen(false);
  }

  function openRiskNotice() {
    setIsPrivacyOpen(false);
    setRiskNoticeRequiresConfirmation(false);
    setIsRiskNoticeOpen(true);
  }

  async function handleEmailAuth(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setMessage("");
    setIsAuthSubmitting(true);
    try {
      if (authMode === "signup") {
        const guestResult = user ? null : result;
        const signUp = await signUpWithEmail(authEmail, authPassword, authName);
        if (signUp.isNewUser) {
          const importedItems = copyGuestHistoryToNewAccount(
            window.localStorage,
            signUp.uid,
            SIGNED_IN_LOCAL_HISTORY_FALLBACK_LIMIT,
          );
          if (importedItems.length || guestResult) {
            setPendingSignUpImport({
              uid: signUp.uid,
              items: importedItems,
              result: guestResult,
            });
          }
        }
      } else {
        await loginWithEmail(authEmail, authPassword);
      }
      setAuthPassword("");
      setMessage(authMode === "signup" ? t("accountCreated") : t("signedIn"));
    } catch (err) {
      setError(accountAuthErrorMessage(err, t));
    } finally {
      setIsAuthSubmitting(false);
    }
  }

  return (
    <main className={`app-shell global-language-page experience-${experienceMode}${activeSection === "home" ? "" : " app-workspace"}`} data-ui-experience={experienceMode}>
      {!isPrivacyOpen && !isRiskNoticeOpen && <LanguageSwitcher locale={locale} onChange={updateLocale} />}
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">
            <img src="/backtest-icon.png" alt="" />
          </div>
          <div>
            <strong>Backtest</strong>
            <span>{t("appSubtitle")}</span>
          </div>
        </div>
        <nav>
          {navItems.filter((item) => PRIMARY_SECTIONS.some((section) => section === item.id)).map((item) => {
            const Icon = item.icon;
            return (
              <React.Fragment key={item.id}>
                <button
                  className={primarySectionFor(activeSection === "ftmo" ? "account" : activeSection) === item.id ? "active" : ""}
                  aria-current={primarySectionFor(activeSection === "ftmo" ? "account" : activeSection) === item.id ? "page" : undefined}
                  onClick={() => handleNavClick(item.id)}
                  type="button"
                >
                  <Icon size={18} /> {item.id === "watchlist" ? futuresMarketT(locale, "favorites") : item.id === "community" ? communityT(locale, "navigationTitle") : t(item.labelKey)}
                </button>
              </React.Fragment>
            );
          })}
        </nav>
        <details className="sidebar-help-disclosure" open={activeSection === "home" ? true : undefined}>
          <summary><BookOpen size={16} /> {t("learn")} <ChevronDown size={16} /></summary>
          <div className="sidebar-help">
            <button type="button" onClick={() => handleNavClick("home")}><Home size={16} /> {t("home")}</button>
            <a href={learnPath}><BookOpen size={16} /> {t("learn")}</a>
            <a href={methodologyPath(locale)}><ShieldCheck size={16} /> {t("methodology")}</a>
          </div>
        </details>
      </aside>

      <section className="content">
        <header className="topbar">
          <div className="hero-copy">
            {activeSection === "home" ? <h1>{t("heroTitle")}</h1> : <h1>{activeSection === "ftmo" ? simulationT(locale, "title") : activeSection === "watchlist" ? futuresMarketT(locale, "favorites") : activeSection === "research" ? workspaceT(locale, activeSection) : activeSection === "history" ? productFocusT(locale, "library") : activeSection === "bots" ? productFocusT(locale, "monitor") : activeSection === "community" ? communityT(locale, "navigationTitle") : t(activeSection)}</h1>}
            {activeSection === "home" && <div className="hero-meta" aria-label={t("heroProofTitle")}>
              <span>{t("backtests")}</span>
              <span>{t("cloudHistory")}</span>
              <span>{t("noRealTrades")}</span>
            </div>}
          </div>
          <div className="hero-actions">
            {activeSection === "home" && <button className="primary" type="button" onClick={() => handleNavClick("backtest")}><Play size={18} /> {t("runFreeBacktest")}</button>}
            <AccountButton user={user} isLoading={isLoadingAccount || isAuthSubmitting} t={t} onSignIn={handleSignInFocus} />
          </div>
        </header>

        {activeSection === "home" && (
          <>
            <HeroConversionPanel t={t} locale={locale} learnPath={learnPath} form={form} onRun={() => handleNavClick("backtest")} />
            <section className="trust-strip" aria-label={t("riskNoticeTitle")}>
              <a href={methodologyPath(locale)}><ShieldCheck size={17} /> {t("methodology")}</a>
              <button type="button" onClick={openPrivacy}><Lock size={17} /> {t("privacy")}</button>
              <button type="button" onClick={openRiskNotice}><ShieldAlert size={17} /> {t("riskNoticeTitle")}</button>
            </section>
          </>
        )}

        {(error || (message && message !== t("ready"))) && (
          <div className={`status ${error ? "error" : ""}`}>
            {error ? <ShieldAlert size={18} /> : <RefreshCw size={18} />}
            <span>{error || message || t("ready")}</span>
          </div>
        )}

        {!isOnline && (
          <div className="local-offline-notice" role="status">
            <Database size={18} aria-hidden="true" />
            <span>{localSupportT(locale, "offline")}</span>
          </div>
        )}

        {(primarySectionFor(activeSection) === "account" || activeSection === "ftmo") && (
          <nav className="section-tabs" aria-label={t("account")}>
            {(["account", "ftmo", "pricing", "settings"] as const).map((section) => (
              <button key={section} type="button" aria-current={activeSection === section ? "page" : undefined}
                className={activeSection === section ? "active" : ""} onClick={() => handleNavClick(section)}>{section === "ftmo" ? "FTMO" : t(section)}</button>
            ))}
          </nav>
        )}
        {primarySectionFor(activeSection) === "backtest" && (
          <nav className="section-tabs" aria-label={workspaceT(locale, "research")}>
            {(["backtest", "results", "history", "bots"] as const).map((section) => (
              <button key={section} type="button" aria-current={activeSection === section ? "page" : undefined}
                className={activeSection === section ? "active" : ""} onClick={() => handleNavClick(section)}>{section === "history" ? productFocusT(locale, "library") : section === "bots" ? productFocusT(locale, "monitor") : t(section)}</button>
            ))}
          </nav>
        )}

        {activeSection === "home" && (
          <section className="screenshot-strip" aria-label={t("heroProofTitle")}>
            <a href="/screenshots/">
              <img src="/screenshots/backtest-trading-strategy-setup.png" alt={t("resultEmptyStep1")} loading="lazy" width={1280} height={900} />
              <span>{t("setup")}</span>
            </a>
            <a href="/screenshots/">
              <img src="/screenshots/btc-backtesting-result-chart.png" alt={t("resultEmptyStep3")} loading="lazy" />
              <span>{t("results")}</span>
            </a>
            <a href="/screenshots/">
              <img src="/screenshots/paper-signal-bot-trading-app.png" alt={`${t("paperAlerts")} · ${t("bots")}`} loading="lazy" />
              <span>{t("bots")}</span>
            </a>
          </section>
        )}

        {activeSection !== "home" && (
        <section ref={routeContentRef} className={`workspace-grid route-grid route-${activeSection}`}>
          {activeSection === "ftmo" && <React.Suspense fallback={<p role="status">{ftmoT(locale, "loading")}</p>}>
            <FTMOSimulatorLanding key={`sim:${accountContext.generation}`} locale={locale} onStart={(selection, capital) => {
              setForm(current => ({ ...current, ftmoSimulation: selection, initialCapital: capital }));
              navigateToSection("backtest", true);
            }}><FTMOWorkspace key={`ftmo:${accountContext.accountID ?? "guest"}:${accountContext.generation}`}
              locale={locale} accountID={accountContext.accountID} authToken={authTokenSource}
              isCurrent={() => isAccountContextCurrent(accountContext)} onSignIn={() => handleSignInFocus("ftmo")} /></FTMOSimulatorLanding>
          </React.Suspense>}
          {activeSection === "watchlist" && <React.Suspense fallback={<p role="status">{workspaceT(locale, "loading")}</p>}>
            <FuturesMarketPanel key={`futures-markets:${accountContext.accountID ?? "guest"}:${accountContext.generation}`}
              locale={locale} accountID={accountContext.accountID}
              isCurrent={() => isAccountContextCurrent(accountContext)} onSelect={openMarketCommunity}/>
          </React.Suspense>}
          {activeSection === "research" && <section className="panel tw-research-intro">
            <h2>{workspaceT(locale, "research")}</h2><p>{workspaceT(locale, "researchHint")}</p>
            <div className="button-row"><button className="primary" type="button" onClick={() => navigateToSection("backtest")}><Play size={17}/>{t("backtest")}</button>
              <button className="secondary" type="button" onClick={() => navigateToSection("history")}><Clock size={17}/>{productFocusT(locale, "library")}</button>
              {communityNavigationAvailable && <button className="secondary" type="button" onClick={() => handleNavClick("community")}><Users size={17}/>{communityT(locale, "navigationTitle")}</button>}</div>
            {recentRuns.length > 0 && <section className="recent-runs" aria-label={productFocusT(locale, "continue")}>{recentRuns.map((item) => <button className="recent-run" type="button" key={item.id} onClick={() => openHistoryItem(item)}><span>{item.setup.symbol} · {item.setup.timeframe} · {storedMethodName(locale, item.setup.strategyMethod, strategyLabel(item.setup.strategyName || "", t, locale))}</span><small>{formatLocalizedDateTime(item.createdAt, locale)}</small><ArrowRight size={16}/></button>)}</section>}
          </section>}
          {activeSection === "backtest" && (
          <section className="panel setup-panel">
            <FTMOSimulationSetup locale={locale} selection={form.ftmoSimulation} capital={form.initialCapital} disabled={isRunning}
              onChange={(selection, capital) => setForm(current => ({ ...current, ftmoSimulation: selection, initialCapital: capital }))} />
            {form.ftmoSimulation && form.strategyMode !== "method" && <p role="status">{simulationT(locale, "methodRequired")}</p>}


            {guidePresetLoaded && (
              <div className="guide-preset-banner">
                <div>
                  <Sparkles size={18} />
                  <span>{t("loadedPresetFromGuide")}: {symbolLabel(form.symbol)} · {timeframeLabel(form.timeframe)} · {strategyLabel(form.strategyName, t, locale)}</span>
                </div>
                <button type="button" onClick={clearGuidePresetBanner}>{t("privacyClose")}</button>
              </div>
            )}

            {starterSampleVisible && !user && <StarterSampleCard locale={locale} onUseSetup={applyStarterSampleSetup} onDismiss={() => { trackStarterSampleEvent("dismissed"); setStarterSampleVisible(false); }} />}

            {!variantBaseline && recentRuns.length > 0 && (
              <details className="recent-results-disclosure">
                <summary>{productFocusT(locale, "continue")} <ChevronDown size={16} /></summary>
                <section className="recent-runs" aria-label={productFocusT(locale, "continue")}>
                {recentRuns.map((item) => <button className="recent-run" type="button" key={item.id} onClick={() => openHistoryItem(item)}>
                  <span>{item.setup.symbol} · {item.setup.timeframe} · {storedMethodName(locale, item.setup.strategyMethod, strategyLabel(item.setup.strategyName || "", t, locale))}</span>
                  <small>{formatLocalizedDateTime(item.createdAt, locale)}</small><ArrowRight size={16} />
                </button>)}
                </section>
              </details>
            )}
            {variantBaseline && <aside className="variant-guidance" role="status">
              <strong>{productFocusT(locale, "variant")}</strong><p>{productFocusT(locale, "variantHint")}</p>
              <button className="secondary" type="button" onClick={() => openHistoryItem(variantBaseline)}>{t("results")}</button>
              <button className="secondary" type="button" onClick={() => setVariantBaseline(null)}>{t("privacyClose")}</button>
            </aside>}

            <section className="setup-group-market" aria-label={t("market")}>
            <h2>{t("market")}</h2>
            <div className="form-grid">
              <MarketDiscoverySelect
                label={t("symbol")}
                locale={locale}
                scope={user?.uid ? `account:${user.uid}` : "guest"}
                value={form.symbol}
                futuresOnly provider={form.provider} onSelectMarket={chooseBacktestMarket}
                onChange={(value) => updateForm("symbol", value)}
              />
              <Segmented
                label={t("timeframe")}
                value={form.timeframe}
                options={form.provider === "yfinance" && form.market === "forex" ? timeframes.filter((frame) => frame !== "4h") : timeframes}
                labels={timeframeLabels}
                onChange={(value) => updateForm("timeframe", value)}
              />
              <Segmented label={t("period")} value={form.period} options={availablePeriods} onChange={(value) => updateForm("period", value)} />
            </div>

            <div className="range-note">
              <span>{t("maxRange")}: {formatLocalizedNumber(maxRangeDays, locale)} {t("quotaDays")}</span>
              <span>{formatLocalizedNumber(rangeSummary.days, locale)} {t("quotaDays")} · ~{formatLocalizedNumber(rangeSummary.candles, locale)} / {formatLocalizedNumber(maxBacktestCandles, locale)} {t("bars")}</span>
            </div>

            {isRunning && (
              <RunProgressCard
                title={t("loadingBacktestTitle")}
                eta={backtestEta}
                steps={[t("loadingBacktestData"), t("loadingBacktestEngine"), t("loadingBacktestReport")]}
              />
            )}

            {form.period === "Custom" && (
              <div ref={dateRecoveryRef} className="date-recovery" tabIndex={-1}>
                <div className="form-grid compact-grid">
                  <DateInput label={t("startDate")} value={form.startDate} onChange={(value) => updateForm("startDate", value)} />
                  <DateInput label={t("endDate")} value={form.endDate} onChange={(value) => updateForm("endDate", value)} />
                </div>
                {customDateRecovery && (
                  <div className="flow-recovery-notice" role="alert">
                    <ShieldAlert size={17} aria-hidden="true" />
                    <span>
                      {customDateRecovery.kind === "invalid_order"
                        ? flowT("invalidDateOrder")
                        : flowT("dateAdjusted", {
                            previous: customDateRecovery.requestedStartDate,
                            current: customDateRecovery.effectiveStartDate,
                          })}
                    </span>
                    {customDateRecovery.kind === "use_allowed_start" && (
                      <button
                        type="button"
                        onClick={() => updateForm("startDate", customDateRecovery.effectiveStartDate)}
                      >
                        {flowT("useAllowedDates")}
                      </button>
                    )}
                    {customDateRecovery.kind === "use_allowed_start" && customDateRecovery.planLimited && (
                      <button type="button" onClick={() => navigateToSection("pricing", true)}>
                        {t("pricing")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            </section>

            <section className="setup-group-strategy" aria-label={t("strategy")}>
            <h2>{t("strategy")}</h2>
            {!form.ftmoSimulation && <StrategyModePicker
              locale={locale}
              value={form.strategyMode}
              t={t}
              onChange={(value) => updateForm("strategyMode", value)}
            />}

            <TradingMethodSelector locale={locale} selected={form.strategyMode === "method"} spec={form.strategyMethod}
              online={isOnline} readOnlyPolicy={form.unsupportedMethodExecution} onAvailability={setMethodsAvailable} onSelect={(spec) => {
                setVariantBaseline(null); setBuilderLocalDraft(undefined);
                setForm((current) => selectTradingMethod(current, spec)); setError(null);
              }} />

            {!form.ftmoSimulation && <>
            {form.strategyMode === "auto" && Boolean(strategyLibrary?.items.length) && (
              <details className="inline-help"><summary>{productFocusT(locale, "library")}</summary><div className="strategy-library">
                {strategyLibrary!.items.map((recipe) => (
                  <button key={recipe.id} type="button" className="strategy-library-recipe"
                    onClick={() => {
                      setBuilderLocalDraft(undefined);
                      recordExplicitPaperSource(selectLibraryRecipe(form, recipe, locale));
                      setVariantBaseline(null);
                      setForm((current) => selectLibraryRecipe(current, recipe, locale));
                    }}>
                    <strong>{recipeText(recipe.title, locale)}</strong>
                    <span>{recipeText(recipe.summary, locale)}</span>
                  </button>
                ))}
              </div></details>
            )}
            {form.strategyMode === "auto" ? (
              <label className="field preset-select"><span>{simpleStrategyT(locale, "preset")}</span>
                <select value={form.strategyName} onChange={(event) => {
                  if (!isStrategyPresetName(event.target.value)) return;
                  const name = event.target.value;
                  setVariantBaseline(null); setForm((current) => selectStrategyPreset(current, name));
                }}>{strategyPresets.map((preset) => <option key={preset.name} value={preset.name}>{strategyLabel(preset.name, t, locale)}</option>)}</select>
              </label>
            ) : null}

            {form.strategyMode === "manual" && <p className="legacy-strategy-notice" role="status">{simpleStrategyT(locale, "legacy")}</p>}

            <div ref={builderRecoveryRef} className="builder-recovery-target" tabIndex={-1}>
              <React.Suspense fallback={null}>
                <StrategyBuilderPanel
                  key={`${API_BASE_URL}:${accountContext.accountID || "guest"}:${accountContext.generation}`}
                  locale={locale}
                  experienceMode={experienceMode}
                  selected={form.strategyMode === "builder"}
                  activeDSL={form.strategyDSL}
                  initialDraft={builderLocalDraft}
                  libraryRecipes={strategyLibrary?.items}
                  onSelectLibraryRecipe={(recipe) => {
                    setBuilderLocalDraft(undefined);
                    recordExplicitPaperSource(selectLibraryRecipe(form, recipe, locale));
                    setVariantBaseline(null);
                    setForm((current) => selectLibraryRecipe(current, recipe, locale));
                  }}
                  onDraftChange={setBuilderLocalDraft}
                  onReadinessChange={setIsBuilderReady}
                  validationRef={builderValidationRef}
                  onValidationStateChange={setBuilderValidationState}
                  onApply={(strategyDSL: StrategyRuleDSL) => {
                    setBuilderLocalDraft(undefined);
                    recordExplicitPaperSource({ ...form, strategyMode: "builder", strategyMethod: undefined, unsupportedMethodExecution: undefined, strategyDSL });
                    setForm((current) => ({
                      ...current,
                      strategyMode: "builder",
                      strategyMethod: undefined, unsupportedMethodExecution: undefined,
                      strategyName: current.strategyMode === "builder" ? current.strategyName : "Visual Strategy Builder",
                      strategyDSL,
                      useAI: false,
                    }));
                    setError(null);
                  }}
                  onFallback={() => {
                    setVariantBaseline(null);
                    setIsBuilderReady(false);
                    setForm((current) => ({
                      ...current,
                      strategyMode: "auto",
                      strategyMethod: undefined, unsupportedMethodExecution: undefined,
                      strategyName: isStrategyPresetName(current.strategyName) ? current.strategyName : DEFAULT_STRATEGY_PRESET,
                      strategyDSL: undefined,
                      useAI: false,
                    }));
                  }}
                />
              </React.Suspense>
            </div>

            {form.strategyMode === "builder" && form.strategyDSL && (
              <StrategyRuleSummary locale={locale} dsl={form.strategyDSL} />
            )}
            </>}

            </section>

            <details className="setup-risk-details">
              <summary className="setup-risk-summary">
                <span><strong>{simpleStrategyT(locale, "risk")}</strong>
                  <small>{currency(form.initialCapital, locale)} · {form.unsupportedMethodExecution ? methodReadOnlyT(locale) : form.strategyMode === "method" ? form.strategyMethod ? methodRisk(locale, form.strategyMethod.method_id) : methodT(locale, "risk") : form.exitMode === "signal_only"
                    ? exitModeLabels.signal_only
                    : `SL ${formatLocalizedPercent(form.stopLossPct, locale)} · TP ${formatLocalizedPercent(form.takeProfitPct, locale)}`}</small>
                </span>
                <ChevronDown size={18} />
              </summary>
              <div className="risk-controls">
            <div className="execution-model-picker">
              <Segmented
                label={t("executionModel")}
                value={form.executionModel}
                options={availableExecutionModels}
                labels={executionModelLabels}
                onChange={(value) => updateForm("executionModel", value)}
              />
              <p>{form.executionModel === "futures" ? t("futuresPositionHint") : t("spotPositionHint")}</p>
            </div>

            <div className="risk-grid">
              {!form.ftmoSimulation && <NumberInput label={t("capital")} prefix="$" value={form.initialCapital} step={1000} min={100} max={1_000_000_000} onChange={(value) => updateForm("initialCapital", value)} />}
              <NumberInput
                label={form.executionModel === "futures" ? t("marginAllocation") : t("capitalAllocation")}
                suffix="%"
                value={form.positionSizePct}
                step={1}
                min={0.1}
                max={100}
                onChange={(value) => updateForm("positionSizePct", value)}
              />
              {form.strategyMode !== "method" && <><NumberInput label={t("stopLossPrice")} suffix="%" value={form.stopLossPct} step={0.5} min={0.1} max={50} onChange={(value) => updateForm("stopLossPct", value)} />
              <NumberInput label={t("takeProfitPrice")} suffix="%" value={form.takeProfitPct} step={0.5} min={0.1} max={100} onChange={(value) => updateForm("takeProfitPct", value)} /></>}
              <NumberInput label={t("commission")} suffix="%" value={form.commissionRate} step={0.05} min={0} max={5} onChange={(value) => updateForm("commissionRate", value)} />
              <NumberInput label={t("slippage")} suffix="bps" value={form.slippageBps} step={1} min={0} max={500} onChange={(value) => updateForm("slippageBps", value)} />
            </div>

            {form.strategyMode === "method" && <p>{form.unsupportedMethodExecution ? methodReadOnlyT(locale) : form.strategyMethod ? methodRisk(locale, form.strategyMethod.method_id) : methodT(locale, "risk")}</p>}
            <div className="risk-preview" aria-label={t("positionPreview")} aria-live="polite">
              <span>{t(form.executionModel === "futures" ? "marginUsed" : "capitalUsed")}: {currency(allocatedCapital, locale)}</span>
              <span>{t("positionValue")}: {currency(estimatedNotional, locale)}</span>
              {form.strategyMode !== "method" && form.exitMode !== "signal_only" && <span>{t("estimatedLossAtStop")}: {currency(estimatedStopLoss, locale)} ({formatLocalizedPercent(estimatedAccountLossPercent, locale, 2)})</span>}
            </div>


            <div className="execution-settings-disclosure">
              <button
                className="execution-settings-toggle"
                type="button"
                aria-expanded={isExecutionSettingsOpen}
                aria-controls="execution-settings-content"
                onClick={() => setIsExecutionSettingsOpen((current) => !current)}
              >
                <span><Settings size={17} /> {experienceT("executionSettings")}</span>
                <ChevronDown className={isExecutionSettingsOpen ? "expanded" : ""} size={18} />
              </button>
              {isExecutionSettingsOpen && (
                <div className="advanced-settings" id="execution-settings-content">
                  {form.executionModel === "futures" && (
                    <>
                      <NumberInput
                        label={t("leverage")}
                        suffix="x"
                        value={form.leverage}
                        step={1}
                        min={1}
                        max={125}
                        onChange={(value) => setForm((current) => {
                          const nextLeverage = Math.min(125, Math.max(1, value));
                          const nextMaintenanceMaximum = Math.min(20, Math.max(0.01, 100 / nextLeverage - 0.01));
                          return {
                            ...current,
                            leverage: nextLeverage,
                            maintenanceMarginPercent: Math.min(current.maintenanceMarginPercent, nextMaintenanceMaximum),
                          };
                        })}
                      />
                      <Segmented
                        label={t("marginMode")}
                        value={form.marginMode}
                        options={marginModes}
                        labels={marginModeLabels}
                        describedBy={marginModeHelpID}
                        onChange={(value) => updateForm("marginMode", value)}
                      />
                      <p id={marginModeHelpID} className="margin-mode-help" aria-live="polite">
                        {t(form.marginMode === "isolated" ? "isolatedMarginHelp" : "crossMarginHelp")}
                      </p>
                      <NumberInput label={t("maintenanceMargin")} suffix="%" value={form.maintenanceMarginPercent} step={0.1} min={0} max={maximumMaintenanceMarginPercent} onChange={(value) => updateForm("maintenanceMarginPercent", value)} />
                      <NumberInput label={t("fundingRate")} suffix="%" value={form.fundingRatePercent} step={0.01} min={-5} max={5} onChange={(value) => updateForm("fundingRatePercent", value)} />
                      <NumberInput label={t("fundingInterval")} suffix="h" value={form.fundingIntervalHours} step={1} min={1} max={168} onChange={(value) => updateForm("fundingIntervalHours", Math.round(value))} />
                      <NumberInput label={t("liquidationFee")} suffix="%" value={form.liquidationFeePercent} step={0.1} min={0} max={5} onChange={(value) => updateForm("liquidationFeePercent", value)} />
                    </>
                  )}
                  <Segmented
                    label={t("direction")}
                    value={form.tradeDirection}
                    options={tradeDirections}
                    labels={tradeDirectionLabels}
                    onChange={(value) => updateForm("tradeDirection", value)}
                  />
                  {form.strategyMode !== "method" && <Segmented
                    label={t("exitMode")}
                    value={form.exitMode}
                    options={exitModes}
                    labels={exitModeLabels}
                    onChange={(value) => updateForm("exitMode", value)}
                  />}
                </div>
              )}
            </div>

              </div>
            </details>

            <div className="execution-assumptions" aria-live="polite">
              <span>{t("executionModel")}: {executionModelLabels[form.executionModel]}</span>
              {form.executionModel === "futures" && <><span>{t("leverage")}: {formatLocalizedNumber(form.leverage, locale)}×</span><span>{marginModeLabels[form.marginMode]}</span></>}
              <span>{t("commission")}: {formatLocalizedPercent(form.commissionRate, locale)}</span>
              <span>{t("slippage")}: {formatLocalizedNumber(form.slippageBps, locale)} bps</span>
            </div>

            <div className="local-draft-notice" data-testid="local-draft-status">
              <div>
                <span aria-live="polite">
                  {localSupportT(locale, draftStatus.state)}
                  {draftStatus.savedAt ? ` · ${formatLocalizedDateTime(new Date(draftStatus.savedAt).toISOString(), locale)}` : ""}
                </span>
              </div>
              {draftStatus.savedAt !== undefined && (
                <button type="button" className="secondary" onClick={clearSavedDraft}>{localSupportT(locale, "clear")}</button>
              )}
            </div>

            <div className="run-backtest-action">
              <button
                className="primary"
                type="button"
                onClick={() => void performBacktestPrimaryAction()}
                disabled={isRunning || isUsageRefreshing || form.strategyMode === "manual"
                  || (form.strategyMode === "method" && (!methodsAvailable || Boolean(form.unsupportedMethodExecution)))
                  || (form.strategyMode === "builder" && !isBuilderReady && builderValidationState !== "ready")}
              >
                {isRunning || isUsageRefreshing || (form.strategyMode === "builder" && builderValidationState === "validating")
                  ? <RefreshCw className="is-spinning" size={18} />
                  : <Play size={18} />}
                {isRunning ? t("running") : backtestPrimaryLabel}
              </button>
            </div>
          </section>
          )}

          {activeSection === "results" && (
          <section className="panel result-panel">
            <div className="panel-title">
              <div>
                <p className="eyebrow">{t("results")}</p>
                <h2 ref={resultHeadingRef} className="result-heading-anchor" tabIndex={-1}>
                  {result ? `${symbolLabel(result.symbol)} · ${timeframeLabel(result.timeframe)}` : t("noBacktest")}
                </h2>
              </div>
              <div className="result-title-actions">
                {result && (
                  <button className="secondary" type="button" disabled={isShareLinkCreating} onClick={handleShareResult}>
                    {isShareLinkCreating ? <RefreshCw className="is-spinning" size={16} /> : <Share2 size={16} />} {isShareLinkCreating ? t("running") : t("shareResult")}
                  </button>
                )}
                {result?.engine && <span className="badge">{result.engine}</span>}
              </div>
            </div>
            {result?.ftmo_simulation && <FTMOSimulationResult report={result.ftmo_simulation} locale={locale} />}
            {shareFeedback && <p className={shareFeedback.failed ? "inline-error" : "muted"} role={shareFeedback.failed ? "alert" : "status"}>{shareFeedback.message}</p>}
            {result ? (
              <>
                <div className="result-next-step">
                  <div>
                    {resultHistoryItem && applyHistorySetup(defaultForm, resultHistoryItem).strategyMode !== "manual" && <button className="primary" type="button" onClick={() => duplicateHistoryItem(resultHistoryItem)}>{productFocusT(locale, "variant")}</button>}
                    <button className="secondary" type="button" onClick={() => navigateToSection("history")}>{productFocusT(locale, "compare")}</button>
                  </div>
                </div>
                {completedVariantComparison && result.run_id === completedVariantComparison.candidate.result?.run_id &&
                  <SavedRunComparison baseline={completedVariantComparison.baseline} candidate={completedVariantComparison.candidate} locale={locale} />}
                <ProfitLossHero result={result} t={t} locale={locale} />
                {strategyCommunityCapability.generatedPublishing && supportsReproducibleSharing
                  && canPublishProfitableCommunityResult(result, history) && (
                  <aside className="community-result-share" aria-busy={isCommunityPublishing}>
                    <div className="button-row">
                      <button className="primary" type="button" disabled={isCommunityPublishing}
                        onClick={() => void shareProfitableMethodToCommunity()}>
                        {isCommunityPublishing ? <RefreshCw className="is-spinning" size={17} /> : <Share2 size={17} />}
                        {communityT(locale, "shareShort")}
                      </button>
                      <button className="secondary" type="button" disabled={isCommunityPublishing} onClick={openCommunitySharingHistory}>{communityT(locale, "myPublications")}</button>
                    </div>
                    <p>{communityT(locale, "shareHint")}</p>
                    <details><summary>{communityT(locale, "shareDetails")}</summary><p>{communityT(locale, "generatedIntro")}</p><p>{communityT(locale, "generatedStandardized")}</p></details>
                    {isCommunityPublishing && <p role="status">{communityT(locale, "checking")}</p>}
                    {communityPublishFeedback && <p className={communityPublishFeedback.failed ? "inline-error" : "muted"} role={communityPublishFeedback.failed ? "alert" : "status"}>{communityPublishFeedback.message}</p>}
                  </aside>
                )}
                <MetricGrid result={result} t={t} locale={locale} />
                {!result.method_evidence && <ConfidenceCard result={result} t={t} locale={locale} />}
                {experienceMode === "guided" && (
                  <details className="inline-help"><summary>{t("learn")}</summary><GuidedLearningCard
                    title={experienceT("resultGuideTitle")}
                    items={[
                      experienceT("resultGuideSample"),
                      experienceT("resultGuideRisk"),
                      experienceT("resultGuideValidation"),
                    ]}
                    tone="orange"
                  /></details>
                )}
                <ResearchEvidence result={result} t={t} locale={locale} />
                <TradingMethodEvidencePanel evidence={result.method_evidence} locale={locale} />
                <PriceChart methodEvidence={result.method_evidence} candles={result.candles} trades={newestTrades} selectedTrade={selectedTrade} onSelectTrade={setSelectedTrade} t={t} locale={locale} />
                <TradeDetail trade={selectedTrade} t={t} locale={locale} />
                <TradeTable trades={newestTrades} selectedTrade={selectedTrade} onSelectTrade={setSelectedTrade} t={t} locale={locale} />
              </>
            ) : (
              <div className="empty-state chart-empty results-empty">
                <div className="empty-icon">
                  <LineChart size={38} />
                </div>
                <div>
                  <h3>{t("emptyResultsTitle")}</h3>
                  <p>{t("emptyResults")}</p>
                </div>
                {experienceMode === "guided" && (
                  <ol className="empty-steps">
                    <li>{t("resultEmptyStep1")}</li>
                    <li>{t("resultEmptyStep2")}</li>
                    <li>{t("resultEmptyStep3")}</li>
                  </ol>
                )}
                <div className="empty-actions">
                  <button className="primary" onClick={() => navigateToSection("backtest", true)}>
                    <Play size={17} />
                    {t("runBacktest")}
                  </button>
                  {history.length > 0 && (
                    <button className="secondary" onClick={() => navigateToSection("history", true)}>
                      <Clock size={17} />
                      {t("viewHistory")}
                    </button>
                  )}
                </div>
                {experienceMode === "guided" && (
                  <div className="empty-preview" aria-hidden="true">
                    <span>{t("netProfit")}</span>
                    <i />
                    <span>{t("winRate")}</span>
                    <i />
                    <span>{t("drawdown")}</span>
                    <i />
                  </div>
                )}
              </div>
            )}
          </section>
          )}

          {activeSection === "account" && (
          <section className="panel account-panel" ref={accountPanelRef}>
            <div className="panel-title compact">
              <div>
                <p className="eyebrow">{t("account")}</p>
                <h2>{user ? user.email || t("signedIn") : t("guestMode")}</h2>
              </div>
              <Bot size={22} />
            </div>
            {!firebaseEnabled && (
              <p className="muted">{t("accountSignInUnavailable")}. {t("accountSignInUnavailableHint")}</p>
            )}
            {firebaseEnabled && !user && (
              <form className="auth-form" onSubmit={handleEmailAuth}>
                <div className="auth-tabs" role="tablist" aria-label={`${t("signIn")} / ${t("signUp")}`}>
                  <button type="button" className={authMode === "signin" ? "active" : ""} onClick={() => setAuthMode("signin")}>
                    {t("signIn")}
                  </button>
                  <button type="button" className={authMode === "signup" ? "active" : ""} onClick={() => setAuthMode("signup")}>
                    {t("signUp")}
                  </button>
                </div>
                {authMode === "signup" && (
                  <label className="field">
                    <span>{t("name")}</span>
                    <input value={authName} autoComplete="name" onChange={(event) => setAuthName(event.target.value)} />
                  </label>
                )}
                <label className="field">
                  <span>{t("email")}</span>
                  <input
                    ref={authEmailRef}
                    type="email"
                    value={authEmail}
                    autoComplete="email"
                    required
                    onChange={(event) => setAuthEmail(event.target.value)}
                  />
                </label>
                <label className="field">
                  <span>{t("password")}</span>
                  <input
                    type="password"
                    value={authPassword}
                    autoComplete={authMode === "signup" ? "new-password" : "current-password"}
                    minLength={8}
                    required
                    onChange={(event) => setAuthPassword(event.target.value)}
                  />
                </label>
                <button className="primary full-width" type="submit" disabled={isAuthSubmitting}>
                  {isAuthSubmitting ? t("running") : authMode === "signup" ? t("createAccount") : t("signIn")}
                </button>
              </form>
            )}
            {user ? token && usage ? (
              <>
                <PlanValueCard usage={usage} t={t} locale={locale} />
                <div className="quota-list">
                  <Quota label={t("backtests")} used={usage.usage.normal_backtests} total={usage.limits.normal_backtests} locale={locale} />
                  <Quota label={t("quotaHistory")} used={history.length} total={localHistoryLimit(usage)} locale={locale} />
                  <Quota label={t("bots")} used={bots.length} total={usage.limits.max_realtime_bots} locale={locale} />
                </div>

              </>
            ) : (
              <p className="muted">
                {isLoadingAccount ? t("running") : t("accountSignInUnavailableHint")}
              </p>
            ) : (
              <>
                <PlanValueCard usage={anonymousUsage} t={t} locale={locale} />
                {anonymousUsage && (
                  <div className="quota-list">
                    <Quota
                      label={t("backtests")}
                      used={anonymousUsage.usage.normal_backtests}
                      total={anonymousUsage.limits.normal_backtests}
                      locale={locale}
                    />
                  </div>
                )}
              </>
            )}
          </section>
          )}

          {activeSection === "pricing" && (
            <PricingPanel
              plans={APP_STORE_REFERENCE_PLANS}
              pricingMode={pricingMode}
              planLimits={planLimits}
              usage={token ? usage : null}
              signedIn={Boolean(user && token)}
              t={t}
              locale={locale}
            />
          )}

          {activeSection === "settings" && (
          <section className="panel settings-panel">
            <div className="panel-title compact">
              <div>
                <p className="eyebrow">{t("settings")}</p>
                <h2>{experienceT("experienceTitle")}</h2>
              </div>
              <Settings size={22} />
            </div>
            <ExperienceModeSelector
              compact
              mode={experienceMode}
              locale={locale}
              onChange={(nextMode) => handleExperienceModeChange(nextMode, "settings")}
            />
            <div className="settings-list">
              <button className="secondary full-width" onClick={openPrivacy}>
                <ShieldCheck size={18} /> {t("privacy")}
              </button>
              <button className="secondary full-width" onClick={openRiskNotice}>
                <ShieldAlert size={18} /> {t("riskNoticeTitle")}
              </button>
            </div>
          </section>
          )}

          {activeSection === "bots" && (
          <section className="panel bots-panel">
            <div className="panel-title">
              <div>
                <h2>{t("paperAlerts")}</h2>
                {token && usage && <span className="muted">{formatLocalizedNumber(bots.length, locale)}/{formatLocalizedNumber(usage.limits.max_realtime_bots, locale)}</span>}
              </div>
              <button
                className="primary"
                onClick={() => void handleBotPrimaryAction()}
                disabled={botCreationAction === "wait"}
              >
                {botCreationAction === "wait"
                  ? <RefreshCw className="is-spinning" size={18} />
                  : botCreationAction === "sign_in"
                    ? <UserCircle size={18} />
                    : botCreationAction === "view_plans"
                      ? <CreditCard size={18} />
                      : <Bot size={18} />}
                {botCreationActionLabel}
              </button>
            </div>
            {!token ? (
              <div className="empty-state compact-empty">{t("botLoginHint")}</div>
            ) : bots.length ? (
              <div className="bot-list">
                {bots.map((bot) => (
                  <BotRow
                    key={bot.id}
                    bot={bot}
                    isBusy={isBotActionRunning || isBotsRefreshing}
                    t={t}
                    locale={locale}
                    onTick={() => handleTickBot(bot)}
                    onDelete={() => handleDeleteBot(bot)}
                    onApplyResult={(nextResult) => applyResult(nextResult, t("loadedLatestBotResult"))}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-state compact-empty">{t("emptyBots")}</div>
            )}
            {token && (
              <button type="button" className="secondary full-width section-action" onClick={() => void refreshBots()}
                disabled={isBotActionRunning || isBotsRefreshing} aria-busy={isBotsRefreshing}>
                <RefreshCw size={18} className={isBotsRefreshing ? "is-spinning" : undefined} />
                {isBotsRefreshing ? referenceT(locale, "refreshing") : t("refresh")}
              </button>
            )}
            {token && (
              <React.Suspense fallback={null}>
                <PaperMonitoringPanel
                  key={user?.uid ?? "signed-in-account"}
                  authToken={token}
                  locale={locale}
                  bots={bots}
                  experienceMode={experienceMode}
                  onSessionLinked={() => refreshBots()}
                  onRunBacktest={() => navigateToSection("backtest", true)}
                  runBacktestLabel={t("runBacktest")}
                />
              </React.Suspense>
            )}
          </section>
          )}

          {activeSection === "bots" && (
            <section className="panel monitor-labs">
              <button className="execution-settings-toggle" type="button" aria-expanded={areMonitorLabsOpen}
                aria-controls="monitor-labs-content" onClick={() => setAreMonitorLabsOpen((open) => !open)}>
                <span><Settings size={17} /> {productFocusT(locale, "labs")}</span>
                <ChevronDown className={areMonitorLabsOpen ? "expanded" : ""} size={18} />
              </button>
              {areMonitorLabsOpen && <div id="monitor-labs-content">
              <p className="muted">{productFocusT(locale, "labsHint")}</p>
              <React.Suspense fallback={null}>
              {paperAppliedSourceState === "blocked" && <p className="paper-exec__notice" role="status">
                {paperExecutionT(locale, "historySourceBlocked")}
              </p>}
              <PaperExecutionPanel
                key={`paper-execution:${accountContext.accountID ?? "guest"}:${accountContext.generation}`}
                locale={locale}
                authToken={authTokenSource}
                sources={paperSources}
                isAccountCurrent={isCommunityAccountCurrent}
                onSignIn={() => handleSignInFocus("bots")}
                onChooseStrategy={() => navigateToSection("backtest", true)}
              />
              <FuturesDemoPanel
                key={`futures-demo:${accountContext.accountID ?? "guest"}:${accountContext.generation}`}
                locale={locale}
                authToken={authTokenSource}
                isAccountCurrent={isCommunityAccountCurrent}
                onSignIn={() => handleSignInFocus("bots")}
              />
              </React.Suspense></div>}
            </section>
          )}

          {activeSection === "history" && (
            <React.Suspense fallback={<section className="panel"><div className="empty-state compact-empty">{t("running")}</div></section>}>
              <ResearchWorkspacePanel
                locale={locale}
                accountID={user?.uid}
                authToken={token}
                experienceMode={experienceMode}
                history={history}
                onOpenHistoryItem={openHistoryItem}
                onRerunHistoryItem={prepareHistoryRerun}
                canRerunHistoryItem={(item) => canPrepareHistoryRerun(form, item)}
                actionsDisabled={isRunning}
                onDuplicateHistoryItem={duplicateHistoryItem}
                onPreparePaperItem={prepareHistoryItemForPaper}
                onSignIn={() => handleSignInFocus("history")}
              />
            </React.Suspense>
          )}

          {activeSection === "community" && (
            <React.Suspense fallback={<section className="panel"><div className="empty-state compact-empty">{t("running")}</div></section>}>
              <StrategyCommunityPanel
                key={`${accountContext.accountID ?? "guest"}:${accountContext.generation}:${communityMarketSelection?.requestID ?? "browse"}`}
                locale={locale}
                initialMarketSelection={communityMarketSelection}
                history={history}
                initialShowOwned={communityHistoryRequested}
                onBacktest={() => navigateToSection("backtest", true)}
                onMarketBacktest={prepareMarketBacktest}
                capability={strategyCommunityCapability}
                capabilityState={communityCapabilityState}
                identityReady={authResolved}
                onRetryCapabilities={() => setCommunityCapabilityRetry((value) => value + 1)}
                accountID={user?.uid}
                isAccountCurrent={isCommunityAccountCurrent}
                authToken={authTokenSource}
                onSignIn={() => handleSignInFocus("community")}
                onApplyReproduction={applyCommunityStrategy}
                onPreparePaper={prepareCommunityForPaper}
              />
            </React.Suspense>
          )}
        </section>
        )}
      </section>
      {isShareDialogOpen && result && (
        <div className="modal-backdrop share-choice-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !isShareLinkCreating) setIsShareDialogOpen(false);
        }}>
          <section className="modal-card share-choice-dialog" role="dialog" aria-modal="true" aria-labelledby="share-choice-title">
            <div className="panel-title compact">
              <div>
                <p className="eyebrow">{t("shareResult")}</p>
                <h2 id="share-choice-title">{t("shareDialogTitle")}</h2>
              </div>
              <Share2 size={22} />
            </div>
            <button
              className="share-choice-option"
              type="button"
              disabled={isShareLinkCreating}
              onClick={() => void publishShare("summary_only")}
            >
              <strong>{t("shareEvidenceOnly")}</strong>
              <span>{t("shareEvidenceOnlyHint")}</span>
            </button>
            <button
              className="share-choice-option is-reproducible"
              type="button"
              disabled={
                isShareLinkCreating
                || !supportsReproducibleSharing
                || !canShareReproducibleStrategy(result, history)
              }
              onClick={() => void publishShare("reproducible_strategy")}
            >
              <strong>{t("shareReusableStrategy")}</strong>
              <span>{
                supportsReproducibleSharing && canShareReproducibleStrategy(result, history)
                  ? t("shareReusableStrategyHint")
                  : t("shareReusableUnavailable")
              }</span>
            </button>
            <div className="modal-actions">
              <button className="secondary" type="button" disabled={isShareLinkCreating} onClick={() => setIsShareDialogOpen(false)}>
                {t("cancel")}
              </button>
            </div>
          </section>
        </div>
      )}
      {isPrivacyOpen && <PrivacyDialog t={t} locale={locale} pricingMode={pricingMode} onClose={closePrivacy} onLocaleChange={updateLocale} />}
      {!isPrivacyOpen && isRiskNoticeOpen && (
        <RiskNoticeDialog
          t={t}
          locale={locale}
          onLocaleChange={updateLocale}
          requiresConfirmation={riskNoticeRequiresConfirmation}
          onAccept={acceptRiskNotice}
        />
      )}
      <AnalyticsConsent locale={locale} />
    </main>
  );
}

function HeroConversionPanel({ t, locale, learnPath, form, onRun }: { t: Translator; locale: Locale; learnPath: string; form: BacktestFormState; onRun: () => void }) {
  return (
    <section className="hero-conversion" aria-label={t("heroProofTitle")}>
      <div className="hero-proof-copy">
        <p className="eyebrow">{t("strategyProof")}</p>
        <h2>{t("heroProofTitle")}</h2>
        <p>{t("heroProofBody")}</p>
        <div className="hero-proof-actions">
          <button className="primary" type="button" onClick={onRun}>
            <Play size={18} /> {t("runFreeBacktest")}
          </button>
          <a className="secondary link-button" href={learnPath}>
            {t("readLearningCenter")} <ArrowRight size={17} />
          </a>
          <AppHandoffActions
            t={t}
            placement="hero"
            locale={locale}
            form={form}
          />
        </div>
      </div>

      <div className="product-preview-card" aria-label={`${t("sample")} · ${t("results")}`}>
        <div className="preview-toolbar">
          <span>BTC/USDT · 1H · MA Cross</span>
          <strong>{formatLocalizedPercent(18.4, locale, 1, "always")}</strong>
        </div>
        <div className="preview-chart" aria-hidden="true">
          <div className="preview-grid" />
          <svg viewBox="0 0 520 210" role="img" aria-label={`${t("sample")} · ${t("results")} · ${t("trades")}`}>
            <path className="preview-area" d="M0 185 L0 154 C42 147 56 126 92 129 C132 134 138 84 178 94 C222 105 230 64 272 70 C314 76 318 42 362 48 C408 54 420 24 466 30 C492 34 504 22 520 18 L520 185 Z" />
            <path className="preview-line" d="M0 154 C42 147 56 126 92 129 C132 134 138 84 178 94 C222 105 230 64 272 70 C314 76 318 42 362 48 C408 54 420 24 466 30 C492 34 504 22 520 18" />
            <circle className="preview-buy" cx="92" cy="129" r="8" />
            <circle className="preview-sell" cx="178" cy="94" r="8" />
            <circle className="preview-buy" cx="272" cy="70" r="8" />
            <circle className="preview-sell" cx="466" cy="30" r="8" />
          </svg>
        </div>
        <div className="preview-metrics">
          <div>
            <span>{t("winRate")}</span>
            <strong>{formatLocalizedPercent(62, locale, 0)}</strong>
          </div>
          <div>
            <span>{t("drawdown")}</span>
            <strong>{formatLocalizedPercent(7.8, locale, 1)}</strong>
          </div>
          <div>
            <span>{t("trades")}</span>
            <strong>{formatLocalizedNumber(48, locale)}</strong>
          </div>
        </div>
        <div className="preview-feature-row">
          <span><BellRing size={16} /> {t("paperAlerts")}</span>
          <span><Activity size={16} /> {t("riskFirst")}</span>
        </div>
      </div>
    </section>
  );
}

function PricingPanel({
  plans,
  pricingMode,
  planLimits,
  usage,
  signedIn,
  t,
  locale,
}: {
  plans: readonly BillingPlan[];
  pricingMode: AppPricingMode;
  planLimits: PlanLimitsByTier;
  usage: AccountUsage | null;
  signedIn: boolean;
  t: Translator;
  locale: Locale;
}) {
  useEffect(() => trackFunnelEvent("pricing_view", { signed_in: signedIn }), [signedIn]);
  const hasPaidPlan = Boolean(usage && !["anonymous", "free"].includes(usage.plan));
  const planFor = (tier: BillingPlan["plan"], interval: BillingPlan["interval"]) =>
    plans.find((plan) => plan.plan === tier && plan.interval === interval);
  const quotaRows = (tier: ComparisonTier) => PLAN_COMPARISON.filter((row) =>
    ["quotaBacktests", "quotaMaxRange", "quotaCloudHistory", "quotaPaperBots"].includes(row.labelKey)
  ).map((row) => ({ label: t(row.labelKey), value: formatQuotaValue(row, tier, planLimits, t, locale) }));
  const guestQuotaSummary = planLimits.guest
    ? `${formatQuotaValue(PLAN_COMPARISON[0], "guest", planLimits, t, locale)} ${t("quotaBacktests")} · ${formatQuotaValue(PLAN_COMPARISON[4], "guest", planLimits, t, locale)} ${t("quotaCloudHistory")}. `
    : "";

  if (pricingMode !== "legacy") {
    return <PaidAppPricingPanel
      locale={locale}
      ready={pricingMode === "paid_app"}
      pricingLabel={t("pricing")}
      storeLabel={t("downloadOnAppStore")}
      storeURL={buildAppStoreCampaignURL("web_to_app", "pricing")}
      quotaRows={PLAN_COMPARISON.map((row) => ({
        label: t(row.labelKey), value: formatQuotaValue(row, "pro", planLimits, t, locale),
      }))}
      onStoreClick={() => trackFunnelEvent("app_store_click", { placement: "pricing" })}
    />;
  }

  return (
    <section className="panel pricing-panel">
      <div className="pricing-heading">
        <p className="eyebrow">{t("pricingEyebrow")}</p>
        <h2>{t("pricingTitle")}</h2>
          <p>{t("pricingBody")}</p>
          <p className="guest-access-note" aria-live="polite">
            <strong>{t("guestAccessTitle")}</strong>{" "}
            {guestQuotaSummary}
            {t("guestAccessBody")}
          </p>
      </div>

      {hasPaidPlan && (
        <section className="current-billing-plan">
          <div>
            <span>{t("currentPlan")}</span>
            <strong>{usage?.plan.toUpperCase()}</strong>
            <small>{t("appStoreBillingSource")}</small>
          </div>
        </section>
      )}

      <div className="pricing-grid">
        <article className="price-card">
          <p className="eyebrow">{t("freePlan")}</p>
          <h3>{formatFreeBillingPrice(locale)}</h3>
          <p className="billing-period">{t("freeForever")}</p>
          <ul>
            {quotaRows("free").map((item) => <li key={item.label}><CheckCircle2 size={16} /> <span><strong>{item.value}</strong> {item.label}</span></li>)}
          </ul>
        </article>

        {(["pro", "power"] as const).map((tier) => {
          const monthlyPlan = planFor(tier, "monthly");
          const yearlyPlan = planFor(tier, "yearly");
          const savings = annualBillingSavings(monthlyPlan, yearlyPlan);
          return (
            <article className={`price-card ${tier} ${tier === "pro" ? "recommended" : ""}`} key={tier}>
              {tier === "pro" && <span className="recommended-badge">{t("mostPopular")}</span>}
              <p className="eyebrow">Backtest {tier === "pro" ? "Pro" : "Power"}</p>
              <div className="tier-prices">
                {monthlyPlan && <div><strong>{formatReferenceBillingPrice(monthlyPlan, locale)}</strong><span>{t("perMonth")}</span></div>}
                {yearlyPlan && <div><strong>{formatReferenceBillingPrice(yearlyPlan, locale)}</strong><span>{t("perYear")}</span></div>}
              </div>
              {savings > 0 && <p className="billing-saving">{t("saveComparedMonthly")} {formatReferenceBillingAmount(savings, locale)} {t("perYear")}</p>}
              <ul>
                {quotaRows(tier).map((item) => <li key={item.label}><CheckCircle2 size={16} /> <span><strong>{item.value}</strong> {item.label}</span></li>)}
              </ul>
              <div className="app-store-plan-cta">
                <AppHandoffActions
                  t={t}
                  placement="pricing"
                  locale={locale}
                  pathname={localizedSectionPath(locale, "pricing")}
                />
                <small>{hasPaidPlan ? t("planActive") : t("continueInIOSApp")}</small>
              </div>
            </article>
          );
        })}
      </div>

      <section className="plan-comparison" aria-labelledby="plan-comparison-title">
        <div className="plan-comparison-heading">
          <p className="eyebrow">{t("quotaEyebrow")}</p>
          <h3 id="plan-comparison-title">{t("quotaTitle")}</h3>
          <p id="plan-comparison-description">{t("quotaBody")}</p>
        </div>
        <div className="comparison-scroll">
          <table aria-describedby="plan-comparison-description">
            <thead><tr><th scope="col">{t("feature")}</th><th scope="col">{t("guestPlan")}</th><th scope="col">{t("freePlan")}</th><th scope="col">Pro</th><th scope="col">Power</th></tr></thead>
            <tbody>
              {PLAN_COMPARISON.map((row) => (
                <tr key={row.labelKey}>
                  <th scope="row">{t(row.labelKey)}</th>
                  <td>{formatQuotaValue(row, "guest", planLimits, t, locale)}</td>
                  <td>{formatQuotaValue(row, "free", planLimits, t, locale)}</td>
                  <td>{formatQuotaValue(row, "pro", planLimits, t, locale)}</td>
                  <td>{formatQuotaValue(row, "power", planLimits, t, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <details className="trial-quota-note">
        <summary>{t("trialQuotaTitle")}</summary>
        <p>{t("trialQuotaBody")}</p>
      </details>

      <p className="billing-fine-print">
        {t("appStorePricingFinePrint")} <a href={privacyPath(locale)}>{t("privacy")}</a>
      </p>
    </section>
  );
}

function strategyLabel(strategy: string, t: Translator, locale: Locale) {
  const normalized = strategy.trim().toLowerCase();
  if (isTradingMethodID(normalized)) return methodName(locale, normalized);
  if (normalized === "visual strategy builder") return t("strategyVisualBuilder");
  if (normalized === "custom dsl strategy") return t("strategyCustomDSL");
  if (normalized.includes("engulf")) return t("strategyEngulfing");
  if (normalized.includes("pin bar")) return t("strategyPinBar");
  if (normalized.includes("inside bar")) return t("strategyInsideBar");
  if (normalized.includes("sweep")) return t("strategySweep");
  if (normalized === "trend+" || normalized.includes("trend pullback momentum")) return t("strategyTrendPlus");
  if (normalized === "vol break" || normalized.includes("volume breakout")) return t("strategyVolBreak");
  if (normalized === "mean+" || normalized.includes("mean reversion")) return t("strategyMeanPlus");
  if (normalized === "mtf" || normalized.includes("multi-timeframe")) return t("strategyMTF");
  if (normalized.includes("pullback")) return t("strategyPullback");
  if (normalized.includes("breakout")) return t("strategyBreakout");
  if (normalized.includes("bollinger")) return t("strategyBollinger");
  if (normalized.includes("macd")) return t("strategyMACD");
  if (normalized.includes("rsi")) return t("strategyRSI");
  if (normalized.includes("ma cross") || normalized.includes("moving average")) return t("strategyMACross");
  return strategy;
}

function PrivacyDialog({ t, locale, pricingMode, onClose, onLocaleChange }: { t: Translator; locale: Locale; pricingMode: AppPricingMode; onClose: () => void; onLocaleChange: (locale: Locale) => void }) {
  return (
    <div className="modal-backdrop language-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="modal privacy-modal" role="dialog" aria-modal="true" aria-labelledby="privacy-title" onClick={(event) => event.stopPropagation()}>
        <LanguageSwitcher locale={locale} onChange={onLocaleChange} />
        <div className="panel-title compact">
          <div>
            <p className="eyebrow">Backtest</p>
            <h2 id="privacy-title">{t("privacy")}</h2>
          </div>
          <button className="secondary" onClick={onClose}>{t("privacyClose")}</button>
        </div>
        <p>{t("privacyOverlayIntro")}</p>
        {[
          ["privacyDataControlTitle", "privacyDataControlBody"],
          ["privacyAppClipTitle", "privacyAppClipBody"],
          ["privacyResearchPaperTitle", "privacyResearchPaperBody"],
          ["privacyCommunityTitle", "privacyCommunityBody"],
          ["privacyAnalyticsTitle", "privacyAnalyticsBody"],
          ["privacyProcessorsTitle", "privacyProcessorsBody"],
          ["privacyTermsTitle", "privacyTermsBody"],
          ["privacyRiskTitle", "privacyRiskBody"],
        ].map(([title, body]) => (
          <div className="privacy-section" key={title}>
            <h3>{t(title as Parameters<Translator>[0])}</h3>
            <p>{title === "privacyTermsTitle" && pricingMode !== "legacy"
              ? pricingMode === "paid_app"
                ? `${paidAppCopy[locale].body} ${paidAppCopy[locale].legacyBody}`
                : paidAppCopy[locale].pendingBody
              : t(body as Parameters<Translator>[0])}</p>
            {title === "privacyTermsTitle" && (
              <p>
                <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noreferrer">
                  {t("privacyTermsLink")}
                </a>
              </p>
            )}
          </div>
        ))}
        <p>
          <a href={privacyPath(locale)}>{t("privacyCompletePolicyLink")}</a>
        </p>
        <p className="muted">{t("privacyOverlayUpdated")}</p>
      </section>
    </div>
  );
}

function RiskNoticeDialog({
  t,
  locale,
  onLocaleChange,
  requiresConfirmation,
  onAccept,
}: {
  t: Translator;
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  requiresConfirmation: boolean;
  onAccept: () => void;
}) {
  const [hasChecked, setHasChecked] = useState(false);
  const points = [
    ["riskNoticeCapitalTitle", "riskNoticeCapitalBody", "chart.line.downtrend.xyaxis"],
    ["riskNoticeBacktestTitle", "riskNoticeBacktestBody", "clock.arrow.circlepath"],
    ["riskNoticeBotTitle", "riskNoticeBotBody", "bolt.badge.clock"],
  ] as const;

  return (
    <div className="modal-backdrop risk-notice-backdrop language-modal-backdrop" role="presentation">
      <section className="modal risk-notice-modal" role="dialog" aria-modal="true" aria-labelledby="risk-notice-title">
        <LanguageSwitcher locale={locale} onChange={onLocaleChange} />
        <div className="risk-notice-hero" aria-hidden="true">
          <ShieldAlert size={46} />
        </div>
        <div className="risk-notice-copy">
          <h2 id="risk-notice-title">{t("riskNoticeTitle")}</h2>
          <p>{t("riskNoticeSubtitle")}</p>
        </div>
        <div className="risk-notice-points">
          {points.map(([title, body], index) => (
            <article className="risk-notice-point" key={title}>
              <span>{index + 1}</span>
              <div>
                <h3>{t(title)}</h3>
                <p>{t(body)}</p>
              </div>
            </article>
          ))}
        </div>
        {requiresConfirmation && (
          <label className="risk-confirmation">
            <input type="checkbox" checked={hasChecked} onChange={(event) => setHasChecked(event.target.checked)} />
            <span>{t("riskNoticeConfirmation")}</span>
          </label>
        )}
        <button
          className="primary full-width"
          type="button"
          disabled={requiresConfirmation && !hasChecked}
          onClick={onAccept}
        >
          {requiresConfirmation ? t("riskNoticeContinue") : t("privacyClose")}
        </button>
      </section>
    </div>
  );
}

function DateInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input type="date" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function AccountButton({
  user,
  isLoading,
  t,
  onSignIn,
}: {
  user: User | null;
  isLoading: boolean;
  t: Translator;
  onSignIn: () => void;
}) {
  if (!firebaseEnabled) {
    return (
      <button className="secondary" disabled>
        <Lock size={18} /> {t("accountSignInUnavailable")}
      </button>
    );
  }
  if (user) {
    return (
      <button className="secondary" onClick={() => void logout()}>
        <LogOut size={18} /> {t("signOut")}
      </button>
    );
  }
  return (
    <button className="secondary" onClick={() => onSignIn()} disabled={isLoading}>
      <UserCircle size={18} /> {t("signIn")}
    </button>
  );
}

function Select({
  label,
  value,
  options,
  labels,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  labels?: Record<string, string>;
  onChange: (value: string) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <div className="select-wrap">
        <select value={value} onChange={(event) => onChange(event.target.value)}>
          {options.map((option) => (
            <option key={option} value={option}>
              {labels?.[option] || option}
            </option>
          ))}
        </select>
        <ChevronDown size={16} />
      </div>
    </label>
  );
}

function Segmented<T extends string>({
  label,
  value,
  options,
  labels,
  describedBy,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly T[];
  labels?: Partial<Record<T, string>>;
  describedBy?: string;
  onChange: (value: T) => void;
}) {
  return (
    <div className="field">
      <span>{label}</span>
      <div className="segmented" role="group" aria-label={label} aria-describedby={describedBy}>
        {options.map((option) => (
          <button key={option} className={value === option ? "active" : ""} onClick={() => onChange(option)}>
            {labels?.[option] || option}
          </button>
        ))}
      </div>
    </div>
  );
}

function StrategyModePicker({
  locale,
  value,
  t,
  onChange,
}: {
  locale: Locale;
  value: StrategyMode;
  t: Translator;
  onChange: (value: StrategyMode) => void;
}) {
  const modes: Array<{
    value: StrategyMode;
    label: string;
    icon: React.ReactNode;
  }> = [
    {
      value: "auto",
      label: simpleStrategyT(locale, "preset"),
      icon: <BarChart3 size={20} aria-hidden="true" />,
    },
    {
      value: "builder",
      label: simpleStrategyT(locale, "custom"),
      icon: <Code2 size={20} aria-hidden="true" />,
    },
  ];
  return (
    <div className="strategy-mode-field">
      <span>{t("mode")}</span>
      <div className="strategy-mode-picker" role="group" aria-label={t("mode")}>
        {modes.map((mode) => (
          <button
            key={mode.value}
            type="button"
            className={value === mode.value ? "is-selected" : undefined}
            aria-pressed={value === mode.value}
            onClick={() => onChange(mode.value)}
          >
            {mode.icon}
            <span>{mode.label}</span>
            {value === mode.value && <CheckCircle2 size={16} aria-hidden="true" />}
          </button>
        ))}
      </div>
    </div>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  step,
  prefix,
  suffix,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  step: number;
  prefix?: string;
  suffix?: string;
  min?: number;
  max?: number;
}) {
  return (
    <label className="number-field">
      <span>{label}</span>
      <div>
        {prefix && <small>{prefix}</small>}
        <input type="number" step={step} min={min} max={max} value={value} onChange={(event) => onChange(Number(event.target.value))} />
        {suffix && <small>{suffix}</small>}
      </div>
    </label>
  );
}

function MetricGrid({ result, t, locale }: { result: BacktestResult; t: Translator; locale: Locale }) {
  const metrics = [
    [t("drawdown"), formatLocalizedPercent(result.performance.max_drawdown, locale, 2)],
    [t("winRate"), formatLocalizedPercent(result.performance.win_rate, locale, 2)],
    [t("trades"), formatLocalizedNumber(result.trades?.length || 0, locale)],
  ];
  return (
    <div className="metrics">
      {metrics.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

function ProfitLossHero({ result, t, locale }: { result: BacktestResult; t: Translator; locale: Locale }) {
  const direction = profitLossDirection(result.performance.net_profit_amount);
  const Icon = direction === "profit" ? TrendingUp : direction === "loss" ? TrendingDown : Minus;
  const amount = currency(result.performance.net_profit_amount, locale);
  const returnValue = formatLocalizedPercent(
    result.performance.total_profit_percentage,
    locale,
    2,
    "always",
  );

  return (
    <section
      className={`profit-loss-hero ${direction}`}
      aria-label={`${t("netProfit")}: ${amount}; ${t("returnMetric")}: ${returnValue}`}
    >
      <Icon size={30} strokeWidth={2.25} aria-hidden="true" />
      <div>
        <span>{t("netProfit")}</span>
        <strong>{amount}</strong>
      </div>
      <div className="profit-loss-return">
        <span>{t("returnMetric")}</span>
        <strong>{returnValue}</strong>
      </div>
    </section>
  );
}

function ConfidenceCard({ result, t, locale }: { result: BacktestResult; t: Translator; locale: Locale }) {
  const confidence = result.confidence;
  if (!confidence) return null;
  const levelKey: TranslationKey = confidence.level === "high" ? "confidenceHigh" : confidence.level === "medium" ? "confidenceMedium" : "confidenceLow";
  const outOfSampleEvaluated = confidence.out_of_sample_evaluated === true || result.robustness?.out_of_sample_evaluated === true;
  const canUseServerCopy = serverLocalizedCopyMatches(result.response_language, locale);
  const codedWarnings = (confidence.warning_codes || [])
    .map((code, index) => ({ code, localized: confidence.localized_warnings?.[index] }))
    .filter(({ code }) => code !== "in_sample_only");
  const codedReasons = (confidence.reason_codes || [])
    .map((code, index) => ({ code, localized: confidence.localized_reasons?.[index] }));
  const hasStableCodes = codedWarnings.length > 0 || codedReasons.length > 0;
  const codedDetails = [...codedWarnings, ...codedReasons].map(({ code, localized }) => (
    confidenceCodeLabel(code, t)
    || (canUseServerCopy ? localized?.trim() : "")
    || t("qualityNotForecast")
  ));
  const localizedDetails = canUseServerCopy
    ? [
        ...(confidence.localized_warnings || []).filter(
          (_detail, index) => confidence.warning_codes?.[index] !== "in_sample_only",
        ),
        ...(confidence.localized_reasons || []),
      ]
    : [];
  const details = [...new Set(
    hasStableCodes
      ? codedDetails
      : localizedDetails.length > 0
        ? localizedDetails
        : [...(confidence.warnings || []), ...(confidence.reasons || [])]
            .filter((detail) => !detail.startsWith("In-sample score only"))
            .map((detail) => confidenceDetailLabel(detail, t, locale)),
  )].slice(0, 4);
  return (
    <section className={`confidence-card confidence-${confidence.level}`}>
      <div>
        <span>{t("strategyConfidence")}</span>
        <strong>{formatLocalizedNumber(confidence.score, locale)}/{formatLocalizedNumber(100, locale)} · {t(levelKey)}</strong>
        <small>{t("qualityNotForecast")}</small>
      </div>
      <div className="confidence-sample">
        <span>{t("sample")}: {formatLocalizedNumber(confidence.sample?.trades ?? result.trades?.length ?? 0, locale)} {t("trades").toLowerCase()}</span>
        {confidence.sample?.profit_factor != null && <span>PF {formatLocalizedNumber(Number(confidence.sample.profit_factor), locale, { maximumFractionDigits: 2 })}</span>}
      </div>
      {details.length > 0 && (
        <ul>
          {details.map((detail) => <li key={detail}>{detail}</li>)}
        </ul>
      )}
      {!outOfSampleEvaluated && (
        <div className="holdout-warning">
          <ShieldAlert size={17} />
          <span><strong>{t("noLockedHoldout")}</strong>{t("noLockedHoldoutBody")}</span>
        </div>
      )}
    </section>
  );
}

function ResearchEvidence({ result, t, locale }: { result: BacktestResult; t: Translator; locale: Locale }) {
  const execution = result.execution;
  const data = result.data;
  const robustness = result.robustness;
  const rejectedOrders = result.rejected_orders || [];
  if (!execution && !data && !robustness && rejectedOrders.length === 0) return null;

  const quality = data?.quality;
  const strategyMetrics = robustness?.strategy_metrics;
  const benchmarkMetrics = robustness?.benchmark?.metrics;
  const continuousGapCount = quality?.continuous_market_gap_check ? Number(quality.gap_count || 0) : null;
  const providerLine = [displayProvider(data?.selected_provider), data?.provider_symbol, data?.interval]
    .filter((value): value is string => Boolean(value))
    .join(" · ");
  const snapshot = data?.snapshot_sha256;

  return (
    <details className="research-details">
      <summary>
        <span><ShieldCheck size={18} /> {t("researchEvidence")}</span>
        <ChevronDown size={18} />
      </summary>
      <div className="research-evidence-grid">
        {execution && (
          <section className="research-evidence-card">
            <h3><Zap size={17} /> {t("executionAssumptions")}</h3>
            <p>{t("executionNextOpen")}</p>
            <p>{t("executionStopFirst")}</p>
          </section>
        )}

        {data && (
          <section className="research-evidence-card">
            <h3><Database size={17} /> {t("dataProvenance")}</h3>
            {providerLine && <strong className="evidence-lead">{providerLine}</strong>}
            <div className="evidence-tags">
              {data.closed_candles_only === true ? (
                <span className="evidence-ok"><CheckCircle2 size={14} /> {t("closedCandlesOnly")}</span>
              ) : (
                <span><Clock size={14} /> {t("candleCloseUnknown")}</span>
              )}
              {continuousGapCount === 0 && (
                <span className="evidence-ok"><CheckCircle2 size={14} /> {t("noUnexpectedGaps")}</span>
              )}
              {continuousGapCount != null && continuousGapCount > 0 && (
                <span className="evidence-warning"><ShieldAlert size={14} /> {t("dataGaps")}: {formatLocalizedNumber(continuousGapCount, locale)} · {formatLocalizedNumber(quality?.estimated_missing_bars || 0, locale)} {t("missingBars")}</span>
              )}
            </div>
            <dl className="evidence-facts">
              {data.candle_count != null && <><dt>{t("sample")}</dt><dd>{formatLocalizedNumber(data.candle_count, locale)} {t("bars")}</dd></>}
              {snapshot && <><dt>{t("dataSnapshot")}</dt><dd><code title={snapshot}>{snapshot.slice(0, 10)}…</code></dd></>}
            </dl>
          </section>
        )}

        {robustness && (
          <section className="research-evidence-card">
            <h3><BarChart3 size={17} /> {t("robustness")}</h3>
            {robustness.available === false && !strategyMetrics ? (
              <p>{t("notAvailable")}</p>
            ) : (
              <dl className="evidence-facts evidence-metrics">
                {strategyMetrics?.total_return != null && <><dt>{t("strategyReturn")}</dt><dd>{ratioPercent(strategyMetrics.total_return, locale)}</dd></>}
                {benchmarkMetrics?.total_return != null && <><dt>{t("benchmarkReturn")}</dt><dd>{ratioPercent(benchmarkMetrics.total_return, locale)}</dd></>}
                {strategyMetrics?.cagr != null && <><dt>CAGR</dt><dd>{ratioPercent(strategyMetrics.cagr, locale)}</dd></>}
                {strategyMetrics?.sharpe_ratio != null && <><dt>Sharpe</dt><dd>{fixedMetric(strategyMetrics.sharpe_ratio, locale)}</dd></>}
                {strategyMetrics?.sortino_ratio != null && <><dt>Sortino</dt><dd>{fixedMetric(strategyMetrics.sortino_ratio, locale)}</dd></>}
                {strategyMetrics?.calmar_ratio != null && <><dt>Calmar</dt><dd>{fixedMetric(strategyMetrics.calmar_ratio, locale)}</dd></>}
                {strategyMetrics?.max_drawdown_duration_periods != null && <><dt>{t("drawdownDuration")}</dt><dd>{formatLocalizedNumber(strategyMetrics.max_drawdown_duration_periods, locale)} {t("bars")}</dd></>}
              </dl>
            )}
            {strategyMetrics?.sample_sufficient === false && <p className="evidence-note">{t("insufficientRiskSample")}</p>}
            {(robustness.warning_codes?.length || robustness.localized_warnings?.length || robustness.warnings?.length) ? (
              <ul className="evidence-warning-list">
                {(robustnessWarningLabels(result, t, locale)).slice(0, 3).map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            ) : null}
          </section>
        )}
      </div>

      {robustness?.cost_stress && robustness.cost_stress.length > 0 && (
        <section className="cost-stress-section">
          <h3>{t("costStress")}</h3>
          <div className="cost-stress-grid">
            {robustness.cost_stress.slice(0, 3).map((scenario) => (
              <div key={scenario.scenario} className={scenario.net_profit_amount < 0 ? "cost-negative" : ""}>
                <span>{scenario.scenario}</span>
                <strong>{formatLocalizedPercent(scenario.total_profit_percentage, locale, 2, "always")}</strong>
                <small>{currency(scenario.net_profit_amount, locale)} · {formatLocalizedNumber(scenario.trade_count ?? 0, locale)} {t("trades").toLowerCase()}</small>
              </div>
            ))}
          </div>
        </section>
      )}

      {rejectedOrders.length > 0 && (
        <section className="rejected-order-section">
          <div className="rejected-order-note">
            <ShieldAlert size={17} />
            <span><strong>{formatLocalizedNumber(rejectedOrders.length, locale)} {t("rejectedOrders")}</strong>{t("rejectedOrdersHint")}</span>
          </div>
          <div className="rejected-order-list">
            {rejectedOrders.slice(0, 3).map((order, index) => (
              <div key={`${order.signal_time || order.attempted_fill_time || "rejected"}-${index}`}>
                <span>
                  <strong>{rejectedOrderSide(order.side, t)}</strong>
                  <small>{rejectedOrderReason(order.reason, t)}</small>
                </span>
                {(order.attempted_notional != null || order.buying_power != null) && (
                  <dl>
                    {order.attempted_notional != null && <><dt>{t("attemptedNotional")}</dt><dd>{currency(order.attempted_notional, locale)}</dd></>}
                    {order.buying_power != null && <><dt>{t("buyingPower")}</dt><dd>{currency(order.buying_power, locale)}</dd></>}
                  </dl>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </details>
  );
}

const confidenceDetailKeys: Record<string, TranslationKey> = {
  "Large trade sample": "confidenceLargeTradeSample",
  "Moderate trade sample": "confidenceModerateTradeSample",
  "Small but usable trade sample": "confidenceSmallTradeSample",
  "Very small trade sample": "confidenceVerySmallTradeSample",
  "Wide candle sample": "confidenceWideCandleSample",
  "Short candle history": "confidenceShortCandleHistory",
  "No gross losing trades in this sample": "confidenceNoGrossLoss",
  "No closed trades": "confidenceNoClosedTrades",
  "Strong profit factor": "confidenceStrongProfitFactor",
  "Positive profit factor": "confidencePositiveProfitFactor",
  "Profit factor below 1.0": "confidenceWeakProfitFactor",
  "Positive net profit after costs": "confidencePositiveNetProfit",
  "Negative net profit after costs": "confidenceNegativeNetProfit",
  "Win rate above 60%": "confidenceHighWinRate",
  "Win rate below 40%": "confidenceLowWinRate",
  "Controlled drawdown": "confidenceControlledDrawdown",
  "High drawdown": "confidenceHighDrawdown",
  "Moderate drawdown": "confidenceModerateDrawdown",
  "Profit depends heavily on one large winner": "confidenceConcentratedProfit",
};

const confidenceCodeKeys: Partial<Record<string, TranslationKey>> = {
  large_trade_sample: "confidenceLargeTradeSample",
  moderate_trade_sample: "confidenceModerateTradeSample",
  small_trade_sample: "confidenceSmallTradeSample",
  very_small_trade_sample: "confidenceVerySmallTradeSample",
  wide_candle_sample: "confidenceWideCandleSample",
  short_candle_history: "confidenceShortCandleHistory",
  no_gross_loss: "confidenceNoGrossLoss",
  no_closed_trades: "confidenceNoClosedTrades",
  strong_profit_factor: "confidenceStrongProfitFactor",
  positive_profit_factor: "confidencePositiveProfitFactor",
  weak_profit_factor: "confidenceWeakProfitFactor",
  positive_net_profit: "confidencePositiveNetProfit",
  negative_net_profit: "confidenceNegativeNetProfit",
  high_win_rate: "confidenceHighWinRate",
  low_win_rate: "confidenceLowWinRate",
  controlled_drawdown: "confidenceControlledDrawdown",
  high_drawdown: "confidenceHighDrawdown",
  moderate_drawdown: "confidenceModerateDrawdown",
  concentrated_profit: "confidenceConcentratedProfit",
  rejected_orders: "rejectedOrdersHint",
  continuous_market_gaps: "continuousMarketGaps",
  cost_doubled_fragile: "costDoubledFragile",
  in_sample_only: "noLockedHoldoutBody",
  insufficient_risk_sample: "insufficientRiskSample",
};

function confidenceCodeLabel(code: string, t: Translator): string | undefined {
  const key = confidenceCodeKeys[code];
  return key ? t(key) : undefined;
}

function serverLocalizedCopyMatches(language: string | null | undefined, locale: Locale): boolean {
  const normalized = language?.trim().replace(/_/g, "-").toLowerCase();
  if (!normalized) return false;
  const isSupportedLanguage = locales.some((candidate) => {
    const canonical = candidate.toLowerCase();
    const base = canonical.split("-")[0];
    return normalized === canonical || normalized === base || normalized.startsWith(`${base}-`);
  });
  return isSupportedLanguage && resolveLocale(normalized) === locale;
}

function robustnessWarningLabels(result: BacktestResult, t: Translator, locale: Locale): string[] {
  const robustness = result.robustness;
  if (!robustness) return [];

  const localizedValues = serverLocalizedCopyMatches(result.response_language, locale)
    ? (robustness.localized_warnings || [])
    : [];
  const stableCodes = robustness.warning_codes || [];
  if (stableCodes.length > 0) {
    return [...new Set(stableCodes.map((code, index) => {
      const stableLabel = confidenceCodeLabel(code, t);
      if (stableLabel) return stableLabel;
      const serverLabel = localizedValues[index]?.trim();
      if (serverLabel) return serverLabel;
      if (["calmar_ratio_undefined", "sharpe_ratio_undefined", "sortino_ratio_undefined"].includes(code)) {
        return t("notAvailable");
      }
      return t("qualityNotForecast");
    }))];
  }
  if (localizedValues.length > 0) return localizedValues.filter((warning) => warning.trim().length > 0);
  return (robustness.warnings || []).map((warning) => confidenceDetailLabel(warning, t, locale));
}

function confidenceDetailLabel(detail: string, t: Translator, locale: Locale) {
  if (detail.includes("rejected by buying-power checks")) return t("rejectedOrdersHint");
  if (detail === "Continuous-market candle gaps were detected") return t("continuousMarketGaps");
  if (detail === "Profit disappears when execution costs are doubled") return t("costDoubledFragile");
  if (detail.startsWith("In-sample score only")) return t("noLockedHoldoutBody");
  const key = confidenceDetailKeys[detail];
  return key ? t(key) : locale === "en" ? detail : t("qualityNotForecast");
}

function ratioPercent(value: number, locale: Locale) {
  return formatLocalizedPercent(value * 100, locale, 2, "always");
}

function fixedMetric(value: number, locale: Locale) {
  return Number.isFinite(value) ? formatLocalizedNumber(value, locale, { maximumFractionDigits: 2 }) : "—";
}

function rejectedOrderSide(side: string | undefined, t: Translator) {
  if (side === "long") return t("long");
  if (side === "short") return t("short");
  return side ? humanizeContractValue(side) : t("notAvailable");
}

function rejectedOrderReason(reason: string | undefined, t: Translator) {
  return reason === "insufficient_buying_power" || reason === "insufficient_buying_power_after_gap"
    ? t("insufficientBuyingPower")
    : reason ? humanizeContractValue(reason) : t("notAvailable");
}

function displayProvider(provider: string | undefined) {
  if (!provider) return undefined;
  if (provider.toLowerCase() === "yfinance") return "Yahoo Finance";
  if (provider.toLowerCase() === "binance") return "Binance";
  return humanizeContractValue(provider);
}

function humanizeContractValue(value: string) {
  return value.replace(/_/g, " ").replace(/^./, (character) => character.toUpperCase());
}

type IndicatorKey = "ema20" | "ema50" | "bollinger" | "volume" | "trades";

function PriceChart({
  methodEvidence,
  candles,
  trades,
  selectedTrade,
  onSelectTrade,
  t,
  locale,
}: {
  methodEvidence?: unknown;
  candles: Candle[];
  trades: Trade[];
  selectedTrade: Trade | null;
  onSelectTrade: (trade: Trade) => void;
  t: Translator;
  locale: Locale;
}) {
  const chartRef = React.useRef<HTMLDivElement | null>(null);
  const backgroundRef = React.useRef<HTMLDivElement | null>(null);
  const selectedTradeKey = selectedTrade ? tradeKey(selectedTrade) : "";
  const [indicators, setIndicators] = useState<Record<IndicatorKey, boolean>>({
    ema20: true,
    ema50: true,
    bollinger: false,
    volume: false,
    trades: true,
  });

  function toggleIndicator(key: IndicatorKey) {
    setIndicators((current) => ({ ...current, [key]: !current[key] }));
  }

  useEffect(() => {
    if (!chartRef.current || !candles.length) return;
    const chart: IChartApi = createChart(chartRef.current, {
      height: 360,
      localization: { locale },
      layout: { background: { color: "#ffffff" }, textColor: "#1f2937" },
      grid: { vertLines: { color: "#eef2f7" }, horzLines: { color: "#eef2f7" } },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false },
    });
    const series: ISeriesApi<"Candlestick"> = chart.addCandlestickSeries({
      upColor: "#16a34a",
      downColor: "#dc2626",
      borderVisible: false,
      wickUpColor: "#16a34a",
      wickDownColor: "#dc2626",
    });
    series.setData(
      candles.map((candle) => ({
        time: Math.floor(new Date(candle.time).getTime() / 1000) as UTCTimestamp,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
      })),
    );
    if (indicators.ema20) {
      chart.addLineSeries({ color: "#2563eb", lineWidth: 2, priceLineVisible: false }).setData(indicatorSeries(ema(candles, 20)));
    }
    if (indicators.ema50) {
      chart.addLineSeries({ color: "#f59e0b", lineWidth: 2, priceLineVisible: false }).setData(indicatorSeries(ema(candles, 50)));
    }
    if (indicators.bollinger) {
      const bands = bollingerBands(candles, 20, 2);
      chart.addLineSeries({ color: "#7c3aed", lineWidth: 1, priceLineVisible: false }).setData(indicatorSeries(bands.upper));
      chart.addLineSeries({ color: "#7c3aed", lineWidth: 1, priceLineVisible: false }).setData(indicatorSeries(bands.lower));
    }
    if (selectedTrade) {
      const exitColor = selectedTrade.profit_amount >= 0 ? "#16a34a" : "#dc2626";
      drawTradeSegment(
        chart,
        selectedTrade.entry_time,
        selectedTrade.exit_time,
        selectedTrade.entry_price,
        selectedTrade.exit_price,
        exitColor,
        t("entryToExit"),
      );
      drawTradeLine(chart, selectedTrade.entry_time, selectedTrade.exit_time, selectedTrade.entry_price, "#2563eb", t("entry"), "entry");
      drawTradeLine(chart, selectedTrade.entry_time, selectedTrade.exit_time, selectedTrade.exit_price, exitColor, t("exit"), "exit");
      if (selectedTrade.stop_loss) {
        drawTradeSegment(chart, selectedTrade.entry_time, selectedTrade.exit_time, selectedTrade.entry_price, selectedTrade.stop_loss, "#dc2626", `${t("entry")} → SL`, 2, 1);
        drawTradeLine(chart, selectedTrade.entry_time, selectedTrade.exit_time, selectedTrade.stop_loss, "#dc2626", "SL");
      }
      if (selectedTrade.take_profit) {
        drawTradeSegment(chart, selectedTrade.entry_time, selectedTrade.exit_time, selectedTrade.entry_price, selectedTrade.take_profit, "#16a34a", `${t("entry")} → TP`, 2, 1);
        drawTradeLine(chart, selectedTrade.entry_time, selectedTrade.exit_time, selectedTrade.take_profit, "#16a34a", "TP");
      }
    }
    if (indicators.volume) {
      chart
        .addHistogramSeries({
          color: "#94a3b8",
          priceFormat: { type: "volume" },
          priceScaleId: "",
        })
        .setData(
          candles.map((candle) => ({
            time: Math.floor(new Date(candle.time).getTime() / 1000) as UTCTimestamp,
            value: candle.volume,
            color: candle.close >= candle.open ? "rgba(22, 163, 74, 0.35)" : "rgba(220, 38, 38, 0.35)",
          })),
        );
      chart.priceScale("").applyOptions({ scaleMargins: { top: 0.78, bottom: 0 } });
    }
    {
      const methodMarkers = methodSignalMarkers(methodEvidence, candles.map((c) => c.time)).map((event) => ({
        time: event.time as UTCTimestamp, position: "aboveBar" as const, color: "#7c3aed", shape: "circle" as const,
        text: `${methodT(locale, "signal")} · ${methodT(locale, event.direction)}`,
      }));
      series.setMarkers([
        ...methodMarkers,
        ...(indicators.trades ? trades.flatMap((trade) => [
          {
            time: Math.floor(new Date(trade.entry_time).getTime() / 1000) as UTCTimestamp,
            position: "belowBar" as const,
            color: "#2563eb",
            shape: "arrowUp" as const,
            text: t("entry"),
          },
          {
            time: Math.floor(new Date(trade.exit_time).getTime() / 1000) as UTCTimestamp,
            position: "aboveBar" as const,
            color: trade.profit_amount >= 0 ? "#16a34a" : "#dc2626",
            shape: "arrowDown" as const,
            text: exitReasonLabel(trade.exit_reason, t),
          },
        ]) : []),
      ].sort((a, b) => Number(a.time) - Number(b.time)));
    }
    if (selectedTrade) {
      focusTradeRange(chart, candles, selectedTrade);
      window.requestAnimationFrame(() => {
        renderTradeBackground(backgroundRef.current, chart, trades, selectedTrade);
        chartRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    } else {
      chart.timeScale().fitContent();
      window.requestAnimationFrame(() => renderTradeBackground(backgroundRef.current, chart, trades, null));
    }

    const redrawBackground = () => renderTradeBackground(backgroundRef.current, chart, trades, selectedTrade);
    chart.timeScale().subscribeVisibleTimeRangeChange(redrawBackground);
    window.addEventListener("resize", redrawBackground);
    return () => {
      chart.timeScale().unsubscribeVisibleTimeRangeChange(redrawBackground);
      window.removeEventListener("resize", redrawBackground);
      if (backgroundRef.current) backgroundRef.current.innerHTML = "";
      chart.remove();
    };
  }, [candles, indicators, locale, selectedTradeKey, t, trades, methodEvidence]);

  return (
    <div className="chart-block">
      <div className="indicator-toolbar" aria-label={t("chartIndicators")}>
        {([
          ["ema20", t("chartEma20")],
          ["ema50", t("chartEma50")],
          ["bollinger", t("chartBollinger")],
          ["volume", t("chartVolume")],
          ["trades", t("chartTrades")],
        ] as const).map(([key, label]) => (
          <button key={key} className={indicators[key] ? "active" : ""} onClick={() => toggleIndicator(key)}>
            {label}
          </button>
        ))}
      </div>
      <div className="chart-shell">
        <div className="trade-background" ref={backgroundRef} aria-hidden="true" />
        <div className="chart" ref={chartRef} />
      </div>
      {trades.length > 0 && (
        <div className="trade-chip-strip" aria-label={t("selectTrade")}>
          {trades.slice(0, 18).map((trade, index) => (
            <button
              key={`${tradeKey(trade)}-${index}`}
              className={selectedTradeKey === tradeKey(trade) ? "active" : ""}
              onClick={() => onSelectTrade(trade)}
            >
              #{formatLocalizedNumber(index + 1, locale)} <span className={trade.profit_amount >= 0 ? "profit" : "loss"}>{currency(trade.profit_amount, locale)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function tradeKey(trade: Trade) {
  return `${trade.entry_time}-${trade.exit_time}-${trade.entry_price}-${trade.exit_price}-${trade.profit_amount}`;
}

function latestTrade(trades: Trade[]): Trade | null {
  return sortTradesNewest(trades)[0] || null;
}

function sortTradesNewest(trades: Trade[]): Trade[] {
  return [...trades].sort((left, right) => {
    const leftTime = new Date(left.exit_time || left.entry_time).getTime();
    const rightTime = new Date(right.exit_time || right.entry_time).getTime();
    return rightTime - leftTime;
  });
}

function renderTradeBackground(container: HTMLDivElement | null, chart: IChartApi, trades: Trade[], selectedTrade: Trade | null) {
  if (!container) return;
  const width = container.clientWidth;
  if (!width) return;
  const selectedKey = selectedTrade ? tradeKey(selectedTrade) : "";
  const bands = trades.slice(-80).flatMap((trade) => {
    const start = chart.timeScale().timeToCoordinate(toChartTime(trade.entry_time));
    const end = chart.timeScale().timeToCoordinate(toChartTime(trade.exit_time));
    if (start === null || end === null) return [];
    const left = Math.max(0, Math.min(start, end));
    const right = Math.min(width, Math.max(start, end));
    const bandWidth = Math.max(3, right - left);
    if (right < 0 || left > width) return [];
    const isProfit = trade.profit_amount >= 0;
    const isSelected = selectedKey === tradeKey(trade);
    return [
      `<span class="trade-bg-band ${isProfit ? "profit" : "loss"} ${isSelected ? "selected" : ""}" style="left:${left}px;width:${bandWidth}px"></span>`,
    ];
  });
  container.innerHTML = bands.join("");
}

function drawTradeLine(chart: IChartApi, start: string, end: string, value: number, color: string, title: string, kind: "entry" | "exit" | "level" = "level") {
  const series = chart.addLineSeries({
    color,
    lineWidth: kind === "entry" ? 2 : 1,
    lineStyle: kind === "entry" || kind === "exit" ? 0 : 2,
    priceLineVisible: false,
    title,
  });
  series.setData([
    { time: Math.floor(new Date(start).getTime() / 1000) as UTCTimestamp, value },
    { time: Math.floor(new Date(end).getTime() / 1000) as UTCTimestamp, value },
  ]);
}

function drawTradeSegment(
  chart: IChartApi,
  start: string,
  end: string,
  startValue: number,
  endValue: number,
  color: string,
  title: string,
  lineStyle = 0,
  lineWidth: 1 | 2 | 3 | 4 = 3,
) {
  const series = chart.addLineSeries({
    color,
    lineWidth,
    lineStyle,
    priceLineVisible: false,
    lastValueVisible: false,
    title,
  });
  series.setData([
    { time: toChartTime(start), value: startValue },
    { time: toChartTime(end), value: endValue },
  ]);
}

function focusTradeRange(chart: IChartApi, candles: Candle[], trade: Trade) {
  const candleTimes = candles.map((candle) => toChartTime(candle.time));
  const entryTime = toChartTime(trade.entry_time);
  const exitTime = toChartTime(trade.exit_time);
  const entryIndex = nearestTimeIndex(candleTimes, entryTime);
  const exitIndex = nearestTimeIndex(candleTimes, exitTime);
  const startIndex = Math.max(0, Math.min(entryIndex, exitIndex) - 10);
  const endIndex = Math.min(candleTimes.length - 1, Math.max(entryIndex, exitIndex) + 10);
  const from = candleTimes[startIndex] ?? entryTime;
  const to = candleTimes[endIndex] ?? exitTime;
  chart.timeScale().setVisibleRange({ from, to });
}

function nearestTimeIndex(times: UTCTimestamp[], target: UTCTimestamp) {
  if (!times.length) return 0;
  let nearestIndex = 0;
  let nearestDistance = Math.abs(Number(times[0]) - Number(target));
  times.forEach((time, index) => {
    const distance = Math.abs(Number(time) - Number(target));
    if (distance < nearestDistance) {
      nearestIndex = index;
      nearestDistance = distance;
    }
  });
  return nearestIndex;
}

function toChartTime(value: string) {
  return Math.floor(new Date(value).getTime() / 1000) as UTCTimestamp;
}

function indicatorSeries(points: Array<{ time: string; value: number | null }>) {
  return points
    .filter((point): point is { time: string; value: number } => point.value !== null)
    .map((point) => ({
      time: toChartTime(point.time),
      value: point.value,
    }));
}

function ema(candles: Candle[], period: number): Array<{ time: string; value: number | null }> {
  const multiplier = 2 / (period + 1);
  let previous: number | null = null;
  return candles.map((candle, index) => {
    if (index < period - 1) {
      return { time: candle.time, value: null };
    }
    if (previous === null) {
      const seed = candles.slice(index - period + 1, index + 1).reduce((sum, item) => sum + item.close, 0) / period;
      previous = seed;
      return { time: candle.time, value: seed };
    }
    previous = (candle.close - previous) * multiplier + previous;
    return { time: candle.time, value: previous };
  });
}

function bollingerBands(candles: Candle[], period: number, deviations: number) {
  const upper: Array<{ time: string; value: number | null }> = [];
  const lower: Array<{ time: string; value: number | null }> = [];
  candles.forEach((candle, index) => {
    if (index < period - 1) {
      upper.push({ time: candle.time, value: null });
      lower.push({ time: candle.time, value: null });
      return;
    }
    const window = candles.slice(index - period + 1, index + 1).map((item) => item.close);
    const mean = window.reduce((sum, value) => sum + value, 0) / period;
    const variance = window.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / period;
    const band = Math.sqrt(variance) * deviations;
    upper.push({ time: candle.time, value: mean + band });
    lower.push({ time: candle.time, value: mean - band });
  });
  return { upper, lower };
}

function TradeDetail({ trade, t, locale }: { trade: Trade | null; t: Translator; locale: Locale }) {
  if (!trade) return null;
  const side = tradeSide(trade);
  const isWin = trade.profit_amount >= 0;
  return (
    <section className={`trade-detail-card ${side} ${isWin ? "win" : "loss"}`}>
      <div className="trade-detail-header">
        <div>
          <span>{t("selectedTrade")}</span>
          <strong>{tradeSideLabel(trade, t)} · {tradePositionLabel(trade, t)}</strong>
          <small>{formatDateTime(trade.entry_time, locale)} → {formatDateTime(trade.exit_time, locale)}</small>
        </div>
        <div className={`trade-outcome ${isWin ? "profit" : "loss"}`}>
          <span>{isWin ? t("winner") : t("loser")}</span>
          <strong>{currency(trade.profit_amount, locale)}</strong>
        </div>
      </div>

      <div className="trade-detail-grid">
        <div>
          <span>{t("entry")}</span>
          <strong>{formatLocalizedNumber(trade.entry_price, locale, { maximumFractionDigits: 2 })}</strong>
          <small>{formatDateTime(trade.entry_time, locale)}</small>
        </div>
        <div>
          <span>{t("exit")}</span>
          <strong>{formatLocalizedNumber(trade.exit_price, locale, { maximumFractionDigits: 2 })}</strong>
          <small>{exitReasonLabel(trade.exit_reason, t)}</small>
        </div>
        <div>
          <span>{t("stopLoss")}</span>
          <strong>{trade.stop_loss == null ? "—" : formatLocalizedNumber(trade.stop_loss, locale, { maximumFractionDigits: 2 })}</strong>
          <small>{t("takeProfit")}: {trade.take_profit == null ? "—" : formatLocalizedNumber(trade.take_profit, locale, { maximumFractionDigits: 2 })}</small>
        </div>
        <div>
          <span>{t("tradeTablePnL")}</span>
          <strong className={isWin ? "profit" : "loss"}>{formatLocalizedPercent(trade.profit_percentage, locale, 2, "always")}</strong>
          <small>{currency(trade.profit_amount, locale)}</small>
        </div>
        <div>
          <span>{t("duration")}</span>
          <strong>{tradeDuration(trade, locale)}</strong>
          <small>{t("entryToExit")}</small>
        </div>
        <div>
          <span>{t("fees")}</span>
          <strong>{trade.fees != null ? currency(trade.fees, locale) : "—"}</strong>
          <small>{t("commission")} / {t("slippage")}</small>
        </div>
        {trade.initial_margin != null && trade.leverage != null && (
          <>
            <div>
              <span>{t("marginUsed")}</span>
              <strong>{currency(trade.initial_margin, locale)}</strong>
              <small>
                {formatLocalizedNumber(trade.leverage, locale, { maximumFractionDigits: 2 })}x · {
                  trade.margin_mode === "isolated"
                    ? t("isolatedMargin")
                    : trade.margin_mode === "cross"
                      ? t("crossMargin")
                      : "—"
                }
              </small>
            </div>
            <div>
              <span>{t("positionValue")}</span>
              <strong>{trade.entry_notional == null ? "—" : currency(trade.entry_notional, locale)}</strong>
              <small>{t("quantity")}: {trade.quantity == null ? "—" : formatLocalizedNumber(trade.quantity, locale, { maximumFractionDigits: 8 })}</small>
            </div>
            <div>
              <span>{t("roe")}</span>
              <strong className={isWin ? "profit" : "loss"}>{trade.return_on_margin_percentage == null ? "—" : formatLocalizedPercent(trade.return_on_margin_percentage, locale, 2, "always")}</strong>
              <small>{t("liquidation")}: {trade.liquidation_price == null ? "—" : formatLocalizedNumber(trade.liquidation_price, locale, { maximumFractionDigits: 4 })}</small>
            </div>
            <div>
              <span>{t("fundingPaid")}</span>
              <strong>{currency(trade.funding_payment || 0, locale)}</strong>
              <small>{t("liquidationFee")}: {currency(trade.liquidation_fee || 0, locale)}</small>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function BotRow({
  bot,
  isBusy,
  t,
  locale,
  onTick,
  onDelete,
  onApplyResult,
}: {
  bot: RealtimeBot;
  isBusy: boolean;
  t: Translator;
  locale: Locale;
  onTick: () => void;
  onDelete: () => void;
  onApplyResult: (result: BacktestResult) => void;
}) {
  const latest = bot.events?.[0];
  const latestResult = latest?.result || null;
  const signal = localizedSignalLabel(bot.last_signal, t);
  return (
    <article className="bot-row">
      <div className="bot-row-header">
        <div>
          <strong>{bot.name}</strong>
          <span>{signal} · {intervalLabel(bot.interval_seconds, locale)}</span>
        </div>
        <span className={`signal-badge ${(bot.last_signal || "WAIT").toLowerCase()}`}>{signal}</span>
      </div>
      <p>{localizedBotReason(latest, t, locale)}</p>
      <div className="bot-meta">
        <span>{symbolLabel(latest?.symbol)} · {timeframeLabel(latest?.timeframe)}</span>
        <span>{latest?.price ? currency(latest.price, locale) : "—"}</span>
        <span>{bot.last_run_at ? formatDateTime(bot.last_run_at, locale) : "—"}</span>
      </div>
      <div className="bot-actions">
        <button className="secondary" onClick={onTick} disabled={isBusy}>
          <RefreshCw size={16} /> {t("tick")}
        </button>
        <button className="secondary" onClick={() => latestResult && onApplyResult(latestResult)} disabled={!latestResult || isBusy}>
          <LineChart size={16} /> {t("applyResult")}
        </button>
        <button className="secondary danger-button" onClick={onDelete} disabled={isBusy}>
          {t("delete")}
        </button>
      </div>
    </article>
  );
}

function localizedSignalLabel(signal: string | null | undefined, t: Translator): string {
  switch ((signal || "WAIT").toUpperCase()) {
    case "BUY": return t("buy");
    case "SELL": return t("sell");
    case "LONG": return t("long");
    case "SHORT": return t("short");
    case "WAIT": return t("ready");
    default: return t("paperAlerts");
  }
}

function localizedBotReason(
  event: RealtimeBot["events"][number] | undefined,
  t: Translator,
  locale: Locale,
): string {
  switch (event?.reason_code) {
    case "no_new_signal":
    case "not_due":
      return t("ready");
    case "paper_position_opened":
    case "position_opened":
      return `${t("entry")}: ${t("paperAlerts")}`;
    case "stop_loss":
      return `${t("exit")}: ${t("stopLoss")}`;
    case "take_profit":
      return `${t("exit")}: ${t("takeProfit")}`;
    case "signal":
      return `${t("exit")}: ${t("signalOnly")}`;
    default: {
      const serverLanguage = event?.preferred_language || event?.result?.response_language;
      if (serverLocalizedCopyMatches(serverLanguage, locale) && event?.localized_reason?.trim()) {
        return event.localized_reason;
      }
      return locale === "en" && event?.reason?.trim() ? event.reason : t("paperAlerts");
    }
  }
}

function TradeTable({
  trades,
  selectedTrade,
  onSelectTrade,
  t,
  locale,
}: {
  trades: Trade[];
  selectedTrade: Trade | null;
  onSelectTrade: (trade: Trade) => void;
  t: Translator;
  locale: Locale;
}) {
  if (!trades.length) {
    return <div className="empty-state">{t("noClosedTrades")}</div>;
  }
  const selectedTradeKey = selectedTrade ? tradeKey(selectedTrade) : "";
  return (
    <div className="trade-table">
      <div className="trade-row header">
        <span>{t("side")}</span>
        <span>{t("exit")}</span>
        <span>{t("entry")}</span>
        <span>{t("riskDetails")}</span>
        <span>{t("tradeTablePnL")}</span>
      </div>
      {trades.slice(0, 12).map((trade) => (
        <button
          className={`trade-row trade-row-button ${selectedTradeKey === tradeKey(trade) ? "selected" : ""}`}
          key={tradeKey(trade)}
          onClick={() => onSelectTrade(trade)}
        >
          <span data-label={t("side")} className={`trade-side ${tradeSide(trade)}`}>{tradeSideLabel(trade, t)} · {tradePositionLabel(trade, t)}</span>
          <span data-label={t("exit")}>{exitReasonLabel(trade.exit_reason, t)}</span>
          <span data-label={t("entry")}>{formatLocalizedNumber(trade.entry_price, locale, { maximumFractionDigits: 2 })}</span>
          <span data-label={t("riskDetails")}>
            {trade.stop_loss == null ? "—" : formatLocalizedNumber(trade.stop_loss, locale, { maximumFractionDigits: 2 })} / {trade.take_profit == null ? "—" : formatLocalizedNumber(trade.take_profit, locale, { maximumFractionDigits: 2 })}
          </span>
          <strong data-label={t("tradeTablePnL")} className={trade.profit_amount >= 0 ? "profit" : "loss"}>{currency(trade.profit_amount, locale)}</strong>
        </button>
      ))}
    </div>
  );
}

function formatDateTime(value: string, locale: Locale) {
  return formatLocalizedDateTime(value, locale);
}

function tradeDuration(trade: Trade, locale: Locale) {
  return formatLocalizedDuration(trade.entry_time, trade.exit_time, locale);
}

function intervalLabel(seconds: number, locale: Locale) {
  if (seconds >= 86400) return formatLocalizedNumber(Math.round(seconds / 86400), locale, { style: "unit", unit: "day", unitDisplay: "short" });
  if (seconds >= 3600) return formatLocalizedNumber(Math.round(seconds / 3600), locale, { style: "unit", unit: "hour", unitDisplay: "short" });
  return formatLocalizedNumber(Math.round(seconds / 60), locale, { style: "unit", unit: "minute", unitDisplay: "short" });
}

function PlanValueCard({ usage, t, locale }: { usage: AccountUsage | null; t: Translator; locale: Locale }) {
  const isGuest = !usage || usage.plan === "anonymous";
  const plan = isGuest ? t("guestMode") : usage.plan;
  const maxDays = usage?.limits.max_period_days;
  const botLimit = usage?.limits.max_realtime_bots;
  return (
    <section className="plan-value-card">
      <div>
        <p className="eyebrow">{t("planValueTitle")}</p>
        <h3>{plan}</h3>
        <p>{t("planValueBody")}</p>
      </div>
      <div className="plan-value-grid">
        <span><Database size={16} /> {t("maxRange")}: {maxDays == null ? "—" : `${formatLocalizedNumber(maxDays, locale)} ${t("quotaDays")}`}</span>
        <span><Bot size={16} /> {t("bots")}: {botLimit == null ? "—" : formatLocalizedNumber(botLimit, locale)}</span>
      </div>
    </section>
  );
}

function Quota({ label, used, total, locale }: { label: string; used: number; total: number; locale: Locale }) {
  const pct = total <= 0 ? 0 : Math.min(100, (used / total) * 100);
  return (
    <div className="quota">
      <div>
        <span>{label}</span>
        <strong>{formatLocalizedNumber(used, locale)}/{formatLocalizedNumber(total, locale)}</strong>
      </div>
      <i>
        <b style={{ width: `${pct}%` }} />
      </i>
    </div>
  );
}


function currency(value: number, locale: Locale) {
  return formatLocalizedCurrency(value, locale);
}

const sharedResultPath = /^\/r(?:\/|$)/.test(window.location.pathname);
void registerOfflineShell().catch(() => { /* Local reference/history support remains useful without a worker. */ });
const sharedResultToken = sharedResultTokenFromPath(window.location.pathname);
createRoot(document.getElementById("root")!).render(
  sharedResultPath ? <SharedResultPage token={sharedResultToken || ""} /> : <App />,
);
